import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import ApiService from '../services/api';
import ClassRepSection from './ClassRepSection';
import LocalNotificationService, { REMINDER_OPTIONS, ActivityReminder } from '../services/localNotifications';
import AlarmKit from '../services/alarmKit';
import { Capacitor } from '@capacitor/core';
import { resetAlarmSyncFlag } from './HomeScreen';
import PhoneInput from './PhoneInput';
import AddressModal from './AddressModal';
import SOCSSetupModal from './SOCSSetupModal';
import SchoolsBuddySetupModal from './SchoolsBuddySetupModal';
import PitcheroSetupModal from './PitcheroSetupModal';
import SchoolPortalSetupModal from './SchoolPortalSetupModal';
import SpondSetupModal from './SpondSetupModal';
import IntegrationBuilder from './IntegrationBuilder';
import TermPlannerClubModal from './TermPlannerClubModal';
import { INTEGRATION_BUILDER_ENABLED } from '../config/features';
import TimePickerDropdown from './TimePickerDropdown';
import { validatePhoneNumber } from '../utils/phoneValidation';
import { Parent, Child } from '../types';
import { ACTIVITY_TYPES, INTEREST_TYPES } from '../constants/activityTypes';
import './ProfileScreen.css';
import { formatDateUK } from '../utils/dateFormat';
import { findLivePlanner, findUpcomingPlanner, isPlannerLive, plannerNameFromDates } from '../utils/termPlanner';

// Helper function to get all dates for a specific day of week within a date range
const getAllDatesForDayOfWeek = (startDate: string, endDate: string, dayOfWeek: number): Date[] => {
  const dates: Date[] = [];
  const start = new Date(startDate);
  const end = new Date(endDate);

  // Find the first occurrence of this day of week on or after start date
  const current = new Date(start);
  while (current.getDay() !== dayOfWeek) {
    current.setDate(current.getDate() + 1);
  }

  // Collect all occurrences until end date
  while (current <= end) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 7);
  }

  return dates;
};

// Helper function to parse ONE-OFFS notes and extract dates with clubs
const parseOneOffsNotes = (notes: string): Map<string, string[]> => {
  const dateToClubs = new Map<string, string[]>();

  if (!notes) return dateToClubs;

  const lines = notes.split('\n');
  for (const line of lines) {
    if (line.startsWith('ONE-OFFS:')) continue;

    // Parse lines like "7 Oct: Club1, Club2"
    const match = line.match(/^(\d{1,2}\s+\w+):\s*(.+)$/);
    if (match) {
      const dateStr = match[1]; // e.g., "7 Oct"
      const clubs = match[2].split(',').map(c => c.trim());
      dateToClubs.set(dateStr, clubs);
    }
  }

  return dateToClubs;
};

// Helper function to format a date as "d Mon" (e.g., "7 Oct")
const formatDateShort = (date: Date): string => {
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

// Integration type for term planner booking
interface TermPlannerIntegration {
  id: string;
  name: string;
  connected: boolean;
  bookingUrl?: string;
}

// Manual one-off club entry
interface ManualOneOff {
  dateStr: string;  // e.g., "20 Oct"
  fullDate?: string; // ISO date for sorting (e.g., "2024-10-20")
  clubName: string;
  clubUuid?: string;
  time?: string;     // Legacy single time field
  startTime?: string; // e.g., "15:30"
  endTime?: string;   // e.g., "18:00"
  location?: string;
  venue?: string;
  kit_items?: string[];
  uniform_items?: string[];
  designated_parent_uuid?: string;
  reminder_minutes?: number | null;
  schoolsBuddyId?: string | number;
}

// Component to display irregular activities with full term coverage and gap highlighting
interface DefaultClub {
  name: string;
  startTime?: string;
  endTime?: string;
  kit_items?: string[];
  designated_parent_uuid?: string;
  reminder_minutes?: number | null;
  excludedDates?: string[];  // Dates where this recurring club is skipped (e.g. ["14 Oct", "21 Oct"])
}

interface ParentForDropdown {
  uuid: string;
  username: string;
  email: string;
  is_primary: boolean;
}

interface IrregularActivitiesDisplayProps {
  notes: string;
  termStartDate: string;
  termEndDate: string;
  dayOfWeek: number;
  label: string;
  backgroundColor: string;
  borderColor: string;
  textColor: string;
  activeIntegration?: TermPlannerIntegration | null;
  manualOneOffs?: ManualOneOff[];
  defaultClubs?: DefaultClub[];  // Regular clubs that show for every date unless overridden
  onAddManually?: (date: Date, dateStr: string) => void;
  onRemoveManualOneOff?: (dateStr: string) => void;
  onRemoveDefaultClub?: (clubName: string) => void;  // Remove a recurring club from clubs[] array
  onExcludeDateFromClub?: (clubName: string, dateStr: string) => void;  // Skip a specific date for a recurring club
  onEditOneOff?: (dateStr: string, clubs: ManualOneOff[]) => void;  // Edit kit/parent for a date
  onRefreshIntegration?: () => void;
  isRefreshingIntegration?: boolean;
  bookedDates?: string[];  // Dates user has clicked "Book on Integration" for
  onBookedOnIntegration?: (dateStr: string) => void;  // Callback when user books
  // Props for inline kit/parent editing on first occurrence
  activityCategory?: 'drop_off' | 'pick_up' | 'special_lesson';
  onOpenConfigModal?: (activityName: string, kitItems: string[], parentUuid: string, reminderMinutes: number | null) => void;
}

const IrregularActivitiesDisplay: React.FC<IrregularActivitiesDisplayProps> = ({
  notes,
  termStartDate,
  termEndDate,
  dayOfWeek,
  label,
  backgroundColor,
  borderColor,
  textColor,
  activeIntegration,
  manualOneOffs = [],
  defaultClubs = [],
  onAddManually,
  onRemoveManualOneOff,
  onRemoveDefaultClub,
  onExcludeDateFromClub,
  onEditOneOff,
  onRefreshIntegration,
  isRefreshingIntegration = false,
  bookedDates = [],
  onBookedOnIntegration,
  activityCategory,
  onOpenConfigModal
}) => {
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedDateStr, setSelectedDateStr] = useState<string>('');

  const handleNoClubClick = (date: Date, dateStr: string) => {
    setSelectedDate(date);
    setSelectedDateStr(dateStr);
    setShowBookingModal(true);
  };

  const handleAddManually = () => {
    if (selectedDate && onAddManually) {
      onAddManually(selectedDate, selectedDateStr);
    }
    setShowBookingModal(false);
  };

  const handleBookOnIntegration = () => {
    // Open SchoolsBuddy in new tab and notify parent about which date was booked
    if (activeIntegration?.bookingUrl) {
      let bookingUrl = activeIntegration.bookingUrl;
      if (!bookingUrl.endsWith('/')) {
        bookingUrl += '/';
      }
      bookingUrl += 'Activities';
      window.open(bookingUrl, '_blank');

      // Notify parent component that this date was booked - enables refresh button for this date
      if (onBookedOnIntegration && selectedDateStr) {
        onBookedOnIntegration(selectedDateStr);
      }
    }
    setShowBookingModal(false);
  };

  // An open-ended planner has no end date, so fall back to the same 12-month
  // horizon the backend generates into. Keeps the day-by-day picker populated
  // instead of collapsing to the notes-only fallback.
  const effectiveTermEndDate = termEndDate || (() => {
    const anchor = termStartDate && new Date(termStartDate) > new Date() ? new Date(termStartDate) : new Date();
    anchor.setMonth(anchor.getMonth() + 12);
    return `${anchor.getFullYear()}-${String(anchor.getMonth() + 1).padStart(2, '0')}-${String(anchor.getDate()).padStart(2, '0')}`;
  })();

  // Only show fallback if we have no data at all (no notes, no manualOneOffs, and no defaultClubs) OR no start date
  if ((!notes && (!manualOneOffs || manualOneOffs.length === 0) && (!defaultClubs || defaultClubs.length === 0)) || !termStartDate) {
    // Fallback to original display if dates not available or no data
    return (
      <div style={{ marginTop: '15px', padding: '10px', background: backgroundColor, borderRadius: '4px', border: `1px solid ${borderColor}` }}>
        <div style={{ fontWeight: 'bold', marginBottom: '8px', color: textColor }}>{label}</div>
        <div style={{ whiteSpace: 'pre-line', fontSize: '13px', color: textColor }}>
          {notes}
        </div>
      </div>
    );
  }

  // Get all dates for this day of week in the term (future only — past dates can't be changed)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const allDates = getAllDatesForDayOfWeek(termStartDate, effectiveTermEndDate, dayOfWeek).filter(d => d >= today);

  // Parse the ONE-OFFS notes to get dates with clubs (legacy text format)
  const dateToClubs = parseOneOffsNotes(notes);

  // Create a map of manual one-offs by date (new array format with times)
  const manualOneOffsByDate = new Map<string, ManualOneOff[]>();
  manualOneOffs.forEach(oneOff => {
    const existing = manualOneOffsByDate.get(oneOff.dateStr) || [];
    manualOneOffsByDate.set(oneOff.dateStr, [...existing, oneOff]);
  });

  // Helper to calculate time range from clubs
  const getTimeRange = (clubs: ManualOneOff[]): { start: string; end: string } | null => {
    const times = clubs.filter(c => c.startTime || c.endTime);
    if (times.length === 0) return null;

    const startTimes = times.map(c => c.startTime).filter(Boolean).sort();
    const endTimes = times.map(c => c.endTime).filter(Boolean).sort();

    return {
      start: startTimes[0] || '',
      end: endTimes[endTimes.length - 1] || ''
    };
  };

  // Helper to format time for display (remove seconds if present)
  const formatTime = (time: string): string => {
    if (!time) return '';
    // Handle HH:MM:SS format
    const parts = time.split(':');
    if (parts.length >= 2) {
      return `${parts[0]}:${parts[1]}`;
    }
    return time;
  };

  // Helper to consolidate similar club names, sorted by start time
  // Handles two cases:
  // 1. Different names with common prefix: "PREP WAC Slot 1", "PREP WAC Slot 2" → "PREP WAC (Slot 1, Slot 2)"
  // 2. Same name, different times: "PREP WAC" 16:30, "PREP WAC" 17:00 → "PREP WAC (16:30, 17:00)"
  // Helper to format time (remove seconds)
  const formatTimeShort = (time: string): string => {
    if (!time) return '';
    const parts = time.split(':');
    return parts.length >= 2 ? `${parts[0]}:${parts[1]}` : time;
  };

  const consolidateClubNames = (clubs: Array<{name: string; startTime?: string; endTime?: string}>): string[] => {
    if (clubs.length === 0) return [];

    // If there's only one club, don't show times in parentheses (they're already shown in the date header)
    if (clubs.length === 1) {
      return [clubs[0].name];
    }

    // Sort clubs by start time first
    const sortedClubs = [...clubs].sort((a, b) => {
      const timeA = a.startTime || '99:99';
      const timeB = b.startTime || '99:99';
      return timeA.localeCompare(timeB);
    });

    // Group by exact name, tracking time ranges for each club
    const byExactName: { [name: string]: { timeRanges: string[]; earliestTime: string } } = {};
    for (const club of sortedClubs) {
      if (!byExactName[club.name]) {
        byExactName[club.name] = { timeRanges: [], earliestTime: club.startTime || '99:99' };
      }
      const start = formatTimeShort(club.startTime || '');
      const end = formatTimeShort(club.endTime || '');
      let timeStr = '';
      if (start && end) {
        timeStr = `${start}-${end}`;
      } else if (start) {
        timeStr = start;
      }
      if (timeStr && !byExactName[club.name].timeRanges.includes(timeStr)) {
        byExactName[club.name].timeRanges.push(timeStr);
      }
    }

    // Convert to array and sort by earliest start time
    const clubEntries = Object.entries(byExactName).sort((a, b) => {
      return a[1].earliestTime.localeCompare(b[1].earliestTime);
    });

    // If there's only one unique club name, don't show times (already in header)
    if (clubEntries.length === 1) {
      return [clubEntries[0][0]];
    }

    // Build result with time ranges for multiple clubs, sorted by earliest time
    const result: string[] = [];
    for (const [name, data] of clubEntries) {
      if (data.timeRanges.length > 0) {
        result.push(`${name} (${data.timeRanges.join(', ')})`);
      } else {
        result.push(name);
      }
    }

    return result;
  };

  // Pre-compute first occurrences of each activity for showing kit/parent buttons
  // Maps activity name to its first occurrence info (kit_items, designated_parent_uuid, dateStr)
  const firstOccurrences = new Map<string, { dateStr: string; kit_items?: string[]; designated_parent_uuid?: string; reminder_minutes?: number | null }>();
  const seenActivities = new Set<string>();

  // Go through all dates in chronological order to find first occurrences
  for (const date of allDates) {
    const dateStr = formatDateShort(date);
    const manualClubs = manualOneOffsByDate.get(dateStr) || [];

    // Check manual one-offs
    for (const club of manualClubs) {
      if (club.clubName && !seenActivities.has(club.clubName)) {
        seenActivities.add(club.clubName);
        firstOccurrences.set(club.clubName, {
          dateStr,
          kit_items: club.kit_items,
          designated_parent_uuid: club.designated_parent_uuid,
          reminder_minutes: club.reminder_minutes
        });
      }
    }

    // Check default clubs (skip excluded dates)
    for (const club of defaultClubs) {
      if (club.excludedDates && club.excludedDates.includes(dateStr)) continue;
      if (!seenActivities.has(club.name)) {
        seenActivities.add(club.name);
        firstOccurrences.set(club.name, {
          dateStr,
          kit_items: club.kit_items,
          designated_parent_uuid: club.designated_parent_uuid,
          reminder_minutes: club.reminder_minutes
        });
      }
    }
  }

  // Build display with gap highlighting
  return (
    <>
      <div style={{ marginTop: '15px', padding: '10px', background: backgroundColor, borderRadius: '4px', border: `1px solid ${borderColor}` }}>
        <div style={{ fontWeight: 'bold', marginBottom: '8px', color: textColor }}>{label}</div>
        <div style={{ fontSize: '13px' }}>
          {allDates.flatMap((date, idx) => {
            const dateStr = formatDateShort(date);
            const notesClubs = dateToClubs.get(dateStr) || [];
            const manualClubs = manualOneOffsByDate.get(dateStr) || [];
            const hasNotesClubs = notesClubs.length > 0;
            const hasManualClubs = manualClubs.length > 0;
            // Check which default clubs are NOT excluded for this date
            const activeDefaultClubs = defaultClubs.filter(club => !club.excludedDates || !club.excludedDates.includes(dateStr));
            const hasActiveDefaultClubs = activeDefaultClubs.length > 0;
            // Has clubs from any source (one-offs, notes, or default weekly clubs not excluded)
            const hasAnyClubs = hasNotesClubs || hasManualClubs || hasActiveDefaultClubs;

            // Merge all activities for this date and sort by start time
            if (hasManualClubs || hasNotesClubs || hasActiveDefaultClubs) {
              // Build unified list of activities for this date
              const dateActivities: Array<{
                name: string;
                startTime?: string;
                endTime?: string;
                isManual: boolean;  // from manualOneOffs (SB bookings)
              }> = [];

              if (hasManualClubs) {
                manualClubs.forEach(m => {
                  dateActivities.push({ name: m.clubName, startTime: m.startTime, endTime: m.endTime, isManual: true });
                });
              }
              if (hasNotesClubs) {
                notesClubs.forEach(n => {
                  dateActivities.push({ name: n, startTime: undefined, endTime: undefined, isManual: true });
                });
              }
              if (hasActiveDefaultClubs) {
                defaultClubs.forEach(club => {
                  // Skip this club on excluded dates
                  if (club.excludedDates && club.excludedDates.includes(dateStr)) return;
                  dateActivities.push({ name: club.name, startTime: club.startTime, endTime: club.endTime, isManual: false });
                });
              }

              // Sort by start time (activities without time go to the end)
              dateActivities.sort((a, b) => {
                const timeA = a.startTime || '';
                const timeB = b.startTime || '';
                if (!timeA && !timeB) return 0;
                if (!timeA) return 1;
                if (!timeB) return -1;
                return timeA.localeCompare(timeB);
              });

              return dateActivities.map((activity, actIdx) => {
                const actTimeRange = (activity.startTime || activity.endTime) ? {
                  start: activity.startTime || '',
                  end: activity.endTime || ''
                } : null;

                let displayStr = dateStr;
                if (actTimeRange && (actTimeRange.start || actTimeRange.end)) {
                  const startFormatted = formatTime(actTimeRange.start);
                  const endFormatted = formatTime(actTimeRange.end);
                  if (startFormatted && endFormatted) {
                    displayStr = `${dateStr} ${startFormatted}-${endFormatted}`;
                  } else if (startFormatted) {
                    displayStr = `${dateStr} ${startFormatted}`;
                  }
                }

                const firstOccInfo = firstOccurrences.get(activity.name);
                const isFirstOccurrence = firstOccInfo?.dateStr === dateStr;
                const showConfigButton = isFirstOccurrence && activityCategory && onOpenConfigModal;

                return (
                  <div
                    key={`${idx}-${actIdx}`}
                    style={{
                      padding: '4px 8px',
                      marginBottom: '2px',
                      borderRadius: '3px',
                      backgroundColor: activity.isManual ? '#e3f2fd' : 'transparent',
                      color: textColor,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <span style={{ flex: '1 1 auto', wordBreak: 'break-word' }}>
                      {`${displayStr}: ${activity.name}`}
                    </span>
                    {showConfigButton && (() => {
                      const hasConfig = (firstOccInfo?.kit_items && firstOccInfo.kit_items.length > 0) || firstOccInfo?.designated_parent_uuid || (firstOccInfo?.reminder_minutes != null && firstOccInfo.reminder_minutes >= 0);
                      const buttonText = activityCategory === 'special_lesson'
                        ? (hasConfig ? 'Edit Kit & Reminders' : 'Add Kit & Reminders')
                        : (hasConfig ? 'Edit Kit, Parent & Reminders' : 'Add Kit, Parent & Reminders');
                      return (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenConfigModal!(activity.name, firstOccInfo?.kit_items || [], firstOccInfo?.designated_parent_uuid || '', firstOccInfo?.reminder_minutes ?? null);
                          }}
                          style={{
                            padding: '2px 8px',
                            fontSize: '10px',
                            background: hasConfig ? '#007bff' : '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                          title={`Configure ${activity.name}`}
                        >
                          {buttonText}
                        </button>
                      );
                    })()}
                    {activity.isManual && onRemoveManualOneOff && (
                      <span
                        onClick={() => onRemoveManualOneOff(dateStr)}
                        style={{ fontSize: '11px', color: '#dc3545', marginLeft: '8px', cursor: 'pointer' }}
                        title="Remove manual entry"
                      >
                        ✕
                      </span>
                    )}
                    {!activity.isManual && onExcludeDateFromClub && (
                      <span
                        onClick={() => onExcludeDateFromClub(activity.name, dateStr)}
                        style={{ fontSize: '11px', color: '#dc3545', marginLeft: '8px', cursor: 'pointer' }}
                        title={`Skip ${activity.name} on ${dateStr}`}
                      >
                        ✕
                      </span>
                    )}
                    {!activity.isManual && isFirstOccurrence && onRemoveDefaultClub && !onExcludeDateFromClub && (
                      <span
                        onClick={() => onRemoveDefaultClub(activity.name)}
                        style={{ fontSize: '11px', color: '#dc3545', marginLeft: '8px', cursor: 'pointer' }}
                        title={`Remove ${activity.name} from all dates`}
                      >
                        ✕
                      </span>
                    )}
                  </div>
                );
              });
            }

            // No clubs at all - show "No Club" row
            return [(
              <div
                key={idx}
                style={{
                  padding: '4px 8px',
                  marginBottom: '2px',
                  borderRadius: '3px',
                  backgroundColor: '#ffebee',
                  color: '#c62828',
                  fontWeight: '500',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <span
                  onClick={() => handleNoClubClick(date, dateStr)}
                  style={{ cursor: 'pointer', flex: '1 1 auto', wordBreak: 'break-word' }}
                >
                  {`${dateStr}: No Club`}
                </span>
                {bookedDates.includes(dateStr) ? (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onRefreshIntegration && !isRefreshingIntegration) {
                        onRefreshIntegration();
                      }
                    }}
                    style={{
                      fontSize: '11px',
                      color: isRefreshingIntegration ? '#999' : '#28a745',
                      marginLeft: '8px',
                      cursor: isRefreshingIntegration ? 'not-allowed' : 'pointer'
                    }}
                    title={activeIntegration ? `Refresh from ${activeIntegration.name}` : 'Refresh'}
                  >
                    {isRefreshingIntegration ? '⏳ Refreshing...' : '🔄 Refresh'}
                  </span>
                ) : (
                  <span
                    onClick={() => handleNoClubClick(date, dateStr)}
                    style={{ fontSize: '11px', color: '#1976d2', marginLeft: '8px', cursor: 'pointer' }}
                  >
                    + Add
                  </span>
                )}
              </div>
            )];
          })}
        </div>
      </div>

      {/* Booking Modal */}
      {showBookingModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000
          }}
          onMouseDown={() => setShowBookingModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 8px 0', fontSize: '18px' }}>Add Club for {selectedDateStr}</h3>
            <p style={{ color: '#666', fontSize: '14px', margin: '0 0 20px 0' }}>
              How would you like to add a club for this date?
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Add Manually Option */}
              <button
                onClick={handleAddManually}
                style={{
                  padding: '14px 16px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <span>✏️</span> Add Manually
              </button>

              {/* Book on Integration Option - show if integration exists */}
              {activeIntegration && (
                <>
                  <button
                    onClick={handleBookOnIntegration}
                    style={{
                      padding: '14px 16px',
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '15px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    <span>🔗</span> Book on {activeIntegration.name}
                  </button>
                  <div style={{
                    fontSize: '12px',
                    color: '#666',
                    textAlign: 'center',
                    marginTop: '-4px'
                  }}>
                    Opens in new tab • You may need to log in
                  </div>
                </>
              )}

              {/* No Integration Message */}
              {!activeIntegration && (
                <div style={{
                  padding: '12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: '#666',
                  textAlign: 'center'
                }}>
                  💡 Connect an integration in your profile to book clubs directly
                </div>
              )}

              {/* Cancel Button */}
              <button
                onClick={() => setShowBookingModal(false)}
                style={{
                  padding: '12px 16px',
                  backgroundColor: '#f8f9fa',
                  color: '#333',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const ProfileScreen = () => {
  const { user, logout, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [username, setUsername] = useState(user?.username || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [addressLine1, setAddressLine1] = useState(user?.address_line_1 || '');
  const [townCity, setTownCity] = useState(user?.town_city || '');
  const [stateProvinceCountry, setStateProvinceCountry] = useState(user?.state_province_country || '');
  const [postCode, setPostCode] = useState(user?.post_code || '');
  const [loading, setLoading] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  // Parent management state
  const [parents, setParents] = useState<Parent[]>([]);
  const [showAddParentModal, setShowAddParentModal] = useState(false);
  const [newParentFirstName, setNewParentFirstName] = useState('');
  const [newParentLastName, setNewParentLastName] = useState('');
  const [newParentEmail, setNewParentEmail] = useState('');
  const [newParentPhone, setNewParentPhone] = useState('');
  const [newParentPassword, setNewParentPassword] = useState('');
  const [newParentConfirmPassword, setNewParentConfirmPassword] = useState('');
  const [newParentRole, setNewParentRole] = useState('parent');
  const [showParentPassword, setShowParentPassword] = useState(false);
  const [showParentConfirmPassword, setShowParentConfirmPassword] = useState(false);

  // Alarm debug state
  const [showAlarmDebug, setShowAlarmDebug] = useState(false);
  const [alarmDebugInfo, setAlarmDebugInfo] = useState<{
    platform: string;
    isNative: boolean;
    iosVersion: string;
    alarmKitAvailable: boolean;
    alarmKitGranted: boolean;
    alarmKitStatus: string;
    storedReminders: ActivityReminder[];
    pendingLocalNotifs: number;
  } | null>(null);
  const [alarmDebugLoading, setAlarmDebugLoading] = useState(false);
  const [addingParent, setAddingParent] = useState(false);
  const [showNewParentAddressModal, setShowNewParentAddressModal] = useState(false);
  const [newParentDetails, setNewParentDetails] = useState<{email: string, name: string} | null>(null);

  // Child management state
  const [children, setChildren] = useState<Child[]>([]);
  const [showAddChildModal, setShowAddChildModal] = useState(false);
  const [showEditChildModal, setShowEditChildModal] = useState(false);
  const [editingChild, setEditingChild] = useState<Child | null>(null);
  const [newChildFirstName, setNewChildFirstName] = useState('');
  const [newChildLastName, setNewChildLastName] = useState('');
  const [newChildBirthDate, setNewChildBirthDate] = useState('');
  const [newChildBirthDay, setNewChildBirthDay] = useState('');
  const [newChildBirthMonth, setNewChildBirthMonth] = useState('');
  const [newChildBirthYear, setNewChildBirthYear] = useState('');
  const [newChildInterests, setNewChildInterests] = useState<string[]>([]);
  const [newChildSchoolVenue, setNewChildSchoolVenue] = useState('');
  const [newChildSchoolCity, setNewChildSchoolCity] = useState('');
  const [editChildFirstName, setEditChildFirstName] = useState('');
  const [editChildLastName, setEditChildLastName] = useState('');
  const [editChildBirthDate, setEditChildBirthDate] = useState('');
  const [editChildBirthDay, setEditChildBirthDay] = useState('');
  const [editChildBirthMonth, setEditChildBirthMonth] = useState('');
  const [editChildBirthYear, setEditChildBirthYear] = useState('');
  const [editChildInterests, setEditChildInterests] = useState<string[]>([]);
  const [editChildSchoolVenue, setEditChildSchoolVenue] = useState('');
  const [editChildSchoolCity, setEditChildSchoolCity] = useState('');
  const [savingChild, setSavingChild] = useState(false);
  const [updatingChild, setUpdatingChild] = useState(false);

  // Collapsible sections state (persist in localStorage)
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem('profileCollapsedSections');
      return saved ? JSON.parse(saved) : {
        children: true,
        family: true,
        integrations: true,
        termPlanners: true,
        notifications: true
      };
    } catch {
      return { children: true, family: true, integrations: true, termPlanners: true, notifications: true };
    }
  });

  const toggleSection = (section: string) => {
    setCollapsedSections(prev => {
      const updated = { ...prev, [section]: !(prev[section] ?? true) };
      localStorage.setItem('profileCollapsedSections', JSON.stringify(updated));
      return updated;
    });
  };

  // Notification preferences state
  const [notifPrefs, setNotifPrefs] = useState<{ coverage_gap: boolean; daily_summary: boolean }>({ coverage_gap: true, daily_summary: true });
  const [loadingNotifPrefs, setLoadingNotifPrefs] = useState(false);

  // Integrations state
  const [integrations, setIntegrations] = useState<any[]>([]);
  const [loadingIntegrations, setLoadingIntegrations] = useState(false);
  const [primaryParentUuid, setPrimaryParentUuid] = useState<string | null>(null);
  const [selectedIntegration, setSelectedIntegration] = useState<any>(null);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [pitcheroUsername, setPitcheroUsername] = useState('');
  const [pitcheroPassword, setPitcheroPassword] = useState('');
  const [showPitcheroPassword, setShowPitcheroPassword] = useState(false);
  const [savingCredentials, setSavingCredentials] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showManualMatching, setShowManualMatching] = useState(false);
  const [unmatchedChildren, setUnmatchedChildren] = useState<any[]>([]);
  const [cluubzChildren, setCluubzChildren] = useState<any[]>([]);
  const [childMatches, setChildMatches] = useState<{ [pitcheroMemberId: string]: string }>({});
  const [pitcheroMappings, setPitcheroMappings] = useState<any[]>([]);
  const [isReviewMode, setIsReviewMode] = useState(false); // true = review all mappings, false = manual match unmatched only
  // Track selected clubs/teams for Pitchero import (key: mappingId-clubName or mappingId-teamId)
  const [reviewSelectedClubs, setReviewSelectedClubs] = useState<{ [key: string]: boolean }>({});
  const [reviewExcludedChildren, setReviewExcludedChildren] = useState<{ [mappingId: number]: boolean }>({});
  const [showSOCSModal, setShowSOCSModal] = useState(false);
  const [syncingSOCS, setSyncingSOCS] = useState(false);
  const [showSchoolsBuddyModal, setShowSchoolsBuddyModal] = useState(false);
  const [loadingTermPlannerTransition, setLoadingTermPlannerTransition] = useState(false);
  const [loadingTermPlannerMessage, setLoadingTermPlannerMessage] = useState<{ title: string; subtitle: string } | null>(null);
  const [showPitcheroModal, setShowPitcheroModal] = useState(false);
  const [showSchoolPortalModal, setShowSchoolPortalModal] = useState(false);
  const [showSpondModal, setShowSpondModal] = useState(false);
  const [showIntegrationBuilder, setShowIntegrationBuilder] = useState(false);

  // Enable flow is a two-step wizard:
  //   Step 1 (creds): parent enters login → we run a live probe scrape,
  //                    fetch the actual fixtures response for THEIR account,
  //                    and derive the picker from the ground-truth data.
  //   Step 2 (mapping): parent maps identities from the live response →
  //                     Cluubz children, then we save credentials + mapping
  //                     and fire the first real sync.
  // Rationale: the publisher's stored sample doesn't reflect this parent's
  // actual data. Two parents on the same connector see different teams,
  // different players in members[], different behalfOfIds. Probing first
  // makes the mapping show only what's relevant to THIS parent.
  const [browserSyncStatus, setBrowserSyncStatus] = useState<Record<string, { running?: boolean; kind: 'info' | 'ok' | 'err'; msg: string }>>({});

  const CLUUBZ_EXT_ID = 'CLUUBZ_EXTENSION_ID_HERE'; // TODO: replace once published
  const runBrowserSync = async (connectorUuid: string) => {
    const w = window as any;
    const jwt = localStorage.getItem('authToken') || '';
    setBrowserSyncStatus(s => ({ ...s, [connectorUuid]: { running: true, kind: 'info', msg: '⏳ Starting…' } }));
    // Native (Capacitor iOS/Android): use CapacitorHttp — no extension needed.
    try {
      const { isNativeCapable, runBrowserSyncNative } = await import('../services/browserSync');
      if (isNativeCapable()) {
        try {
          const r = await runBrowserSyncNative(connectorUuid, jwt, (p) => {
            let msg = '';
            if (p.kind === 'starting') msg = `⏳ Fetching ${p.totalUrls} team page${p.totalUrls === 1 ? '' : 's'}…`;
            else if (p.kind === 'fetching') msg = `⏳ Fetching team ${p.index}/${p.totalUrls}…`;
            else if (p.kind === 'parsed') msg = `📄 Parsed ${p.rowCount} rows`;
            else if (p.kind === 'posting') msg = `⏳ Saving ${p.totalRows} fixtures…`;
            if (msg) setBrowserSyncStatus(s => ({ ...s, [connectorUuid]: { running: true, kind: 'info', msg } }));
          });
          setBrowserSyncStatus(s => ({ ...s, [connectorUuid]: { kind: 'ok', msg: `✅ Synced ${r.activitiesWritten} activities across ${r.childrenAffected} child(ren)` } }));
          loadIntegrations();
          return;
        } catch (e: any) {
          setBrowserSyncStatus(s => ({ ...s, [connectorUuid]: { kind: 'err', msg: '⚠️ ' + e.message } }));
          return;
        }
      }
    } catch (_) { /* import failed, fall through to extension path */ }
    // Desktop web: route via Chrome extension using a window.postMessage
    // bridge (no extension-id needed — the extension's content script runs
    // on this page and relays to background).
    try {
      const onWinMsg = (ev: MessageEvent) => {
        const p = (ev.data as any)?.__cluubz_browser_sync;
        if (!p || p.connectorUuid !== connectorUuid) return;
        if (p.type === 'sync-done') {
          window.removeEventListener('message', onWinMsg);
          setBrowserSyncStatus(s => ({ ...s, [connectorUuid]: { kind: 'ok', msg: `✅ Synced ${p.activitiesWritten} activities across ${p.childrenAffected || 0} child(ren)` } }));
          loadIntegrations();
        } else if (p.type === 'error') {
          window.removeEventListener('message', onWinMsg);
          setBrowserSyncStatus(s => ({ ...s, [connectorUuid]: { kind: 'err', msg: '⚠️ ' + (p.error || 'Sync failed') } }));
        } else if (p.type === 'progress') {
          setBrowserSyncStatus(s => ({ ...s, [connectorUuid]: { running: true, kind: 'info', msg: p.msg || '⏳ Syncing…' } }));
        }
      };
      window.addEventListener('message', onWinMsg);
      window.postMessage({ __cluubz_browser_sync_start: true, connectorUuid, jwt }, '*');
      // Fail-safe: if no ack in 3s, extension probably isn't installed on
      // this page (content.js didn't inject).
      setTimeout(() => {
        setBrowserSyncStatus(s => {
          const cur = s[connectorUuid];
          if (cur && cur.msg && cur.msg !== '⏳ Starting…') return s;
          window.removeEventListener('message', onWinMsg);
          return { ...s, [connectorUuid]: { kind: 'err', msg: '⚠️ Cluubz Capture extension not installed or not enabled for this site.' } };
        });
      }, 3000);
    } catch (e: any) {
      setBrowserSyncStatus(s => ({ ...s, [connectorUuid]: { kind: 'err', msg: '⚠️ ' + e.message } }));
    }
  };

  const openCrowdEnable = (connector: any) => {
    setCrowdEnableFor(connector);
    setCrowdCreds({ username: '', password: '' });
    setCrowdMapping({});
    setCrowdEnableResult(null);
    setCrowdPicker(null);
    setCrowdBusy(false);
    // Public-site connectors need no credentials — kick off probe immediately.
    if (connector.requires_login === false) {
      setTimeout(() => runCrowdProbeFor(connector), 0);
    }
  };

  const runCrowdProbeFor = async (connector: any) => {
    setCrowdBusy(true); setCrowdEnableResult(null);
    try {
      const resp = await apiService.crowdProbe(connector.uuid, { username: '', password: '' });
      if (!resp.success) { setCrowdEnableResult(resp.error || 'Probe failed'); return; }
      if (resp.data?.ok) {
        setCrowdPicker(resp.data);
        setCrowdMapping({ ...(resp.data.suggestions || {}) });
      } else {
        setCrowdEnableResult(resp.data?.error || 'Probe did not succeed');
      }
    } catch (e: any) { setCrowdEnableResult(e.message || 'Probe error'); }
    finally { setCrowdBusy(false); }
  };

  const runCrowdProbe = async () => {
    if (!crowdEnableFor) return;
    if (crowdEnableFor.requires_login !== false && (!crowdCreds.username || !crowdCreds.password)) return;
    setCrowdBusy(true); setCrowdEnableResult(null);
    try {
      const resp = await apiService.crowdProbe(crowdEnableFor.uuid, {
        username: crowdCreds.username, password: crowdCreds.password,
      });
      if (!resp.success) {
        const diag = resp.data?.diag?.loginDiag;
        const msg = diag ? `Login returned HTTP ${diag.loginStatus} with a ${diag.tokenLength}-char token. Check your username and password.` : (resp.error || 'Probe failed');
        setCrowdEnableResult(msg);
      } else if (resp.data?.ok) {
        setCrowdPicker(resp.data);
        // Only auto-fill mappings where we have a real fuzzy suggestion.
        // Do NOT default to "first child" — that misleads the parent into
        // thinking Cluubz found a match when it didn't. Empty dropdowns
        // stay on '— Don't import —' until the parent picks.
        setCrowdMapping({ ...(resp.data.suggestions || {}) });
      } else {
        setCrowdEnableResult(resp.data?.error || 'Probe did not succeed');
      }
    } catch (e: any) { setCrowdEnableResult(e.message); }
    setCrowdBusy(false);
  };

  const submitCrowdEnable = async () => {
    if (!crowdEnableFor || !crowdPicker) return;
    if (crowdEnableFor.requires_login !== false && (!crowdCreds.username || !crowdCreds.password)) return;
    setCrowdBusy(true); setCrowdEnableResult(null);
    try {
      // Strip out empties — the dropdown's "— Don't import —" option is stored
      // as '' and shouldn't reach the backend (empty string counts as a valid
      // mapping in the worker's Object.keys check).
      const cleanedMapping = Object.fromEntries(Object.entries(crowdMapping).filter(([, v]) => v));
      const en = await apiService.crowdEnable(crowdEnableFor.uuid, {
        username: crowdCreds.username, password: crowdCreds.password, childMapping: cleanedMapping,
      });
      if (!en.success) { setCrowdEnableResult(en.error || 'Enable failed'); setCrowdBusy(false); return; }
      // Browser-mode connectors can't be synced from the Heroku backend
      // (the site blocks our IP). Fire the client-side sync path instead —
      // the extension / Capacitor app fetches with the parent's own IP.
      if (crowdPicker?.syncMode === 'browser') {
        setCrowdEnableResult('✅ Enabled — starting browser sync…');
        setCrowdBusy(false);
        runBrowserSync(crowdEnableFor.uuid);
        runCrowdSearch(crowdQuery);
        return;
      }
      const sy = await apiService.crowdSyncNow(crowdEnableFor.uuid);
      if (!sy.success) { setCrowdEnableResult(sy.error || 'First sync failed'); setCrowdBusy(false); return; }
      const written = sy.data?.activitiesWritten ?? 0;
      setCrowdEnableResult(`✅ Enabled — wrote ${written} activities across ${sy.data?.clubs ?? 0} child(ren).`);
      runCrowdSearch(crowdQuery); // refresh so the "Enabled" chip shows
    } catch (e: any) { setCrowdEnableResult(e.message); }
    setCrowdBusy(false);
  };

  // Crowd Connectors — community-built integrations discoverable by any parent.
  // State kept local to the profile screen; the enable flow drives a modal.
  const [crowdQuery, setCrowdQuery] = useState('');
  const [crowdResults, setCrowdResults] = useState<any[]>([]);
  const [crowdLoading, setCrowdLoading] = useState(false);
  const [crowdError, setCrowdError] = useState<string | null>(null);
  const [crowdEnableFor, setCrowdEnableFor] = useState<any | null>(null); // connector row being enabled
  const [crowdPicker, setCrowdPicker] = useState<any | null>(null);       // /picker payload
  const [crowdCreds, setCrowdCreds] = useState({ username: '', password: '' });
  const [crowdMapping, setCrowdMapping] = useState<Record<string, string>>({});
  const [crowdBusy, setCrowdBusy] = useState(false);
  const [crowdEnableResult, setCrowdEnableResult] = useState<string | null>(null);

  // Debounced Crowd Connectors search — fires 250ms after last keystroke so
  // we don't spam the DB while typing. Empty query returns top 50 (server
  // orders by success_count desc so proven connectors surface first).
  const runCrowdSearch = React.useCallback(async (q: string) => {
    setCrowdLoading(true); setCrowdError(null);
    try {
      const resp = await apiService.crowdSearch(q);
      if (resp.success && resp.data) setCrowdResults(resp.data.integrations || []);
      else { setCrowdError(resp.error || 'Search failed'); setCrowdResults([]); }
    } catch (e: any) { setCrowdError(e.message || 'Search failed'); setCrowdResults([]); }
    setCrowdLoading(false);
  }, []);
  React.useEffect(() => {
    const t = setTimeout(() => runCrowdSearch(crowdQuery), 250);
    return () => clearTimeout(t);
  }, [crowdQuery, runCrowdSearch]);
  const [integrationEmails, setIntegrationEmails] = useState<string[]>([]); // Emails used across integrations
  const [showSchoolsBuddyPassword, setShowSchoolsBuddyPassword] = useState(false);
  const [syncingSchoolsBuddy, setSyncingSchoolsBuddy] = useState(false);
  const [schoolsBuddyMappings, setSchoolsBuddyMappings] = useState<any[]>([]);
  const [selectedTermPlanner, setSelectedTermPlanner] = useState<string>('');
  const [termPlanners, setTermPlanners] = useState<any[]>([]);
  const [schoolStartTime, setSchoolStartTime] = useState<string>('08:45');
  const [schoolEndTime, setSchoolEndTime] = useState<string>('15:30');
  const [showTermPlannerModal, setShowTermPlannerModal] = useState(false);
  const [termPlannerChild, setTermPlannerChild] = useState<Child | null>(null);
  const [termPlannerStep, setTermPlannerStep] = useState<'list' | 'intro' | 'setup' | 'create'>('list');
  const [cameFromOnboarding, setCameFromOnboarding] = useState(false);  // Track if opened from onboarding flow
  const [onboardingTermPlanners, setOnboardingTermPlanners] = useState<{childUuid: string; childName: string; termPlannerUuid: string}[]>([]);
  const [onboardingChildrenWithoutPlanners, setOnboardingChildrenWithoutPlanners] = useState<any[]>([]);
  const [currentOnboardingTermPlannerIndex, setCurrentOnboardingTermPlannerIndex] = useState(0);
  const processedOnboardingChildrenRef = useRef<Set<string>>(new Set()); // Track children who have had planners saved
  // `currentChildName` is empty when this slide is shown BEFORE the first TP
  // editor opens (no prior child has been saved yet). When non-empty, the top
  // "Term planner saved for X!" confirmation line also renders.
  // `hasIntegrations` drives copy: true → "Let's set up X's activities … we'll
  // import any activities from your connected apps"; false → shorter "Let's
  // set up X's term planner" (no mention of imports).
  const [nextChildIntroSlide, setNextChildIntroSlide] = useState<{
    nextChildName: string;
    currentChildName: string;
    activitiesCreated: number;
    alarmsScheduled: number;
    remainingCount: number;
    hasIntegrations: boolean;
  } | null>(null);

  // True if any integration is configured for this child. Used to pick intro
  // copy: if the TP is going to be pre-populated from an integration cache,
  // the intro mentions imports; otherwise it's a plain "set up term planner".
  const childHasIntegrations = (childUuid: string): boolean => {
    for (const integration of integrations) {
      if (!integration?.connected) continue;
      const matched = integration.matchedChildren;
      if (!matched) continue;
      const matches = Array.isArray(matched)
        ? matched.some((m: any) => m?.childUuid === childUuid || m?.uuid === childUuid || m?.child_uuid === childUuid)
        : false;
      if (matches) return true;
    }
    if (schoolsBuddyMappings.some((m: any) => m.cluubzChildUuid === childUuid)) return true;
    return false;
  };
  const pendingNextChildActionRef = useRef<(() => void) | null>(null);
  const termPlannerBodyRef = useRef<HTMLDivElement>(null);
  const [existingTermPlanners, setExistingTermPlanners] = useState<any[]>([]);
  const [loadingTermPlanners, setLoadingTermPlanners] = useState(false);
  const [allTermPlanners, setAllTermPlanners] = useState<Record<string, any[]>>({});  // Grouped by child UUID
  const [loadingAllTermPlanners, setLoadingAllTermPlanners] = useState(false);

  // The planner currently in force for the child being set up, if any. A new
  // planner supersedes this one from its start date. Open-ended planners
  // (end_date null) are always live; older ones are live until their end date.
  // A planner already queued to start later. Worth naming, so a parent setting one
  // up in advance can see what it will butt up against — the backend clamps this
  // planner's end to the day before it, silently otherwise.
  const upcomingPlanner = useMemo(() => {
    if (!termPlannerChild) return null;
    const merged = [
      ...(allTermPlanners[termPlannerChild.uuid] || []),
      ...existingTermPlanners,
    ].filter((tp: any) => !tp.child_uuid || tp.child_uuid === termPlannerChild.uuid);
    return findUpcomingPlanner(merged);
  }, [existingTermPlanners, allTermPlanners, termPlannerChild]);

  // The planner this new one would replace. Both stores are passed because either
  // may be the one that loaded first, and findLivePlanner filters to this child.
  const outgoingPlanner = useMemo(
    () => findLivePlanner(
      termPlannerChild?.uuid,
      allTermPlanners[termPlannerChild?.uuid || ''],
      existingTermPlanners
    ),
    [existingTermPlanners, allTermPlanners, termPlannerChild]
  );

  // Term planner form state
  // Planners are open-ended: they start on a date the parent actually knows and
  // run until a newer planner supersedes them. The first planner starts today;
  // a subsequent one starts on the date picked for the new term. end_date is
  // always null on create — the backend fills it in when superseding.
  const todayForInput = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const getDefaultPlannerDates = () => {
    // Provisional only — the backend names the planner from the dates it actually
    // saves, which may differ if the end date gets clamped.
    const start = todayForInput();
    return {
      startDate: start,
      endDate: '',
      termName: plannerNameFromDates(start, '')
    };
  };
  const [termName, setTermName] = useState('');
  const [termStartDate, setTermStartDate] = useState('');
  const [termEndDate, setTermEndDate] = useState('');
  const [currentDayTab, setCurrentDayTab] = useState(1); // Start on Monday (day 1)
  const [editingTermPlannerUuid, setEditingTermPlannerUuid] = useState<string | null>(null);
  const [dailySchedules, setDailySchedules] = useState<any>({
    0: { drop_off: { manualOneOffs: [] }, club_reminders: [], pick_up: { manualOneOffs: [] } },
    1: { drop_off: { manualOneOffs: [] }, club_reminders: [], pick_up: { manualOneOffs: [] } },
    2: { drop_off: { manualOneOffs: [] }, club_reminders: [], pick_up: { manualOneOffs: [] } },
    3: { drop_off: { manualOneOffs: [] }, club_reminders: [], pick_up: { manualOneOffs: [] } },
    4: { drop_off: { manualOneOffs: [] }, club_reminders: [], pick_up: { manualOneOffs: [] } },
    5: { drop_off: { manualOneOffs: [] }, club_reminders: [], pick_up: { manualOneOffs: [] } },
    6: { drop_off: { manualOneOffs: [] }, club_reminders: [], pick_up: { manualOneOffs: [] } }
  });
  const [creatingTermPlanner, setCreatingTermPlanner] = useState(false);
  const [showClubModal, setShowClubModal] = useState(false);
  const [clubModalType, setClubModalType] = useState<'drop_off' | 'pick_up' | 'club_reminder'>('drop_off');
  const [currentClubReminderIndex, setCurrentClubReminderIndex] = useState<number>(0);
  const dailyScheduleRef = useRef<HTMLDivElement>(null);

  // One-off manual add state
  const [oneOffDate, setOneOffDate] = useState<Date | null>(null);
  const [oneOffDateStr, setOneOffDateStr] = useState<string>('');
  const [oneOffType, setOneOffType] = useState<'drop_off' | 'pick_up' | 'special_lesson'>('pick_up');
  const [showOneOffClubModal, setShowOneOffClubModal] = useState(false);
  const [showOneOffTimeModal, setShowOneOffTimeModal] = useState(false);
  const [oneOffSelectedClub, setOneOffSelectedClub] = useState<any>(null);
  const [oneOffTime, setOneOffTime] = useState<string>('');
  const [oneOffKitItems, setOneOffKitItems] = useState<string[]>([]);
  const [oneOffDesignatedParent, setOneOffDesignatedParent] = useState<string>('');
  const [oneOffReminderMinutes, setOneOffReminderMinutes] = useState<number | null>(null);
  const [oneOffReminderTarget, setOneOffReminderTarget] = useState<'start' | 'end' | 'both' | null>(null);
  const [oneOffUniform, setOneOffUniform] = useState<string[]>([]);
  const [integrationBookedDates, setIntegrationBookedDates] = useState<string[]>([]);  // Track dates user clicked "Book on Integration"

  // Edit one-off kit/parent modal state
  const [showEditOneOffModal, setShowEditOneOffModal] = useState(false);
  const [editOneOffDateStr, setEditOneOffDateStr] = useState<string>('');
  const [editOneOffClubs, setEditOneOffClubs] = useState<ManualOneOff[]>([]);
  const [editOneOffKitItems, setEditOneOffKitItems] = useState<string[]>([]);
  const [editOneOffDesignatedParent, setEditOneOffDesignatedParent] = useState<string>('');
  const [editOneOffType, setEditOneOffType] = useState<'drop_off' | 'pick_up'>('pick_up');

  // Activity configuration modal state (for term planner kit/parent editing)
  const [showActivityConfigModal, setShowActivityConfigModal] = useState(false);
  const [configActivityName, setConfigActivityName] = useState<string>('');
  const [configActivityCategory, setConfigActivityCategory] = useState<'drop_off' | 'pick_up' | 'special_lesson'>('pick_up');
  const [configKitItems, setConfigKitItems] = useState<string[]>([]);
  const [configNewKitItem, setConfigNewKitItem] = useState('');
  const [configParentUuid, setConfigParentUuid] = useState<string>('');
  const [configReminderMinutes, setConfigReminderMinutes] = useState<number | null>(null);
  const [configReminderTarget, setConfigReminderTarget] = useState<'start' | 'end' | 'both' | null>(null);

  // Track unsaved changes in term planner
  const [hasUnsavedTermPlannerChanges, setHasUnsavedTermPlannerChanges] = useState(false);

  // SchoolsBuddy preview/configuration step state
  const [previewedClubs, setPreviewedClubs] = useState<any[]>([]);
  const [previewDisplayGroups, setPreviewDisplayGroups] = useState<any[]>([]);  // Grouped by date/time for display
  const [clubConfigurations, setClubConfigurations] = useState<Record<string, {
    kit_items: string[];
    designated_parent_uuid: string;
    uniform_items: string[];
  }>>({});
  const [fetchingPreview, setFetchingPreview] = useState(false);

  // "Replace With Club" state for overriding default times
  const [showReplaceClubModal, setShowReplaceClubModal] = useState(false);
  const [replaceClubType, setReplaceClubType] = useState<'drop_off' | 'pick_up'>('drop_off');
  const [replaceClubSelected, setReplaceClubSelected] = useState<any>(null);
  const [showReplaceClubDetailsModal, setShowReplaceClubDetailsModal] = useState(false);
  const [replaceClubStartTime, setReplaceClubStartTime] = useState<string>('');
  const [replaceClubEndTime, setReplaceClubEndTime] = useState<string>('');
  const [replaceClubKitItems, setReplaceClubKitItems] = useState<string[]>([]);
  const [replaceClubUniform, setReplaceClubUniform] = useState<string[]>([]);
  const [replaceClubDesignatedParent, setReplaceClubDesignatedParent] = useState<string>('');
  const [replaceClubReminderMinutes, setReplaceClubReminderMinutes] = useState<number | null>(null);
  const [replaceClubReminderTarget, setReplaceClubReminderTarget] = useState<'start' | 'end' | 'both' | null>(null);

  // Delete All Schedule confirmation modal state
  const [showDeleteScheduleModal, setShowDeleteScheduleModal] = useState(false);
  const [deleteScheduleCategory, setDeleteScheduleCategory] = useState<'drop_off' | 'special_lessons' | 'pick_up' | null>(null);

  // Day names constant for term planner
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const apiService = ApiService.getInstance();

  // Sync local state when user data changes (e.g., after login)
  useEffect(() => {
    if (user) {
      setUsername(user.username || '');
      setEmail(user.email || '');
      setPhone(user.phone || '');
      setAddressLine1(user.address_line_1 || '');
      setTownCity(user.town_city || '');
      setStateProvinceCountry(user.state_province_country || '');
      setPostCode(user.post_code || '');
    }
  }, [user]);

  // Load notification preferences
  const loadNotifPrefs = async () => {
    setLoadingNotifPrefs(true);
    try {
      const response = await apiService.getNotificationPreferences();
      if (response.success && response.data) {
        setNotifPrefs(response.data);
      }
    } catch (error) {
      console.error('Error loading notification preferences:', error);
    } finally {
      setLoadingNotifPrefs(false);
    }
  };

  const handleToggleNotifPref = async (key: 'coverage_gap' | 'daily_summary') => {
    const oldValue = notifPrefs[key];
    const newValue = !oldValue;

    // Optimistic update
    setNotifPrefs(prev => ({ ...prev, [key]: newValue }));

    try {
      const response = await apiService.updateNotificationPreferences({ [key]: newValue });
      if (response.success && response.data) {
        setNotifPrefs(response.data);
      } else {
        // Revert on failure
        setNotifPrefs(prev => ({ ...prev, [key]: oldValue }));
      }
    } catch (error) {
      console.error('Error updating notification preference:', error);
      setNotifPrefs(prev => ({ ...prev, [key]: oldValue }));
    }
  };

  // Load parents, children, and integrations when component mounts
  useEffect(() => {
    if (user) {
      loadParents();
      loadChildren();
      loadIntegrations();
      loadNotifPrefs();
    }
  }, [user]);

  // Load term planners when children or parents change
  useEffect(() => {
    if (children.length > 0 && parents.length > 0) {
      loadAllTermPlanners();
    }
  }, [children, parents]);

  // Handle editChild URL parameter for skeleton child editing
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const editChildUuid = urlParams.get('editChild');

    if (editChildUuid && children.length > 0) {
      const childToEdit = children.find(c => c.uuid === editChildUuid);
      if (childToEdit) {
        handleEditChild(childToEdit);
        // Clear the URL parameter
        window.history.replaceState({}, '', '/profile');
      }
    }
  }, [location.search, children]);

  // Handle navigation state for creating new term planner from HomeScreen
  useEffect(() => {
    const state = location.state as any;
    // Waits for `parents` too: the conflict check needs the primary parent to load
    // the child's existing planners, and firing before they arrive would silently
    // skip it and show the first-planner screen again.
    if (state?.createNewTermPlanner && state?.childUuid && children.length > 0 && parents.length > 0) {
      const child = children.find((c: any) => c.uuid === state.childUuid);
      if (child) {
        console.log('🟢 ProfileScreen: Creating new term planner from HomeScreen for', child.name);
        // Go directly to setup step (term name, dates, school times) instead of list
        const defaults = getDefaultPlannerDates();
        setTermPlannerChild(child);
        setTermName(defaults.termName);
        setTermStartDate(defaults.startDate);
        setTermEndDate(defaults.endDate);
        setSchoolStartTime('08:45');
        setSchoolEndTime('15:30');
        setEditingTermPlannerUuid(null);
        setShowTermPlannerModal(true);

        // Load the child's planners BEFORE showing the setup step. Arriving this
        // way — the home-screen banner, and "Set up a new term" after a holiday —
        // skipped the load entirely, so an existing planner went undetected and the
        // parent got the first-planner "Starts today" screen with no conflict
        // prompt. Showing the step first and filling in later is not enough either:
        // the screen would start on the wrong branch and a quick tap on Continue
        // would replace the live planner without ever asking.
        setLoadingTermPlanners(true);
        setTermPlannerStep('list');
        (async () => {
          try {
            const primaryParent = parents.find((p: any) => p.is_primary) || parents[0];
            if (primaryParent) {
              const response = await apiService.getTermPlanners(primaryParent.uuid, child.uuid);
              if (response.success && response.data) {
                setExistingTermPlanners(response.data);
              }
            }
          } catch (error) {
            console.error('Failed to load existing planners for conflict check:', error);
          } finally {
            setLoadingTermPlanners(false);
            setTermPlannerStep('setup');
          }
        })();
        // Clear the navigation state
        window.history.replaceState({}, '', '/profile');
      }
    }
  }, [location.state, children, parents]);

  // Handle navigation state for opening term planner modal from onboarding
  useEffect(() => {
    const state = location.state as {
      openTermPlanner?: boolean;
      termPlanners?: {childUuid: string; childName: string; termPlannerUuid: string}[];
      childrenWithoutPlanners?: any[];
      currentTermPlannerIndex?: number;
      fromOnboarding?: boolean;
    } | null;

    if (state?.openTermPlanner && children.length > 0 && parents.length > 0) {
      const termPlanners = state.termPlanners || [];
      const childrenWithoutPlanners = (state.childrenWithoutPlanners || []).filter((c: any) => !c.is_parent);

      // Only proceed if there are term planners OR children needing planners
      if (termPlanners.length === 0 && childrenWithoutPlanners.length === 0) return;

      console.log('🟢 ProfileScreen: Opening term planner from navigation state', state);

      // Track onboarding context
      if (state.fromOnboarding) {
        setCameFromOnboarding(true);
        setOnboardingTermPlanners(termPlanners);
        setOnboardingChildrenWithoutPlanners(childrenWithoutPlanners);
        setCurrentOnboardingTermPlannerIndex(state.currentTermPlannerIndex || 0);
      }

      // Get the first term planner to show (if any exist)
      const currentTP = termPlanners[state.currentTermPlannerIndex || 0];
      if (currentTP) {
        // Existing TP (e.g. from SchoolsBuddy) — show intro then load editor
        const child = children.find((c: any) => c.uuid === currentTP.childUuid);
        if (child) {
          setTermPlannerChild(child);
          const nextChildName = child.first_name || child.name?.split(' ')[0] || child.name || 'your child';
          const remainingCount = termPlanners.length + childrenWithoutPlanners.length;

          pendingNextChildActionRef.current = async () => {
            setLoadingTermPlannerTransition(true);
            try {
              await handleEditTermPlanner(currentTP.termPlannerUuid);
              const defaults = getDefaultPlannerDates();
              setTermStartDate(defaults.startDate);
              setTermEndDate(defaults.endDate);
              setTermName(defaults.termName);
              setTermPlannerStep('setup');
              setShowTermPlannerModal(true);
              setTimeout(() => termPlannerBodyRef.current?.scrollTo(0, 0), 50);
            } finally {
              setLoadingTermPlannerTransition(false);
            }
          };
          setNextChildIntroSlide({
            nextChildName,
            currentChildName: '',
            activitiesCreated: 0,
            alarmsScheduled: 0,
            remainingCount,
            hasIntegrations: childHasIntegrations(child.uuid),
          });
        }
      } else if (childrenWithoutPlanners.length > 0) {
        // No existing TPs — first child needs manual setup
        // Show intro then open dates form (setup step)
        const nextChild = childrenWithoutPlanners[0];
        const child = children.find((c: any) => c.uuid === nextChild.uuid) || nextChild;
        const nextChildName = child.first_name || child.name?.split(' ')[0] || child.name || 'your child';

        setTermPlannerChild(child);

        pendingNextChildActionRef.current = () => {
          setOnboardingChildrenWithoutPlanners(childrenWithoutPlanners.slice(1));
          const defaults = getDefaultPlannerDates();
          setEditingTermPlannerUuid(null);
          setTermName(defaults.termName);
          setTermStartDate(defaults.startDate);
          setTermEndDate(defaults.endDate);
          setSchoolStartTime('08:45');
          setSchoolEndTime('15:30');
          setDailySchedules({
            0: { drop_off: { manualOneOffs: [] }, club_reminders: [], pick_up: { manualOneOffs: [] } },
            1: { drop_off: { manualOneOffs: [] }, club_reminders: [], pick_up: { manualOneOffs: [] } },
            2: { drop_off: { manualOneOffs: [] }, club_reminders: [], pick_up: { manualOneOffs: [] } },
            3: { drop_off: { manualOneOffs: [] }, club_reminders: [], pick_up: { manualOneOffs: [] } },
            4: { drop_off: { manualOneOffs: [] }, club_reminders: [], pick_up: { manualOneOffs: [] } },
            5: { drop_off: { manualOneOffs: [] }, club_reminders: [], pick_up: { manualOneOffs: [] } },
            6: { drop_off: { manualOneOffs: [] }, club_reminders: [], pick_up: { manualOneOffs: [] } }
          });
          setCurrentDayTab(1);
          setHasUnsavedTermPlannerChanges(false);
          setTermPlannerStep('setup');
          setShowTermPlannerModal(true);
          setTimeout(() => termPlannerBodyRef.current?.scrollTo(0, 0), 50);
        };

        setNextChildIntroSlide({
          nextChildName,
          currentChildName: '',
          activitiesCreated: 0,
          alarmsScheduled: 0,
          remainingCount: childrenWithoutPlanners.length,
          hasIntegrations: childHasIntegrations(child.uuid),
        });
      }

      // Clear the navigation state to prevent re-opening on subsequent renders
      window.history.replaceState({}, '', '/profile');
    }
  }, [location.state, children, parents]);

  // Prevent modal dismissal from global keyboard events
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (showAddParentModal) {
        // Prevent browser back navigation on backspace
        if (e.key === 'Backspace' && e.target === document.body) {
          e.preventDefault();
        }
        // Only allow Escape to close modal
        if (e.key === 'Escape') {
          setShowAddParentModal(false);
        }
      }
    };

    if (showAddParentModal) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent scroll on modal background
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [showAddParentModal]);

  const loadParents = async () => {
    try {
      const response = await apiService.getParents();
      if (response.success && response.data) {
        setParents(response.data);
      }
    } catch (error) {
      console.error('Failed to load parents:', error);
    }
  };

  const loadChildren = async () => {
    try {
      const response = await apiService.getChildren();
      if (response.success && response.data) {
        setChildren(response.data);
      }
    } catch (error) {
      console.error('Failed to load children:', error);
    }
  };

  const loadIntegrations = async () => {
    try {
      setLoadingIntegrations(true);

      // Get primary parent
      const parentsResponse = await apiService.getParents();
      if (parentsResponse.success && parentsResponse.data && parentsResponse.data.length > 0) {
        const primaryParent = parentsResponse.data.find((p: any) => p.is_primary) || parentsResponse.data[0];
        setPrimaryParentUuid(primaryParent.uuid);

        // Load Pitchero status
        const pitcheroStatus = await apiService.getPitcheroStatus(primaryParent.uuid);

        // Load SOCS status
        const socsStatus = await apiService.getSOCSStatus(primaryParent.uuid);

        const allIntegrations = [];

        // Always show Pitchero (whether connected or not)
        if (pitcheroStatus.success && pitcheroStatus.data) {
          allIntegrations.push({
            id: 'pitchero',
            name: 'Pitchero',
            connected: pitcheroStatus.data.connected,
            username: pitcheroStatus.data.username,
            lastSync: pitcheroStatus.data.last_sync,
            syncEnabled: pitcheroStatus.data.sync_enabled,
            matchedChildren: pitcheroStatus.data.matched_children
          });
        }

        // Always show SOCS (whether connected or not)
        if (socsStatus.success && socsStatus.data) {
          allIntegrations.push({
            id: 'socs',
            name: 'SOCS School Sports',
            connected: socsStatus.data.connected,
            username: socsStatus.data.username,
            lastSync: socsStatus.data.last_sync,
            syncEnabled: socsStatus.data.sync_enabled,
            matchedChildren: socsStatus.data.matched_children
          });
        }

        // Load SchoolsBuddy status
        const schoolsBuddyStatus = await apiService.getSchoolsBuddyStatus();

        // Always show SchoolsBuddy (whether connected or not)
        if (schoolsBuddyStatus.success && schoolsBuddyStatus.data) {
          allIntegrations.push({
            id: 'schoolsbuddy',
            name: 'SchoolsBuddy',
            connected: schoolsBuddyStatus.data.isConfigured,
            username: schoolsBuddyStatus.data.email,
            lastSync: schoolsBuddyStatus.data.lastSync,
            syncEnabled: true,
            matchedChildren: schoolsBuddyStatus.data.matchedChildren,
            bookingUrl: schoolsBuddyStatus.data.schoolUrl || null
          });

          // Store the child mappings for later use (e.g., refresh from term planner)
          if (schoolsBuddyStatus.data.matchedChildren && schoolsBuddyStatus.data.matchedChildren.length > 0) {
            const mappings = schoolsBuddyStatus.data.matchedChildren.map((m: any) => ({
              cluubzChildUuid: m.childUuid,
              schoolsBuddyChildId: m.schoolsBuddyChildId,
              schoolsBuddyChildName: m.schoolsBuddyChildName,
              schoolsBuddyName: m.schoolsBuddyChildName, // Alias for modal display
              confidenceScore: m.confidenceScore || 0.5 // Default if not provided
            }));
            setSchoolsBuddyMappings(mappings);
          }
        }

        // Load School Portal status
        const schoolPortalStatus = await apiService.getSchoolPortalStatus(primaryParent.uuid);

        // Always show School Portal (whether connected or not)
        if (schoolPortalStatus.success && schoolPortalStatus.data) {
          // Extract email from mappings if available
          const schoolPortalEmail = schoolPortalStatus.data.mappings?.[0]?.email || null;
          allIntegrations.push({
            id: 'schoolportal',
            name: 'My School Portal',
            connected: schoolPortalStatus.data.connected,
            username: schoolPortalEmail,
            lastSync: schoolPortalStatus.data.last_sync,
            syncEnabled: schoolPortalStatus.data.sync_enabled,
            matchedChildren: schoolPortalStatus.data.matched_children
          });
        } else {
          // Show as not connected if status call fails
          allIntegrations.push({
            id: 'schoolportal',
            name: 'My School Portal',
            connected: false,
            lastSync: null,
            syncEnabled: true,
            matchedChildren: []
          });
        }

        // Load Spond status
        const spondStatus = await apiService.getSpondStatus(primaryParent.uuid);
        if (spondStatus.success && spondStatus.data) {
          allIntegrations.push({
            id: 'spond',
            name: 'Spond',
            connected: spondStatus.data.connected,
            username: spondStatus.data.username,
            lastSync: spondStatus.data.last_sync,
            syncEnabled: spondStatus.data.sync_enabled,
            matchedChildren: spondStatus.data.matched_children
          });
        } else {
          allIntegrations.push({
            id: 'spond',
            name: 'Spond',
            connected: false,
            lastSync: null,
            syncEnabled: true,
            matchedChildren: 0
          });
        }

        setIntegrations(allIntegrations);

        // Collect emails from all integrations for suggestions
        const emails = new Set<string>();
        // Add user's signup email first (highest priority)
        if (user?.email) {
          emails.add(user.email);
        }
        // Add emails from integrations
        allIntegrations.forEach((integration: any) => {
          if (integration.username && integration.username.includes('@')) {
            emails.add(integration.username);
          }
        });
        // Also check school portal matched children for emails
        if (schoolPortalStatus.success && schoolPortalStatus.data?.matched_children) {
          schoolPortalStatus.data.matched_children.forEach((child: any) => {
            // School portal stores email in credentials, not in matched_children
          });
        }
        setIntegrationEmails(Array.from(emails));
      }
    } catch (error) {
      console.error('Failed to load integrations:', error);
    } finally {
      setLoadingIntegrations(false);
    }
  };

  const loadAllTermPlanners = async () => {
    try {
      setLoadingAllTermPlanners(true);
      const primaryParent = parents.find((p: any) => p.is_primary) || parents[0];

      if (!primaryParent || children.length === 0) {
        setAllTermPlanners({});
        return;
      }

      const plannersGrouped: Record<string, any[]> = {};

      for (const child of children) {
        try {
          const response = await apiService.getTermPlanners(primaryParent.uuid, child.uuid);
          if (response.success && response.data) {
            plannersGrouped[child.uuid] = response.data;
          } else {
            plannersGrouped[child.uuid] = [];
          }
        } catch (error) {
          console.error(`Failed to load term planners for child ${child.name}:`, error);
          plannersGrouped[child.uuid] = [];
        }
      }

      setAllTermPlanners(plannersGrouped);
    } catch (error) {
      console.error('Failed to load all term planners:', error);
    } finally {
      setLoadingAllTermPlanners(false);
    }
  };

  // Track if auto-search has been triggered for SchoolsBuddy

  const handleManageIntegration = (integration: any) => {
    setSelectedIntegration(integration);
    if (integration.id === 'pitchero') {
      // Use the PitcheroSetupModal with child exclusion support
      setShowPitcheroModal(true);
    } else if (integration.id === 'schoolsbuddy') {
      // Use the new SchoolsBuddySetupModal with multi-child credentials support
      setShowSchoolsBuddyModal(true);
    } else if (integration.id === 'schoolportal') {
      // Use the SchoolPortalSetupModal for full setup flow
      setShowSchoolPortalModal(true);
    } else {
      setShowCredentialsModal(true);
    }
  };

  const handleSavePitcheroCredentials = async () => {
    if (!primaryParentUuid || !selectedIntegration) return;

    if (!pitcheroUsername || !pitcheroPassword) {
      alert('Please enter both username and password');
      return;
    }

    try {
      setSavingCredentials(true);

      const response = await apiService.savePitcheroCredentials(primaryParentUuid, {
        username: pitcheroUsername,
        password: pitcheroPassword
      });

      if (response.success) {
        // Check if this is a new connection (Pitchero wasn't connected before)
        const pitcheroIntegration = integrations.find(i => i.id === 'pitchero');
        const isNewConnection = !pitcheroIntegration || !pitcheroIntegration.connected;

        if (isNewConnection) {
          // Auto-trigger match-only sync for new connections
          setSyncing(true);
          let syncSuccessful = false;

          // Trigger match-only sync (no activities yet)
          try {
            const syncResponse = await apiService.triggerPitcheroSync(primaryParentUuid, true);

            if (syncResponse.success) {
              // Load mappings to show to user
              const mappingsResponse = await apiService.getPitcheroMappings(primaryParentUuid);

              if (mappingsResponse.success && mappingsResponse.data && mappingsResponse.data.mappings.length > 0) {
                // Load all mappings for review
                setPitcheroMappings(mappingsResponse.data.mappings || []);
                setCluubzChildren(mappingsResponse.data.children || []);

                // Pre-populate child matches from existing mappings
                const initialMatches: { [key: string]: string } = {};
                mappingsResponse.data.mappings.forEach((mapping: any) => {
                  initialMatches[mapping.id] = mapping.mappedChildUuid;
                });
                setChildMatches(initialMatches);

                // Show review modal directly
                setIsReviewMode(true);
                setShowManualMatching(true);
                setSelectedIntegration({ id: 'pitchero', name: 'Pitchero' });
                syncSuccessful = true;
              } else {
                // No children found - re-open credentials modal for correction
                alert('❌ No child matches found!\n\nNo clubs or children were found in your Pitchero account. Please check your credentials and try again.');
                // Don't close modal, keep credentials populated for user to correct
                return; // Exit early, don't close modal
              }
            } else {
              alert(`❌ Sync failed!\n\n${syncResponse.error || 'Unknown error'}\n\nPlease check your credentials and try again.`);
              // Don't close modal, keep credentials populated for user to correct
              return; // Exit early, don't close modal
            }
          } catch (syncError) {
            console.error('Auto-sync failed:', syncError);
            alert('❌ Failed to sync!\n\nAn error occurred during sync. Please check your credentials and try again.');
            // Don't close modal, keep credentials populated for user to correct
            return; // Exit early, don't close modal
          } finally {
            setSyncing(false);
          }
        } else {
          alert('✅ Credentials updated successfully!');
        }

        // Only close modal and reset state if sync was successful (or if updating existing connection)
        setShowCredentialsModal(false);
        setPitcheroUsername('');
        setPitcheroPassword('');
        setSelectedIntegration(null);
        await loadIntegrations();
      } else {
        alert(`Failed to save credentials: ${response.error}`);
      }
    } catch (error) {
      console.error('Failed to save credentials:', error);
      alert('Failed to save credentials');
    } finally {
      setSavingCredentials(false);
    }
  };

  // Handler when user clicks "Book on Integration" for a specific date
  const handleBookedOnIntegration = (dateStr: string) => {
    setIntegrationBookedDates(prev => {
      if (!prev.includes(dateStr)) {
        return [...prev, dateStr];
      }
      return prev;
    });
  };

  // Handler to refresh SchoolsBuddy data from term planner (re-import and reload)
  const handleRefreshSchoolsBuddyFromTermPlanner = async () => {
    if (!primaryParentUuid || !termPlannerChild?.uuid || !editingTermPlannerUuid) {
      alert('❌ Cannot refresh: Missing term planner information');
      return;
    }

    try {
      setSyncingSchoolsBuddy(true);

      // Find SchoolsBuddy child ID from mappings
      const mapping = schoolsBuddyMappings.find(
        (m: any) => m.cluubzChildUuid === termPlannerChild.uuid
      );
      const schoolsBuddyChildId = mapping?.schoolsBuddyChildId;

      // Use the import endpoint to re-fetch SchoolsBuddy activities
      const importResponse = await apiService.importSchoolsBuddyActivities(
        termPlannerChild.uuid,
        editingTermPlannerUuid,
        schoolsBuddyChildId
      );

      if (importResponse.success) {
        // Reload the term planner data to get updated notes
        const termPlannerResponse = await apiService.getTermPlanner(primaryParentUuid, editingTermPlannerUuid);
        if (termPlannerResponse.success && termPlannerResponse.data) {
          // Update daily schedules with new notes from refreshed data
          const updatedSchedules: any = { ...dailySchedules };
          termPlannerResponse.data.daily_schedules?.forEach((schedule: any) => {
            const day = schedule.day_of_week;
            if (updatedSchedules[day]) {
              // Update drop_off notes
              if (updatedSchedules[day].drop_off) {
                updatedSchedules[day].drop_off.notes = schedule.drop_off_notes || '';
              }
              // Update pick_up notes
              if (updatedSchedules[day].pick_up) {
                updatedSchedules[day].pick_up.notes = schedule.pick_up_notes || '';
              }
              // Update club_reminders
              if (schedule.club_reminders) {
                updatedSchedules[day].club_reminders = schedule.club_reminders;
              }
            }
          });
          setDailySchedules(updatedSchedules);

          // Clear the booked dates since we've now synced
          setIntegrationBookedDates([]);
        }

        const message = importResponse.data?.message || 'SchoolsBuddy data refreshed!';
        alert(`✅ ${message}`);
      } else {
        alert(`❌ Failed to refresh: ${importResponse.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('SchoolsBuddy refresh error:', error);
      alert(`Error refreshing: ${error.message || 'Failed to refresh'}`);
    } finally {
      setSyncingSchoolsBuddy(false);
    }
  };

  const handleSync = async (integration: any) => {
    if (!primaryParentUuid) return;

    try {
      setSyncing(true);

      if (integration.id === 'pitchero') {
        // Step 1: Trigger match-only sync (no activities yet)
        const response = await apiService.triggerPitcheroSync(primaryParentUuid, true);

        if (response.success) {
          // Step 2: Load mappings to show to user
          const mappingsResponse = await apiService.getPitcheroMappings(primaryParentUuid);

          if (mappingsResponse.success && mappingsResponse.data && mappingsResponse.data.mappings.length > 0) {
            // Load all mappings for review
            const mappings = mappingsResponse.data.mappings || [];
            setPitcheroMappings(mappings);
            setCluubzChildren(mappingsResponse.data.children || []);

            // Pre-populate child matches from existing mappings
            const initialMatches: { [key: string]: string } = {};
            mappings.forEach((mapping: any) => {
              initialMatches[mapping.id] = mapping.mappedChildUuid;
            });
            setChildMatches(initialMatches);

            // Initialize all clubs as selected by default
            const initialClubSelection: { [key: string]: boolean } = {};
            mappings.forEach((mapping: any) => {
              if (mapping.clubs) {
                mapping.clubs.forEach((club: string) => {
                  initialClubSelection[`${mapping.id}-${club}`] = true;
                });
              }
            });
            setReviewSelectedClubs(initialClubSelection);
            setReviewExcludedChildren({});

            // Show review modal directly
            setIsReviewMode(true);
            setShowManualMatching(true);
            setSelectedIntegration(integration);
          } else {
            // No children found - open credentials modal for user to correct
            alert('❌ No child matches found!\n\nNo clubs or children were found in your Pitchero account. Please check your credentials and try again.');
            // Re-open credentials modal for user to update credentials
            setSelectedIntegration(integration);
            setShowCredentialsModal(true);
          }
        } else {
          alert(`❌ Failed to sync!\n\n${response.error || 'Unknown error'}\n\nPlease check your credentials and try again.`);
          // Re-open credentials modal for user to correct
          setSelectedIntegration(integration);
          setShowCredentialsModal(true);
        }
      } else if (integration.id === 'schoolportal') {
        // Trigger School Portal forced sync (bypass 24-hour check)
        const response = await apiService.schoolPortalAutoSync(true);

        if (response.success && response.data) {
          const { syncPerformed, totalNewActivities, message } = response.data;
          if (syncPerformed) {
            alert(`✅ School Portal sync completed!\n\n${message || `${totalNewActivities || 0} new activities found.`}`);
          } else {
            alert(`ℹ️ ${message || 'No new activities found.'}`);
          }
          await loadIntegrations();
        } else {
          alert(`❌ Failed to sync School Portal!\n\n${response.error || 'Unknown error'}`);
        }
      } else if (integration.id === 'spond') {
        // Trigger Spond sync
        const response = await apiService.spondSync();

        if (response.success) {
          alert(`✅ Spond sync completed!\n\n${response.data?.message || 'Activities synced.'}`);
          await loadIntegrations();
        } else {
          alert(`❌ Failed to sync Spond!\n\n${response.error || 'Unknown error'}`);
        }
      } else if (integration.id === 'schoolsbuddy') {
        // Trigger SchoolsBuddy sync
        const response = await apiService.syncSchoolsBuddyActivities();

        if (response.success) {
          alert(`✅ SchoolsBuddy sync completed successfully!\n\n${response.data?.message || 'Activities synced.'}`);
          await loadIntegrations();
        } else {
          alert(`❌ Failed to sync SchoolsBuddy!\n\n${response.error || 'Unknown error'}\n\nPlease check your credentials and try again.`);
          setSelectedIntegration(integration);
          setShowCredentialsModal(true);
        }
      }
    } catch (error) {
      console.error('Failed to trigger sync:', error);
      alert('❌ Failed to sync!\n\nAn error occurred during sync. Please check your credentials and try again.');
      // Re-open credentials modal for user to correct
      setSelectedIntegration(integration);
      setShowCredentialsModal(true);
    } finally {
      setSyncing(false);
    }
  };

  const handleSOCSSync = async () => {
    try {
      setSyncingSOCS(true);
      console.log('🔄 Starting SOCS fixture sync...');

      const response = await apiService.socsSyncAllFixtures();

      if (response.success && response.data) {
        // Use the user-friendly message from backend
        const message = response.data.message || 'Sync completed successfully';
        alert(`✅ ${message}`);
      } else {
        alert(`❌ Sync failed: ${response.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('SOCS sync error:', error);
      alert(`❌ Failed to sync SOCS fixtures: ${error.message}`);
    } finally {
      setSyncingSOCS(false);
    }
  };

  const handleDisconnectIntegration = async (integration: any) => {
    if (!window.confirm(`Are you sure you want to disconnect ${integration.name}? Your imported activities will remain, but automatic syncing will stop.`)) {
      return;
    }

    try {
      setSavingCredentials(true);

      if (integration.id === 'pitchero') {
        if (!primaryParentUuid) {
          alert('Cannot disconnect Pitchero - parent account not found');
          return;
        }
        const response = await apiService.updatePitcheroSettings(primaryParentUuid, {
          disconnect: true
        });

        if (response.success) {
          alert(`${integration.name} disconnected successfully`);
          await loadIntegrations();
        } else {
          alert(`Failed to disconnect: ${response.error}`);
        }
      } else if (integration.id === 'spond') {
        const response = await apiService.spondDisconnect();
        if (response.success) {
          alert(`${integration.name} disconnected successfully`);
          await loadIntegrations();
        } else {
          alert(`Failed to disconnect: ${response.error}`);
        }
      } else if (integration.id === 'schoolsbuddy') {
        const response = await apiService.disconnectSchoolsBuddy();
        if (response.success) {
          alert(`${integration.name} disconnected successfully`);
          await loadIntegrations();
        } else {
          alert(`Failed to disconnect: ${response.error}`);
        }
      } else if (integration.id === 'schoolportal') {
        // Disconnect all children's school portal credentials
        let allSuccess = true;
        let errorMsg = '';

        // Get children from matchedChildren or fall back to all children
        const childrenToDisconnect = integration.matchedChildren?.length > 0
          ? integration.matchedChildren
          : children;

        for (const child of childrenToDisconnect) {
          const childUuid = child.childUuid || child.uuid;
          if (childUuid) {
            const response = await apiService.disconnectSchoolPortal(childUuid);
            if (!response.success) {
              allSuccess = false;
              errorMsg = response.error || 'Unknown error';
            }
          }
        }

        if (allSuccess) {
          alert(`${integration.name} disconnected successfully`);
          await loadIntegrations();
        } else {
          alert(`Failed to disconnect: ${errorMsg}`);
        }
      }
    } catch (error) {
      console.error('Failed to disconnect:', error);
      alert('Failed to disconnect integration');
    } finally {
      setSavingCredentials(false);
    }
  };

  const handleSaveManualMatches = async () => {
    if (!primaryParentUuid) return;

    try {
      setSavingCredentials(true);

      // Filter out "skip" selections and include pitchero child names
      const matches = Object.entries(childMatches)
        .filter(([_, childUuid]) => childUuid !== 'skip')
        .map(([pitcheroMemberId, childUuid]) => {
          const pitcheroChild = unmatchedChildren.find((child: any) => child.member_id === pitcheroMemberId);
          return {
            pitchero_member_id: pitcheroMemberId,
            child_uuid: childUuid,
            pitchero_child_name: pitcheroChild?.pitcheroName || 'Unknown'
          };
        });

      if (matches.length === 0) {
        setShowManualMatching(false);
        setChildMatches({});
        alert('No children matched. You can match them later by syncing again.');
        await loadIntegrations();
        return;
      }

      const response = await apiService.savePitcheroManualMatches(primaryParentUuid, matches);

      if (response.success) {
        setShowManualMatching(false);
        setChildMatches({});
        alert(`✅ Successfully matched ${matches.length} ${matches.length === 1 ? 'child' : 'children'}!`);
        await loadIntegrations();
      } else {
        alert(`Failed to save matches: ${response.error}`);
      }
    } catch (error) {
      console.error('Failed to save manual matches:', error);
      alert('Failed to save manual matches');
    } finally {
      setSavingCredentials(false);
    }
  };

  const handleEditProfile = async () => {
    try {
      setLoading(true);

      // Fetch latest profile data from backend
      const response = await apiService.getProfile();
      if (response.success && response.data) {
        const profileData = response.data;

        // Update form fields with latest data
        setUsername(profileData.username || '');
        setEmail(profileData.email || '');
        setPhone(profileData.phone || '');
        setAddressLine1(profileData.address_line_1 || '');
        setTownCity(profileData.town_city || '');
        setStateProvinceCountry(profileData.state_province_country || '');
        setPostCode(profileData.post_code || '');
        
        // Also refresh the user context with latest data
        await refreshUser();
      } else {
        console.error('Failed to load profile data:', response.error);
        alert('Failed to load latest profile data');
      }
      
      setEditing(true);
    } catch (error) {
      console.error('Error loading profile data:', error);
      alert('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddParent = () => {
    setShowAddParentModal(true);
  };

  const handleCreateParent = async () => {
    if (!newParentFirstName.trim() || !newParentLastName.trim() || !newParentEmail.trim() || !newParentPhone.trim() || !newParentPassword.trim()) {
      alert('Please fill in all fields');
      return;
    }

    // Validate password match
    if (newParentPassword !== newParentConfirmPassword) {
      alert('Passwords do not match');
      return;
    }

    // Validate password length
    if (newParentPassword.length < 6) {
      alert('Password must be at least 6 characters long');
      return;
    }

    // Validate phone number
    const phoneValidation = validatePhoneNumber(newParentPhone);
    if (!phoneValidation.isValid) {
      alert(`Invalid phone number: ${phoneValidation.error}`);
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newParentEmail)) {
      alert('Please enter a valid email address');
      return;
    }

    setAddingParent(true);
    try {
      const response = await apiService.createParent({
        username: `${newParentFirstName.trim()} ${newParentLastName.trim()}`,
        email: newParentEmail.trim(),
        phone: newParentPhone.trim(),
        password: newParentPassword,
        role: newParentRole
      });

      if (response.success) {
        const parentName = `${newParentFirstName.trim()} ${newParentLastName.trim()}`;
        
        // Clear form and close modal
        setNewParentFirstName('');
        setNewParentLastName('');
        setNewParentEmail('');
        setNewParentPhone('');
        setNewParentPassword('');
        setNewParentConfirmPassword('');
        setNewParentRole('parent');
        setShowAddParentModal(false);
        
        // Reload parents list
        loadParents();

        // Show address modal for new parent first
        setNewParentDetails({
          email: newParentEmail.trim(),
          name: parentName
        });
        setShowNewParentAddressModal(true);

        // Note: parentAdded event will be emitted after address modal is closed
      } else {
        alert(`Failed to add parent: ${response.error || 'Unknown error'}`);
      }
    } catch (error) {
      alert('Failed to add parent');
      console.error('Add parent error:', error);
    } finally {
      setAddingParent(false);
    }
  };

  const handleDeleteParent = async (parentUuid: string, parentName: string) => {
    if (window.confirm(`Are you sure you want to remove ${parentName} from this account? They will no longer have access to manage children and activities.`)) {
      try {
        const response = await apiService.deleteParent(parentUuid);
        if (response.success) {
          alert('Parent removed successfully');
          loadParents(); // Reload the parents list
        } else {
          alert(`Failed to remove parent: ${response.error || 'Unknown error'}`);
        }
      } catch (error) {
        alert('Failed to remove parent');
        console.error('Delete parent error:', error);
      }
    }
  };

  const handleSaveProfile = async () => {
    if (!username.trim() || !email.trim() || !phone.trim()) {
      alert('Please fill in all fields');
      return;
    }

    // Validate phone number
    const phoneValidation = validatePhoneNumber(phone);
    if (!phoneValidation.isValid) {
      alert(`Invalid phone number: ${phoneValidation.error}`);
      return;
    }

    setLoading(true);
    try {
      const response = await apiService.updateProfile({
        username: username.trim(),
        email: email.trim(),
        phone: phone.trim(),
        address_line_1: addressLine1.trim(),
        town_city: townCity.trim(),
        state_province_country: stateProvinceCountry.trim(),
        post_code: postCode.trim()
      });

      if (response.success && response.data) {
        // Refresh user data in context
        await refreshUser();
        setEditing(false);
        alert('Profile updated successfully');
      } else {
        alert(`Failed to update profile: ${response.error || 'Unknown error'}`);
      }
    } catch (error) {
      alert('Failed to update profile');
      console.error('Update profile error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    if (user) {
      setUsername(user.username);
      setEmail(user.email);
      setPhone(user.phone);
      setAddressLine1(user.address_line_1 || '');
      setTownCity(user.town_city || '');
      setStateProvinceCountry(user.state_province_country || '');
      setPostCode(user.post_code || '');
    }
    setEditing(false);
  };

  const handleChangePassword = () => {
    setShowPasswordModal(true);
  };

  const handlePasswordSubmit = async () => {
    if (!currentPassword.trim()) {
      alert('Please enter your current password');
      return;
    }

    if (!newPassword.trim()) {
      alert('Please enter a new password');
      return;
    }

    if (newPassword.length < 6) {
      alert('New password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      alert('New passwords do not match');
      return;
    }

    setPasswordLoading(true);
    try {
      const response = await apiService.changePassword({
        currentPassword: currentPassword.trim(),
        newPassword: newPassword.trim()
      });

      if (response.success) {
        alert('Password changed successfully');
        setShowPasswordModal(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        alert(`Failed to change password: ${response.error || 'Unknown error'}`);
      }
    } catch (error) {
      alert('Failed to change password');
      console.error('Change password error:', error);
    } finally {
      setPasswordLoading(false);
    }
  };

  const handlePasswordCancel = () => {
    setShowPasswordModal(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
    }
  };

  const loadAlarmDebugInfo = async () => {
    setAlarmDebugLoading(true);
    try {
      const localNotifService = LocalNotificationService.getInstance();
      const platform = Capacitor.getPlatform();
      const isNative = Capacitor.isNativePlatform();

      let iosVersion = 'N/A';
      let alarmKitAvailable = false;
      let alarmKitGranted = false;
      let alarmKitStatus = 'N/A';

      if (isNative && platform === 'ios') {
        try {
          const avail = await AlarmKit.isAvailable();
          alarmKitAvailable = avail.available;
          iosVersion = (avail as any).iosVersion || 'unknown';
        } catch (e: any) {
          iosVersion = 'error: ' + (e?.message || e);
        }

        if (alarmKitAvailable) {
          try {
            const perm = await AlarmKit.checkPermission();
            alarmKitGranted = perm.granted;
            alarmKitStatus = perm.status;
          } catch (e: any) {
            alarmKitStatus = 'error: ' + (e?.message || e);
          }
        }
      }

      const storedReminders = await localNotifService.getAllReminders();
      let pendingLocalNotifs = 0;
      if (isNative) {
        const pending = await localNotifService.getPendingNotifications();
        pendingLocalNotifs = pending.length;
      }

      setAlarmDebugInfo({
        platform,
        isNative,
        iosVersion,
        alarmKitAvailable,
        alarmKitGranted,
        alarmKitStatus,
        storedReminders,
        pendingLocalNotifs,
      });
    } catch (e) {
      alert('Error loading debug info: ' + e);
    } finally {
      setAlarmDebugLoading(false);
    }
  };

  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deleteAccountPassword, setDeleteAccountPassword] = useState('');
  const [deleteAccountLoading, setDeleteAccountLoading] = useState(false);
  const handleDeleteAccount = async () => {
    if (!deleteAccountPassword) {
      alert('Please enter your password to confirm');
      return;
    }

    setDeleteAccountLoading(true);
    try {
      const response = await apiService.deleteAccount(deleteAccountPassword);
      if (response.success) {
        alert('Your account has been deleted. You will now be logged out.');
        logout();
      } else {
        alert(response.error || 'Failed to delete account');
      }
    } catch (error) {
      alert('Failed to delete account. Please try again.');
      console.error('Delete account error:', error);
    } finally {
      setDeleteAccountLoading(false);
    }
  };

  const handleNewParentAddressModalClose = () => {
    setShowNewParentAddressModal(false);
    setNewParentDetails(null);
    // Reload parents list even if address wasn't saved
    loadParents();
    alert('Parent added successfully! They can update their address when they first log in.');

    // Emit event for onboarding flow after address modal interaction
    window.dispatchEvent(new CustomEvent('parentAdded'));
  };

  const handleNewParentAddressModalSaved = () => {
    setShowNewParentAddressModal(false);
    const parentName = newParentDetails?.name || 'Parent';
    setNewParentDetails(null);
    // Reload parents list
    loadParents();
    alert(`${parentName} added successfully! Their address has been saved to their profile.`);

    // Set flag in localStorage to indicate parent was just added
    localStorage.setItem('parentJustAdded', 'true');

    // Emit event for onboarding flow after address modal interaction
    window.dispatchEvent(new CustomEvent('parentAdded'));
  };

  const handleEditChild = (child: Child) => {
    setEditingChild(child);

    const displayName = child.display_name || child.name || '';
    const nameParts = displayName.split(' ');

    const firstName = child.first_name || nameParts[0] || '';
    const lastName = child.last_name || nameParts.slice(1).join(' ') || '';

    // Parse interests string into array
    const interestsStr = child.interests || '';
    const interestsArray = interestsStr ? interestsStr.split(',').map(i => i.trim()).filter(i => i) : [];

    let birthDate = '';
    let birthDay = '';
    let birthMonth = '';
    let birthYear = '';
    if (child.age) {
      const currentYear = new Date().getFullYear();
      birthYear = String(currentYear - child.age);
      birthDate = `${birthYear}-01-01`;
      birthDay = '1';
      birthMonth = '1';
    }

    setEditChildFirstName(firstName);
    setEditChildLastName(lastName);
    setEditChildBirthDate(birthDate);
    setEditChildBirthDay(birthDay);
    setEditChildBirthMonth(birthMonth);
    setEditChildBirthYear(birthYear);
    setEditChildInterests(interestsArray);
    setEditChildSchoolVenue(child.school_venue || '');
    setEditChildSchoolCity(child.school_city || '');

    setTimeout(() => {
      setShowEditChildModal(true);
    }, 10);
  };

  const handleUpdateChild = async () => {
    if (!editingChild) return;

    if (!editChildFirstName.trim()) {
      alert('Please enter the child\'s first name');
      return;
    }

    if (!editChildSchoolVenue.trim()) {
      alert('Please enter the child\'s school name. This is required for integration features.');
      return;
    }

    setUpdatingChild(true);
    try {
      let age = undefined;
      // Construct birth date from day/month/year dropdowns
      if (editChildBirthDay && editChildBirthMonth && editChildBirthYear) {
        const birthDate = new Date(
          parseInt(editChildBirthYear),
          parseInt(editChildBirthMonth) - 1,
          parseInt(editChildBirthDay)
        );
        const today = new Date();
        age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
      }

      const updateData = {
        first_name: editChildFirstName.trim(),
        last_name: editChildLastName.trim(),
        age: age,
        interests: editChildInterests.length > 0 ? editChildInterests.join(', ') : undefined,
        school_venue: editChildSchoolVenue.trim() || undefined,
        school_city: editChildSchoolCity.trim() || undefined
      };

      const response = await apiService.updateChild(editingChild.uuid, updateData);

      if (response.success) {
        setShowEditChildModal(false);
        setEditingChild(null);

        // Clear URL parameter to prevent modal from reopening
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('editChild')) {
          window.history.replaceState({}, '', '/profile');
        }

        await loadChildren();

        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('childrenDataChanged'));
          window.dispatchEvent(new CustomEvent('childUpdated'));
        }

        alert('Child updated successfully');
      } else {
        alert(`Error: ${response.error || 'Failed to update child'}`);
      }
    } catch (error) {
      alert('Failed to update child');
      console.error('Update child error:', error);
    } finally {
      setUpdatingChild(false);
    }
  };

  const handleDeleteChild = async (child: Child) => {
    if (window.confirm(`Are you sure you want to delete ${child.display_name || child.name}? This will also delete all their activities.`)) {
      try {
        const response = await apiService.deleteChild(child.uuid);
        if (response.success) {
          loadChildren();
          alert('Child deleted successfully');
        } else {
          alert(`Error: ${response.error || 'Failed to delete child'}`);
        }
      } catch (error) {
        alert('Failed to delete child');
        console.error('Delete child error:', error);
      }
    }
  };

  const handleTermPlanner = async (child: Child) => {
    setTermPlannerChild(child);
    setTermPlannerStep('list');
    setLoadingTermPlanners(true);
    setShowTermPlannerModal(true);

    // Load existing term planners for this child
    try {
      const primaryParent = parents.find((p: any) => p.is_primary) || parents[0];
      if (primaryParent) {
        const response = await apiService.getTermPlanners(primaryParent.uuid, child.uuid);
        if (response.success && response.data) {
          setExistingTermPlanners(response.data);
        }
      }
    } catch (error) {
      console.error('Failed to load term planners:', error);
    } finally {
      setLoadingTermPlanners(false);
    }
  };

  const handleCreateNewTerm = () => {
    const defaults = getDefaultPlannerDates();
    setTermName(defaults.termName);
    setTermStartDate(defaults.startDate);
    setTermEndDate(defaults.endDate);
    setCurrentDayTab(1); // Default to Monday
    setEditingTermPlannerUuid(null);
    setDailySchedules({
      0: { drop_off: {}, club_reminders: [], pick_up: {} },
      1: { drop_off: {}, club_reminders: [], pick_up: {} },
      2: { drop_off: {}, club_reminders: [], pick_up: {} },
      3: { drop_off: {}, club_reminders: [], pick_up: {} },
      4: { drop_off: {}, club_reminders: [], pick_up: {} },
      5: { drop_off: {}, club_reminders: [], pick_up: {} },
      6: { drop_off: {}, club_reminders: [], pick_up: {} }
    });
    setTermPlannerStep('create');
  };

  // Poll until every integration this child is set up for has its clubs cached.
  // Returns false if they haven't landed inside the timeout, so the caller can
  // hold the user rather than opening a planner the import hasn't reached yet.
  const waitForClubDataReady = async (childUuid: string, maxWaitMs = 60000): Promise<boolean> => {
    const pollIntervalMs = 1500;
    const startedAt = Date.now();
    const label = (source: string) =>
      source === 'schoolsbuddy' ? 'SchoolsBuddy' : source === 'school_portal' ? 'School Portal' : source;

    while (Date.now() - startedAt < maxWaitMs) {
      try {
        const status = await apiService.getChildClubDataStatus(childUuid);
        // A backend that can't answer must not block the planner — only a
        // definite "still pending" keeps the user waiting.
        if (!status.success || !status.data) return true;
        if (status.data.ready) return true;
        setLoadingTermPlannerMessage({
          title: 'Import still in progress...',
          subtitle: `Fetching your ${status.data.pending.map(label).join(' and ')} clubs`
        });
      } catch {
        return true; // transient network error — don't strand the user
      }
      await new Promise(r => setTimeout(r, pollIntervalMs));
    }
    return false;
  };

  const handleSetupTermPlanner = () => {
    const defaults = getDefaultPlannerDates();
    setTermName(defaults.termName);
    setTermStartDate(defaults.startDate);
    setTermEndDate(defaults.endDate);
    setSchoolStartTime('08:45');
    setSchoolEndTime('15:30');
    setEditingTermPlannerUuid(null);
    setTermPlannerStep('setup');
  };

  // Proceed from setup to create with auto-populated schedules
  const handleProceedToSchedule = async () => {
    if (!termStartDate || !termPlannerChild) {
      alert('Please choose when this planner starts');
      return;
    }
    // Replacing a live planner is destructive from the changeover date onward,
    // so confirm before the pre-create call actually supersedes anything.
    if (outgoingPlanner) {
      // This prompt exists BECAUSE another planner is already running, so it has to
      // name it — without that the parent cannot tell what they are about to
      // replace, and the choice is not a real one. The date is stated once as the
      // changeover, not repeated for every consequence.
      const childName = termPlannerChild?.name
        || `${termPlannerChild?.first_name || ''}`.trim()
        || 'your child';
      const confirmed = window.confirm(
        `${childName} already has a planner running:\n\n` +
        `    ${outgoingPlanner.term_name}\n\n` +
        `Your new planner starts ${formatDateUK(termStartDate)} and replaces it from that day. ` +
        `Activities before then stay as they are.`
      );
      if (!confirmed) return;
    }
    if (!termName) setTermName(plannerNameFromDates(termStartDate, ''));

    // Auto-populate all weekdays (Mon-Fri) with default school times
    const newSchedules: any = {
      0: { drop_off: { manualOneOffs: [] }, club_reminders: [], pick_up: { manualOneOffs: [] } }, // Sunday - no school
      1: { drop_off: { time: schoolStartTime, manualOneOffs: [] }, club_reminders: [], pick_up: { time: schoolEndTime, manualOneOffs: [] } }, // Monday
      2: { drop_off: { time: schoolStartTime, manualOneOffs: [] }, club_reminders: [], pick_up: { time: schoolEndTime, manualOneOffs: [] } }, // Tuesday
      3: { drop_off: { time: schoolStartTime, manualOneOffs: [] }, club_reminders: [], pick_up: { time: schoolEndTime, manualOneOffs: [] } }, // Wednesday
      4: { drop_off: { time: schoolStartTime, manualOneOffs: [] }, club_reminders: [], pick_up: { time: schoolEndTime, manualOneOffs: [] } }, // Thursday
      5: { drop_off: { time: schoolStartTime, manualOneOffs: [] }, club_reminders: [], pick_up: { time: schoolEndTime, manualOneOffs: [] } }, // Friday
      6: { drop_off: { manualOneOffs: [] }, club_reminders: [], pick_up: { manualOneOffs: [] } }  // Saturday - no school
    };
    setDailySchedules(newSchedules);
    setCurrentDayTab(1); // Start on Monday

    // Uniform flow for every child: pre-create the term planner so the backend
    // placer merges any cached integration data (SchoolsBuddy, School Portal,
    // future sources) from club_data_cache into the new schedules before the
    // editor opens. No branching per integration — the backend reads every
    // source for the child and places sessions idempotently.
    setLoadingTermPlannerMessage({
      title: 'Generating your term planner...',
      subtitle: 'Pulling in any saved club data'
    });
    setLoadingTermPlannerTransition(true);

    // Hard gate: the planner is only worth pre-creating once every integration
    // the child is set up for has actually landed its clubs in the cache. If we
    // create it while an import is still running, the placer finds nothing and
    // the editor opens empty — which is exactly how a SchoolsBuddy term planner
    // came up blank while the School Portal one beside it was fine.
    const clubDataReady = await waitForClubDataReady(termPlannerChild.uuid);
    if (!clubDataReady) {
      setLoadingTermPlannerTransition(false);
      setLoadingTermPlannerMessage(null);
      alert(
        'Your club import is still running.\n\n' +
        'Give it another moment and open the term planner again — your clubs ' +
        'will be pulled in automatically once the import finishes.'
      );
      return;
    }

    try {
      const primaryParent = parents.find((p: any) => p.is_primary) || parents[0];
      if (!primaryParent) throw new Error('No primary parent found');

      const createResponse = await apiService.createTermPlanner(primaryParent.uuid, {
        child_uuid: termPlannerChild.uuid,
        term_name: termName,
        start_date: termStartDate,
        end_date: null,   // open-ended — closed off only when superseded
        school_start_time: schoolStartTime,
        school_end_time: schoolEndTime,
        daily_schedules: newSchedules
      });

      if (createResponse.success && createResponse.data?.uuid) {
        setEditingTermPlannerUuid(createResponse.data.uuid);
        await handleEditTermPlanner(createResponse.data.uuid);
        await loadAllTermPlanners();
      }
    } catch (error) {
      console.warn('Term planner pre-create failed, falling back to empty editor:', error);
    }

    setLoadingTermPlannerTransition(false);
    setLoadingTermPlannerMessage(null);
    setTermPlannerStep('create');
  };

  const handleSkipTermPlanner = () => {
    setShowTermPlannerModal(false);
  };

  const handleSaveTermPlanner = async () => {
    // end_date is optional now: a new planner is open-ended. The edit flow still
    // carries whatever end date the planner already had.
    if (!termPlannerChild || !termStartDate) {
      alert('Please fill in all required fields');
      return;
    }
    const effectiveTermName = termName || plannerNameFromDates(termStartDate, termEndDate);

    const primaryParent = parents.find((p: any) => p.is_primary) || parents[0];
    if (!primaryParent) {
      alert('No primary parent found');
      return;
    }

    // If editing, show warning and get confirmation
    if (editingTermPlannerUuid) {
      // "delete all activities" was both alarming and untrue — past activities are
      // preserved, and only this planner's own entries are rebuilt.
      const confirmed = window.confirm(
        `Save changes to this planner?\n\n` +
        `Upcoming school and club entries will be rebuilt from the schedule you've set.\n\n` +
        `Activities that have already happened stay as they are, and nothing outside this planner is touched.`
      );
      if (!confirmed) {
        return;
      }
    }

    setCreatingTermPlanner(true);
    try {
      const termData = {
        child_uuid: termPlannerChild.uuid,
        term_name: effectiveTermName,
        start_date: termStartDate,
        end_date: termEndDate || null,   // null = open-ended
        school_start_time: schoolStartTime,
        school_end_time: schoolEndTime,
        daily_schedules: dailySchedules
      };

      let response;
      if (editingTermPlannerUuid) {
        // Update existing term planner
        response = await apiService.updateTermPlanner(primaryParent.uuid, editingTermPlannerUuid, termData);
      } else {
        // Create new term planner
        response = await apiService.createTermPlanner(primaryParent.uuid, termData);
      }

      if (response.success) {
        // The backend's TP-CREATE-BG / TP-UPDATE-BG path refreshes integration
        // caches (SchoolsBuddy + School Portal) then regenerates activities,
        // and stamps bg_sync_completed_at when done. Keep the spinner up and
        // poll that flag before showing the planner — otherwise the user sees
        // a stale view that hasn't picked up the SB clubs yet.
        const savedTermPlannerUuid: string | undefined = response.data?.uuid || editingTermPlannerUuid || undefined;
        if (savedTermPlannerUuid) {
          const maxWaitMs = 45000; // School Portal Playwright can be slow.
          const pollIntervalMs = 1500;
          const startedAt = Date.now();
          while (Date.now() - startedAt < maxWaitMs) {
            try {
              const s = await apiService.getTermPlannerBgStatus(savedTermPlannerUuid);
              if (s.success && s.data?.completed) break;
            } catch { /* keep polling — transient errors shouldn't break UX */ }
            await new Promise(r => setTimeout(r, pollIntervalMs));
          }
        }

        setCreatingTermPlanner(false);

        // Reload all term planners for the section view (background)
        loadAllTermPlanners().catch(err => console.warn('loadAllTermPlanners failed:', err));

        // Reset alarm sync flag so HomeScreen re-checks for AlarmKit permission banner
        resetAlarmSyncFlag();

        // Schedule local alarms for generated activities (background — activities
        // may still be generating on the backend; HomeScreen alarm sync will pick
        // up any that arrive after this runs).
        let alarmsScheduled = 0;
        const scheduleAlarmsInBackground = async () => {
          try {
            const localNotifService = LocalNotificationService.getInstance();

            // When editing, backend deletes old activities (new UUIDs created).
            // Purge all stored reminders so stale alarms don't occupy AlarmKit slots.
            if (editingTermPlannerUuid) {
              await localNotifService.purgeAllAlarms();
            }

            const now = new Date();
            const farFuture = new Date(now);
            farFuture.setFullYear(farFuture.getFullYear() + 1);
            const calResponse = await apiService.getCalendarActivities(
              now.toISOString().split('T')[0],
              farFuture.toISOString().split('T')[0]
            );
            if (calResponse.success && calResponse.data) {
              const upcoming = Array.isArray(calResponse.data) ? calResponse.data : [];
              // Sort by date ascending so nearest events get AlarmKit priority (limited slots)
              upcoming.sort((a, b) => {
                const dateA = new Date(`${(a.start_date || '').split('T')[0]}T${a.start_time || '00:00'}`);
                const dateB = new Date(`${(b.start_date || '').split('T')[0]}T${b.start_time || '00:00'}`);
                return dateA.getTime() - dateB.getTime();
              });
              for (const a of upcoming) {
                if (a.reminder_minutes != null && a.reminder_minutes >= 0) {
                  const actUuid = a.activity_uuid || a.uuid;
                  const existing = await localNotifService.getReminder(actUuid);
                  // Skip only if already scheduled with same minutes AND using AlarmKit
                  if (existing && existing.minutesBefore === a.reminder_minutes && existing.useAlarmKit) continue;
                  if (existing) {
                    await localNotifService.cancelReminder(actUuid);
                  }
                  await localNotifService.scheduleReminder({
                    activityUuid: actUuid,
                    activityName: a.name,
                    startDate: a.start_date,
                    startTime: a.start_time || '00:00',
                    endTime: a.end_time || '',
                    minutesBefore: a.reminder_minutes,
                    reminderTarget: a.reminder_target || 'start',
                    childName: a.child_name || a.childName || '',
                  });
                  alarmsScheduled++;
                }
              }
              console.log(`🔔 Scheduled ${alarmsScheduled} alarms after term planner save`);
            }
          } catch (e) {
            console.log('Failed to schedule alarms after term planner save', e);
          }
        };
        if (Capacitor.isNativePlatform()) {
          scheduleAlarmsInBackground();
        }

        const currentChildName = termPlannerChild?.name || `${termPlannerChild?.first_name} ${termPlannerChild?.last_name}`;
        const activitiesCreated = response.data?.activities_created || 0;

        // If we came from onboarding, handle the multi-child flow
        if (cameFromOnboarding) {
          const currentChildUuid = termPlannerChild?.uuid;
          const currentChildName_debug = termPlannerChild?.first_name || termPlannerChild?.name;

          console.log('🔄 ONBOARDING SAVE - Current child:', currentChildName_debug, currentChildUuid);
          console.log('🔄 ONBOARDING SAVE - processedOnboardingChildrenRef BEFORE:', Array.from(processedOnboardingChildrenRef.current));
          console.log('🔄 ONBOARDING SAVE - onboardingTermPlanners:', onboardingTermPlanners.map(tp => tp.childName));
          console.log('🔄 ONBOARDING SAVE - onboardingChildrenWithoutPlanners:', onboardingChildrenWithoutPlanners.map((c: any) => c.first_name || c.name));
          console.log('🔄 ONBOARDING SAVE - currentOnboardingTermPlannerIndex:', currentOnboardingTermPlannerIndex);

          // Mark this child as processed (using ref for synchronous update)
          if (currentChildUuid) {
            processedOnboardingChildrenRef.current.add(currentChildUuid);
            console.log('🔄 ONBOARDING SAVE - Added to processedRef:', currentChildUuid);
          }

          console.log('🔄 ONBOARDING SAVE - processedOnboardingChildrenRef AFTER:', Array.from(processedOnboardingChildrenRef.current));

          // Find the next term planner for a DIFFERENT child (skip duplicates for same child AND already processed children)
          let nextIndex = currentOnboardingTermPlannerIndex + 1;
          while (nextIndex < onboardingTermPlanners.length &&
                 (onboardingTermPlanners[nextIndex].childUuid === currentChildUuid ||
                  processedOnboardingChildrenRef.current.has(onboardingTermPlanners[nextIndex].childUuid))) {
            nextIndex++;
          }

          console.log('🔄 ONBOARDING SAVE - nextIndex after while loop:', nextIndex, 'of', onboardingTermPlanners.length);

          // Check if there are more SchoolsBuddy term planners to review for other children
          if (nextIndex < onboardingTermPlanners.length) {
            const nextTP = onboardingTermPlanners[nextIndex];
            console.log('🔄 ONBOARDING SAVE - Found more SchoolsBuddy planners, next child:', nextTP.childName);
            const nextChild = children.find((c: any) => c.uuid === nextTP.childUuid);
            if (nextChild) {
              const remainingChildrenWithoutPlannersLocal = onboardingChildrenWithoutPlanners.filter(
                (child: any) => !processedOnboardingChildrenRef.current.has(child.uuid)
              );
              const remainingCount = (onboardingTermPlanners.length - nextIndex) + remainingChildrenWithoutPlannersLocal.length;

              pendingNextChildActionRef.current = () => {
                // Move to next SchoolsBuddy term planner (for a different child)
                // Even though they have a SchoolsBuddy term planner, show setup screen first
                // so user can confirm/edit dates and times (child may be at different school)
                setCurrentOnboardingTermPlannerIndex(nextIndex);
                console.log('🔄 Moving to next SchoolsBuddy child:', nextChild.first_name || nextChild.name);
                setTermPlannerChild(nextChild);

                setLoadingTermPlannerTransition(true);
                handleEditTermPlanner(nextTP.termPlannerUuid).then(() => {
                  console.log('🔄 Loaded term planner, now showing setup step for date/time confirmation');
                  const defaults = getDefaultPlannerDates();
                  setTermStartDate(defaults.startDate);
                  setTermEndDate(defaults.endDate);
                  setTermName(defaults.termName);
                  setLoadingTermPlannerTransition(false);
                  setTermPlannerStep('setup');
                  setTimeout(() => termPlannerBodyRef.current?.scrollTo(0, 0), 50);
                }).catch(() => {
                  setLoadingTermPlannerTransition(false);
                });
                setHasUnsavedTermPlannerChanges(false);
              };

              setNextChildIntroSlide({
                nextChildName: nextTP.childName,
                currentChildName,
                activitiesCreated,
                alarmsScheduled,
                remainingCount,
                hasIntegrations: childHasIntegrations(nextTP.childUuid),
              });
              return;
            }
          }

          // Done with SchoolsBuddy planners - check if there are children without any planners
          // Filter out children we've already processed
          console.log('🔄 ONBOARDING SAVE - No more SchoolsBuddy planners, checking children without planners');
          const remainingChildrenWithoutPlanners = onboardingChildrenWithoutPlanners.filter(
            (child: any) => !processedOnboardingChildrenRef.current.has(child.uuid)
          );
          console.log('🔄 ONBOARDING SAVE - remainingChildrenWithoutPlanners:', remainingChildrenWithoutPlanners.map((c: any) => c.first_name || c.name));

          if (remainingChildrenWithoutPlanners.length > 0) {
            const nextChild = remainingChildrenWithoutPlanners[0];
            const nextChildName = nextChild.first_name || nextChild.name?.split(' ')[0] || nextChild.name || 'your child';
            console.log('🔄 ONBOARDING SAVE - Will show intro for next child:', nextChildName);

            pendingNextChildActionRef.current = () => {
              console.log('🔄 SWITCHING TO MANUAL SETUP for:', nextChildName);
              setOnboardingChildrenWithoutPlanners(remainingChildrenWithoutPlanners.slice(1));
              setTermPlannerChild(nextChild);
              const defaults = getDefaultPlannerDates();
              setEditingTermPlannerUuid(null);
              setTermName(defaults.termName);
              setTermStartDate(defaults.startDate);
              setTermEndDate(defaults.endDate);
              setSchoolStartTime('08:45');
              setSchoolEndTime('15:30');
              setDailySchedules({
                0: { drop_off: { manualOneOffs: [] }, club_reminders: [], pick_up: { manualOneOffs: [] } },
                1: { drop_off: { manualOneOffs: [] }, club_reminders: [], pick_up: { manualOneOffs: [] } },
                2: { drop_off: { manualOneOffs: [] }, club_reminders: [], pick_up: { manualOneOffs: [] } },
                3: { drop_off: { manualOneOffs: [] }, club_reminders: [], pick_up: { manualOneOffs: [] } },
                4: { drop_off: { manualOneOffs: [] }, club_reminders: [], pick_up: { manualOneOffs: [] } },
                5: { drop_off: { manualOneOffs: [] }, club_reminders: [], pick_up: { manualOneOffs: [] } },
                6: { drop_off: { manualOneOffs: [] }, club_reminders: [], pick_up: { manualOneOffs: [] } }
              });
              setCurrentDayTab(1);
              setHasUnsavedTermPlannerChanges(false);
              setTermPlannerStep('setup');
              setTimeout(() => termPlannerBodyRef.current?.scrollTo(0, 0), 50);
            };

            setNextChildIntroSlide({
              nextChildName,
              currentChildName,
              activitiesCreated,
              alarmsScheduled,
              remainingCount: remainingChildrenWithoutPlanners.length,
              hasIntegrations: childHasIntegrations(nextChild.uuid),
            });
            return;
          }

          // All done - finish onboarding
          const alarmText = alarmsScheduled > 0 ? `\n${alarmsScheduled} reminder alarms scheduled.` : '';
          alert(`✅ Term Planner saved for ${currentChildName}!\n\n${activitiesCreated} recurring activities created.${alarmText}`);
          setCameFromOnboarding(false);
          setOnboardingTermPlanners([]);
          setOnboardingChildrenWithoutPlanners([]);
          processedOnboardingChildrenRef.current.clear(); // Reset for next onboarding session
          navigate('/');
          window.dispatchEvent(new CustomEvent('termPlannerSetupComplete'));
        } else {
          // Not from onboarding - show summary of what was created
          const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
          const reminderSummaryLines: string[] = [];

          Object.entries(dailySchedules).forEach(([dayIdx, sched]: [string, any]) => {
            if (!sched) return;
            const dayName = dayNames[parseInt(dayIdx)] || `Day ${dayIdx}`;
            const items: string[] = [];

            if (sched.drop_off?.time) {
              const rm = sched.drop_off.reminder_minutes;
              const reminder = rm != null && rm >= 0 ? ` (reminder ${rm === 0 ? 'at time' : rm + 'min before'})` : '';
              items.push(`Drop-off at ${sched.drop_off.time}${reminder}`);
            }
            if (sched.club_reminders?.length > 0) {
              sched.club_reminders.forEach((club: any) => {
                if (club.activity_name || club.club_name) {
                  items.push(`${club.activity_name || club.club_name}${club.start_time ? ' at ' + club.start_time : ''}`);
                }
              });
            }
            if (sched.pick_up?.time) {
              const rm = sched.pick_up.reminder_minutes;
              const reminder = rm != null && rm >= 0 ? ` (reminder ${rm === 0 ? 'at time' : rm + 'min before'})` : '';
              items.push(`Pick-up at ${sched.pick_up.time}${reminder}`);
            }

            if (items.length > 0) {
              reminderSummaryLines.push(`${dayName}:\n  • ${items.join('\n  • ')}`);
            }
          });

          const summaryText = reminderSummaryLines.length > 0
            ? `\n\n${reminderSummaryLines.join('\n')}`
            : '';

          const alarmText = alarmsScheduled > 0 ? `\n${alarmsScheduled} reminder alarms scheduled.` : '';
          alert(`✅ Term Planner ${editingTermPlannerUuid ? 'updated' : 'saved'} for ${currentChildName}!\n\n${activitiesCreated} recurring activities created.${alarmText}${summaryText}`);

          // Reset editing state and close modal - go back to profile page
          setEditingTermPlannerUuid(null);
          setTermPlannerStep('list');
          setHasUnsavedTermPlannerChanges(false);
          setShowTermPlannerModal(false);
          setTermPlannerChild(null);
        }
      } else {
        alert(`Failed to ${editingTermPlannerUuid ? 'update' : 'create'} term planner: ${response.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to save term planner:', error);
      alert('Failed to save term planner. Please try again.');
    } finally {
      setCreatingTermPlanner(false);
    }
  };

  const handleEditTermPlanner = async (termPlannerUuid: string) => {
    const primaryParent = parents.find((p: any) => p.is_primary) || parents[0];
    if (!primaryParent) return;

    try {
      const response = await apiService.getTermPlanner(primaryParent.uuid, termPlannerUuid);
      console.log('[TP-EDIT] GET response success:', response.success, 'schedules:', response.data?.daily_schedules?.length);
      if (response.data?.daily_schedules) {
        response.data.daily_schedules.forEach((s: any) => {
          const doCount = (s.drop_off_manual_one_offs || []).length;
          const puCount = (s.pick_up_manual_one_offs || []).length;
          if (doCount > 0 || puCount > 0) console.log(`[TP-EDIT] Day ${s.day_of_week}: ${doCount} drop-off, ${puCount} pick-up one-offs`);
        });
      }
      if (response.success && response.data) {
        const termData = response.data;

        // Helper function to format dates for date inputs (YYYY-MM-DD)
        const formatDateForInput = (dateStr: string) => {
          if (!dateStr) return '';
          const date = new Date(dateStr);
          if (isNaN(date.getTime())) return ''; // Invalid date
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        // Load term data into form
        setTermName(termData.term_name);
        setTermStartDate(formatDateForInput(termData.start_date));
        setTermEndDate(formatDateForInput(termData.end_date));
        setEditingTermPlannerUuid(termPlannerUuid);

        // Load school times from saved term planner
        const loadedStartTime = termData.school_start_time || '08:45';
        const loadedEndTime = termData.school_end_time || '15:30';
        setSchoolStartTime(loadedStartTime);
        setSchoolEndTime(loadedEndTime);

        // Convert daily schedules from database format to form format
        // Use loaded school times for weekdays (Mon-Fri = days 1-5), empty for weekends
        const schedules: any = {
          0: { drop_off: { manualOneOffs: [] }, club_reminders: [], pick_up: { manualOneOffs: [] } }, // Sunday
          1: { drop_off: { time: loadedStartTime, manualOneOffs: [] }, club_reminders: [], pick_up: { time: loadedEndTime, manualOneOffs: [] } }, // Monday
          2: { drop_off: { time: loadedStartTime, manualOneOffs: [] }, club_reminders: [], pick_up: { time: loadedEndTime, manualOneOffs: [] } }, // Tuesday
          3: { drop_off: { time: loadedStartTime, manualOneOffs: [] }, club_reminders: [], pick_up: { time: loadedEndTime, manualOneOffs: [] } }, // Wednesday
          4: { drop_off: { time: loadedStartTime, manualOneOffs: [] }, club_reminders: [], pick_up: { time: loadedEndTime, manualOneOffs: [] } }, // Thursday
          5: { drop_off: { time: loadedStartTime, manualOneOffs: [] }, club_reminders: [], pick_up: { time: loadedEndTime, manualOneOffs: [] } }, // Friday
          6: { drop_off: { manualOneOffs: [] }, club_reminders: [], pick_up: { manualOneOffs: [] } }  // Saturday
        };

        if (termData.daily_schedules) {
          termData.daily_schedules.forEach((schedule: any) => {
            // Frontend and backend now both use JavaScript day numbering (0=Sun, 1=Mon, ... 6=Sat)
            const dayIndex = schedule.day_of_week;

            // Convert old single-club format to new clubs array format
            // This handles both SchoolsBuddy imported data (single club) and manually added clubs (array)
            let dropOffClubs = schedule.drop_off_clubs || [];
            if (dropOffClubs.length === 0 && schedule.drop_off_club_name) {
              // Convert old format to new array format
              dropOffClubs = [{
                name: schedule.drop_off_club_name,
                startTime: schedule.drop_off_time || '',
                endTime: '',
                venue: schedule.drop_off_venue || '',
                city: schedule.drop_off_city || '',
                kit_items: schedule.drop_off_kit_items || [],
                uniform_items: schedule.drop_off_uniform_type ? schedule.drop_off_uniform_type.split(', ') : [],
                designated_parent_uuid: schedule.drop_off_designated_parent_uuid || ''
              }];
            }

            let pickUpClubs = schedule.pick_up_clubs || [];
            if (pickUpClubs.length === 0 && schedule.pick_up_club_name) {
              // Convert old format to new array format
              pickUpClubs = [{
                name: schedule.pick_up_club_name,
                startTime: schedule.pick_up_time || '',
                endTime: '',
                venue: schedule.pick_up_venue || '',
                city: schedule.pick_up_city || '',
                kit_items: schedule.pick_up_kit_items || [],
                uniform_items: [],
                designated_parent_uuid: schedule.pick_up_designated_parent_uuid || ''
              }];
            }

            // Calculate drop-off time from clubs (use earliest start time)
            let calculatedDropOffTime = schedule.drop_off_time || '';
            if (dropOffClubs.length > 0) {
              const clubTimes = dropOffClubs
                .map((c: any) => c.startTime)
                .filter((t: string) => t && t !== '--');
              if (clubTimes.length > 0) {
                // Sort times and get earliest
                clubTimes.sort();
                calculatedDropOffTime = clubTimes[0];
              }
            }

            // Calculate pick-up time from clubs (use earliest start and latest end)
            let calculatedPickUpTime = schedule.pick_up_time || '';
            if (pickUpClubs.length > 0) {
              const startTimes = pickUpClubs
                .map((c: any) => c.startTime)
                .filter((t: string) => t && t !== '--');
              if (startTimes.length > 0) {
                // Sort times and get earliest for pick-up start
                startTimes.sort();
                calculatedPickUpTime = startTimes[0];
              }
            }

            schedules[dayIndex] = {
              drop_off: {
                time: calculatedDropOffTime,
                uniform_items: schedule.drop_off_uniform_type ? schedule.drop_off_uniform_type.split(', ') : [],
                club_name: schedule.drop_off_club_name || '',
                venue: schedule.drop_off_venue || '',
                city: schedule.drop_off_city || '',
                designated_parent_uuid: schedule.drop_off_designated_parent_uuid || '',
                kit_items: schedule.drop_off_kit_items || [],
                notes: schedule.drop_off_notes || null,
                manualOneOffs: schedule.drop_off_manual_one_offs || [],
                clubs: dropOffClubs,
                reminder_minutes: schedule.drop_off_reminder_minutes ?? null
              },
              club_reminders: (schedule.club_reminders || []).map((r: any) => ({
                club_name: r.activity_name,
                activity_name: r.activity_name,
                venue: r.club_venue || '',
                city: r.club_city || '',
                kit_needed: r.kit_needed,
                kit_items: r.kit_items || [],
                notes: r.notes || null,
                manual_one_offs: r.manual_one_offs || [],
                start_time: r.start_time || '',
                end_time: r.end_time || ''
              })),
              pick_up: {
                time: calculatedPickUpTime,
                club_name: schedule.pick_up_club_name || '',
                venue: schedule.pick_up_venue || '',
                city: schedule.pick_up_city || '',
                designated_parent_uuid: schedule.pick_up_designated_parent_uuid || '',
                kit_items: schedule.pick_up_kit_items || [],
                notes: schedule.pick_up_notes || null,
                manualOneOffs: schedule.pick_up_manual_one_offs || [],
                clubs: pickUpClubs,
                reminder_minutes: schedule.pick_up_reminder_minutes ?? null
              }
            };
          });
        }

        setDailySchedules(schedules);

        // Auto-navigate to first day with integration data so user sees it immediately
        const firstDayWithData = [1,2,3,4,5,0,6].find(d => {
          const s = schedules[d];
          return (s?.drop_off?.manualOneOffs?.length > 0) || (s?.drop_off?.clubs?.length > 0) ||
                 (s?.pick_up?.manualOneOffs?.length > 0) || (s?.pick_up?.clubs?.length > 0);
        });
        if (firstDayWithData !== undefined) setCurrentDayTab(firstDayWithData);

        setTermPlannerStep('create');
      } else {
        alert(`Failed to load term planner: ${response.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to load term planner:', error);
      alert('Failed to load term planner. Please try again.');
    }
  };

  const handleDeleteTermPlanner = async (termPlannerUuid: string, activityCount?: number) => {
    const countText = activityCount ? ` and ${activityCount} associated ${activityCount === 1 ? 'activity' : 'activities'}` : ' and all associated activities';
    if (!window.confirm(`Are you sure you want to delete this term planner${countText}?`)) {
      return;
    }

    const primaryParent = parents.find((p: any) => p.is_primary) || parents[0];
    if (!primaryParent) return;

    try {
      const response = await apiService.deleteTermPlanner(primaryParent.uuid, termPlannerUuid);
      if (response.success) {
        // Cancel AlarmKit alarms and local notifications for all deleted activities
        const deletedUuids: string[] = response.data?.deleted_activity_uuids || [];
        if (deletedUuids.length > 0) {
          const localNotifService = LocalNotificationService.getInstance();
          for (const activityUuid of deletedUuids) {
            await localNotifService.cancelReminder(activityUuid);
          }
          console.log(`✅ Cancelled ${deletedUuids.length} reminders/alarms for deleted term planner activities`);
        }

        alert(`✅ Term planner deleted. ${response.data?.activities_deleted || 0} activities were removed.`);
        // Reload term planners list (for modal)
        if (termPlannerChild) {
          const listResponse = await apiService.getTermPlanners(primaryParent.uuid, termPlannerChild.uuid);
          if (listResponse.success && listResponse.data) {
            setExistingTermPlanners(listResponse.data);
          }
        }
        // Reload all term planners for the section view
        await loadAllTermPlanners();
      } else {
        alert(`Failed to delete term planner: ${response.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to delete term planner:', error);
      alert('Failed to delete term planner. Please try again.');
    }
  };

  const updateDaySchedule = (dayIndex: number, field: string, value: any) => {
    setDailySchedules((prev: any) => ({
      ...prev,
      [dayIndex]: {
        ...prev[dayIndex],
        [field]: value
      }
    }));
  };

  const addClubReminder = (dayIndex: number) => {
    setDailySchedules((prev: any) => ({
      ...prev,
      [dayIndex]: {
        ...prev[dayIndex],
        club_reminders: [
          ...(prev[dayIndex].club_reminders || []),
          { activity_name: '', kit_needed: '' }
        ]
      }
    }));
  };

  const removeClubReminder = (dayIndex: number, reminderIndex: number) => {
    setDailySchedules((prev: any) => ({
      ...prev,
      [dayIndex]: {
        ...prev[dayIndex],
        club_reminders: prev[dayIndex].club_reminders.filter((_: any, i: number) => i !== reminderIndex)
      }
    }));
  };

  const updateClubReminder = (dayIndex: number, reminderIndex: number, field: string, value: any) => {
    setDailySchedules((prev: any) => ({
      ...prev,
      [dayIndex]: {
        ...prev[dayIndex],
        club_reminders: prev[dayIndex].club_reminders.map((reminder: any, i: number) => {
          if (i === reminderIndex) {
            // If clearing club_name, also clear venue and city
            if (field === 'club_name' && value === '') {
              return { ...reminder, club_name: '', venue: '', city: '', kit_items: [] };
            }
            return { ...reminder, [field]: value };
          }
          return reminder;
        })
      }
    }));
  };

  // Clear all schedule data for a specific category on the current day
  const clearScheduleForCategory = (category: 'drop_off' | 'special_lessons' | 'pick_up') => {
    setDailySchedules((prev: any) => {
      const updated = { ...prev };

      if (category === 'drop_off') {
        // Clear drop-off: remove clubs, manualOneOffs, but keep time
        updated[currentDayTab] = {
          ...updated[currentDayTab],
          drop_off: {
            ...updated[currentDayTab].drop_off,
            clubs: [],
            manualOneOffs: [],
            club_name: undefined,
            city: undefined,
            venue: undefined,
            kit_items: [],
            notes: ''
          }
        };
      } else if (category === 'special_lessons') {
        // Clear special lessons: remove all club_reminders for this day
        updated[currentDayTab] = {
          ...updated[currentDayTab],
          club_reminders: []
        };
      } else if (category === 'pick_up') {
        // Clear pick-up: remove clubs, manualOneOffs, but keep time
        updated[currentDayTab] = {
          ...updated[currentDayTab],
          pick_up: {
            ...updated[currentDayTab].pick_up,
            clubs: [],
            manualOneOffs: [],
            club_name: undefined,
            city: undefined,
            venue: undefined,
            kit_items: [],
            notes: ''
          }
        };
      }

      return updated;
    });

    setHasUnsavedTermPlannerChanges(true);
    setShowDeleteScheduleModal(false);
    setDeleteScheduleCategory(null);
  };

  const handleOpenClubModal = (type: 'drop_off' | 'pick_up') => {
    setClubModalType(type);
    setShowClubModal(true);
  };

  const handleClubSelect = (club: any) => {
    if (clubModalType === 'club_reminder') {
      // Update the specific club reminder
      const updatedReminders = [...(dailySchedules[currentDayTab].club_reminders || [])];

      if (currentClubReminderIndex === -1) {
        // Add new club reminder
        updatedReminders.push({
          club_name: club.name,
          activity_name: club.name,
          venue: club.venue || '',
          city: club.location || '',
          kit_needed: '',
          kit_items: club.kit_items || [],
          start_time: club.start_time || club.startTime || '',
          end_time: club.end_time || club.endTime || ''
        });
      } else {
        // Update existing club reminder
        updatedReminders[currentClubReminderIndex] = {
          ...updatedReminders[currentClubReminderIndex],
          club_name: club.name,
          activity_name: club.name,
          venue: club.venue || '',
          city: club.location || '',
          kit_items: club.kit_items || [],
          start_time: club.start_time || club.startTime || updatedReminders[currentClubReminderIndex].start_time || '',
          end_time: club.end_time || club.endTime || updatedReminders[currentClubReminderIndex].end_time || ''
        };
      }

      setDailySchedules((prev: any) => ({
        ...prev,
        [currentDayTab]: {
          ...prev[currentDayTab],
          club_reminders: updatedReminders
        }
      }));
    } else {
      // For drop_off and pick_up, add to clubs array (supporting multiple sequential clubs)
      const fieldType = clubModalType === 'drop_off' ? 'drop_off' : 'pick_up';
      const currentField = dailySchedules[currentDayTab][fieldType] || {};
      const existingClubs = currentField.clubs || [];

      // If there's a legacy single club, migrate it to the array first
      let clubs = [...existingClubs];
      if (currentField.club_name && clubs.length === 0) {
        clubs.push({
          club_name: currentField.club_name,
          club_uuid: currentField.club_uuid,
          venue: currentField.venue || '',
          city: currentField.city || '',
          end_time: '',
          kit_items: currentField.kit_items || []
        });
      }

      // Add the new club to the array
      clubs.push({
        club_name: club.name,
        club_uuid: club.uuid,
        venue: club.venue || '',
        city: club.location || '',
        end_time: '',
        kit_items: club.kit_items || []
      });

      updateDaySchedule(currentDayTab, fieldType, {
        ...currentField,
        clubs: clubs,
        // Keep legacy fields for backward compatibility (use first club's data)
        club_name: clubs[0]?.club_name || '',
        venue: clubs[0]?.venue || '',
        city: clubs[0]?.city || '',
        kit_items: clubs[0]?.kit_items || []
      });
    }
    setShowClubModal(false);
  };

  // Remove a club from the clubs array
  const handleRemoveClub = (fieldType: 'drop_off' | 'pick_up', clubIndex: number) => {
    const currentField = dailySchedules[currentDayTab][fieldType] || {};
    const clubs = [...(currentField.clubs || [])];
    clubs.splice(clubIndex, 1);

    updateDaySchedule(currentDayTab, fieldType, {
      ...currentField,
      clubs: clubs,
      // Update legacy fields for backward compatibility
      club_name: clubs[0]?.club_name || '',
      venue: clubs[0]?.venue || '',
      city: clubs[0]?.city || '',
      kit_items: clubs[0]?.kit_items || []
    });
  };

  // Update a specific club's end time
  const handleUpdateClubEndTime = (fieldType: 'drop_off' | 'pick_up', clubIndex: number, endTime: string) => {
    const currentField = dailySchedules[currentDayTab][fieldType] || {};
    const clubs = [...(currentField.clubs || [])];
    if (clubs[clubIndex]) {
      clubs[clubIndex] = { ...clubs[clubIndex], end_time: endTime };
    }

    updateDaySchedule(currentDayTab, fieldType, {
      ...currentField,
      clubs: clubs
    });
  };

  // Update a specific club's kit items
  const handleUpdateClubKitItems = async (fieldType: 'drop_off' | 'pick_up', clubIndex: number, kitItems: string[]) => {
    const currentField = dailySchedules[currentDayTab][fieldType] || {};
    const clubs = [...(currentField.clubs || [])];
    if (clubs[clubIndex]) {
      clubs[clubIndex] = { ...clubs[clubIndex], kit_items: kitItems };

      // Also update the club's kit items in the database
      try {
        await apiService.updateClubKitItemsByName(clubs[clubIndex].club_name, kitItems);
      } catch (error) {
        console.error('Failed to update club kit items:', error);
      }
    }

    updateDaySchedule(currentDayTab, fieldType, {
      ...currentField,
      clubs: clubs,
      // Update legacy kit_items for first club
      kit_items: clubs[0]?.kit_items || []
    });
  };

  // Helper to get clubs array, migrating from legacy single club if needed
  const getClubsArray = (fieldData: any): any[] => {
    if (fieldData?.clubs && fieldData.clubs.length > 0) {
      return fieldData.clubs;
    }
    // Migrate legacy single club to array format
    if (fieldData?.club_name) {
      return [{
        club_name: fieldData.club_name,
        club_uuid: fieldData.club_uuid,
        venue: fieldData.venue || '',
        city: fieldData.city || '',
        end_time: '',
        kit_items: fieldData.kit_items || []
      }];
    }
    return [];
  };

  const handleAddChild = async () => {
    if (!newChildFirstName.trim()) {
      alert('Please enter the child\'s first name');
      return;
    }

    if (!newChildSchoolVenue.trim()) {
      alert('Please enter the child\'s school name. This is required for integration features.');
      return;
    }

    setSavingChild(true);
    try {
      let age = undefined;
      // Construct birth date from day/month/year dropdowns
      if (newChildBirthDay && newChildBirthMonth && newChildBirthYear) {
        const birthDate = new Date(
          parseInt(newChildBirthYear),
          parseInt(newChildBirthMonth) - 1,
          parseInt(newChildBirthDay)
        );
        const today = new Date();
        age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
      }

      const childData = {
        first_name: newChildFirstName.trim(),
        last_name: newChildLastName.trim(),
        age: age,
        interests: newChildInterests.length > 0 ? newChildInterests.join(', ') : undefined,
        school_venue: newChildSchoolVenue.trim() || undefined,
        school_city: newChildSchoolCity.trim() || undefined
      };

      const response = await apiService.createChild(childData);

      if (response.success) {
        setShowAddChildModal(false);
        setNewChildFirstName('');
        setNewChildLastName('');
        setNewChildBirthDate('');
        setNewChildBirthDay('');
        setNewChildBirthMonth('');
        setNewChildBirthYear('');
        setNewChildInterests([]);
        setNewChildSchoolVenue('');
        setNewChildSchoolCity('');
        await loadChildren();

        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('childrenDataChanged'));
          // Also dispatch childCreated to trigger welcome modal during onboarding
          window.dispatchEvent(new CustomEvent('childCreated'));
        }

        alert('Child added successfully');
      } else {
        alert(`Error: ${response.error || 'Failed to add child'}`);
      }
    } catch (error) {
      alert('Failed to add child');
      console.error('Add child error:', error);
    } finally {
      setSavingChild(false);
    }
  };

  if (!user) {
    return (
      <div className="profile-screen">
        <div className="loading">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="profile-screen">
      <div className="profile-header">
        <h2>Profile</h2>
      </div>

      <div className="profile-card">
        {editing ? (
          <form className="edit-form" autoComplete="on" onSubmit={(e) => { e.preventDefault(); handleSaveProfile(); }}>
            <h3>Edit Profile</h3>

            <div className="form-group">
              <label htmlFor="profile-username">Username</label>
              <input
                id="profile-username"
                name="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="form-input"
                autoComplete="username"
              />
            </div>

            <div className="form-group">
              <label htmlFor="profile-email">Email</label>
              <input
                id="profile-email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="form-input"
                autoComplete="email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="profile-phone">Phone</label>
              <PhoneInput
                value={phone}
                onChange={setPhone}
                className="form-input"
                autoComplete="tel"
              />
            </div>

            <div className="address-section">
              <h4>Address Information</h4>

              <div className="form-group">
                <label htmlFor="profile-address">Address Line 1</label>
                <input
                  id="profile-address"
                  name="address1"
                  type="text"
                  value={addressLine1}
                  onChange={(e) => setAddressLine1(e.target.value)}
                  className="form-input"
                  placeholder="Street address"
                  autoComplete="address-line1"
                />
              </div>

              <div className="form-group">
                <label htmlFor="profile-city">Town/City</label>
                <input
                  id="profile-city"
                  name="city"
                  type="text"
                  value={townCity}
                  onChange={(e) => setTownCity(e.target.value)}
                  className="form-input"
                  placeholder="Town or city"
                  autoComplete="address-level2"
                />
              </div>

              <div className="form-group">
                <label htmlFor="profile-state">State/Province/County</label>
                <input
                  id="profile-state"
                  name="region"
                  type="text"
                  value={stateProvinceCountry}
                  onChange={(e) => setStateProvinceCountry(e.target.value)}
                  className="form-input"
                  placeholder="State, province, or county"
                  autoComplete="address-level1"
                />
              </div>

              <div className="form-group">
                <label htmlFor="profile-postcode">Post Code</label>
                <input
                  id="profile-postcode"
                  name="postalCode"
                  type="text"
                  value={postCode}
                  onChange={(e) => setPostCode(e.target.value)}
                  className="form-input"
                  placeholder="Post code"
                  autoComplete="postal-code"
                />
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                onClick={handleCancelEdit}
                className="cancel-btn"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`save-btn ${loading ? 'disabled' : ''}`}
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        ) : (
          <div className="profile-info">
            <div className="profile-avatar">
              <div className="avatar-circle">
                {user.username.charAt(0).toUpperCase()}
              </div>
              <div className="profile-details">
                <h3>{user.username}</h3>
                <p>Member since {formatDateUK(user.created_at)}</p>
              </div>
            </div>

            <div className="info-sections">
              <div className="info-section">
                <label>Username</label>
                <div className="info-value">{user.username}</div>
              </div>

              <div className="info-section">
                <label>Email</label>
                <div className="info-value">{user.email}</div>
              </div>

              <div className="info-section">
                <label>Phone</label>
                <div className="info-value">{user.phone}</div>
              </div>

              {(user.address_line_1 || user.town_city || user.state_province_country || user.post_code) && (
                <div className="address-info">
                  <h4>Address</h4>
                  {user.address_line_1 && (
                    <div className="info-section">
                      <label>Address</label>
                      <div className="info-value">{user.address_line_1}</div>
                    </div>
                  )}
                  {(user.town_city || user.post_code) && (
                    <div className="info-section">
                      <label>Town/City & Post Code</label>
                      <div className="info-value">
                        {user.town_city}{user.town_city && user.post_code && ', '}{user.post_code}
                      </div>
                    </div>
                  )}
                  {user.state_province_country && (
                    <div className="info-section">
                      <label>State/Province/County</label>
                      <div className="info-value">{user.state_province_country}</div>
                    </div>
                  )}
                </div>
              )}

              <div className="info-section">
                <label>Role</label>
                <div className="info-value">
                  <span className={`role-badge ${user.role}`}>
                    {user.role === 'super_admin' ? 'Super Admin' : 
                     user.role === 'admin' ? 'Admin' : 'User'}
                  </span>
                </div>
              </div>
            </div>

            <button
              onClick={handleEditProfile}
              disabled={loading}
              className="edit-btn"
            >
              {loading ? 'Loading...' : 'Edit Profile'}
            </button>
          </div>
        )}
      </div>

      {/* Children Section */}
      <div className="profile-card">
        <div className="collapsible-header" onClick={() => toggleSection('children')}>
          <h3><span className="collapse-chevron">{collapsedSections.children ? '▶' : '▼'}</span> My Children</h3>
          {!collapsedSections.children && (
            <button
              onClick={(e) => { e.stopPropagation(); setShowAddChildModal(true); }}
              className="add-btn"
              style={{ marginLeft: 'auto' }}
            >
              + Add Child
            </button>
          )}
        </div>

        {!collapsedSections.children && (children.length === 0 ? (
          <div className="empty-state">
            <p>No children added yet</p>
            <p>Click "Add Child" above to get started</p>
          </div>
        ) : (
          <div className="parents-list">
            {/* Hide the account's own placeholder row. Signup inserts a children row
                with is_parent=true so a parent can own activities without a child. Listing
                it here — with Edit and Delete — invited exactly what happened on this
                account: the row was edited into a real child, which then stayed invisible
                to Term Planners and Holiday Mode because those DO filter it out. */}
            {children.filter(c => !(c as any).is_parent).map((child) => (
              <div key={child.uuid} className="parent-card">
                <div className="parent-info">
                  <div className="parent-avatar">
                    {(child.display_name || child.name).charAt(0).toUpperCase()}
                  </div>
                  <div className="parent-details">
                    <h4>{child.display_name || child.name}</h4>
                    {child.age && <p>Age: {child.age}</p>}
                    {child.interests && <p>{child.interests}</p>}
                  </div>
                </div>
                <div className="child-actions">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditChild(child);
                    }}
                    className="edit-btn"
                  >
                    Edit
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteChild(child);
                    }}
                    className="delete-btn"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Parents Section */}
      <div className="profile-card">
        <div className="collapsible-header" onClick={() => toggleSection('family')}>
          <h3><span className="collapse-chevron">{collapsedSections.family ? '▶' : '▼'}</span> Family Members</h3>
          {!collapsedSections.family && (
            <button onClick={(e) => { e.stopPropagation(); handleAddParent(); }} className="add-btn">
              + Add Parent
            </button>
          )}
        </div>
        
        {!collapsedSections.family && (parents.length === 0 ? (
          <div className="empty-state">
            <p>No additional parents added</p>
            <p>Add another parent to share access to children and activities</p>
          </div>
        ) : (
          <div className="parents-list">
            {parents.map((parent) => (
              <div key={parent.uuid} className="parent-card">
                <div className="parent-info">
                  <div className="parent-avatar">
                    {parent.username.charAt(0).toUpperCase()}
                  </div>
                  <div className="parent-details">
                    <h4>{parent.username}</h4>
                    <p>{parent.email}</p>
                    <p>{parent.phone}</p>
                    <span className={`role-badge ${parent.role}`}>
                      {parent.is_primary ? 'Primary Account' : parent.role.charAt(0).toUpperCase() + parent.role.slice(1)}
                    </span>
                  </div>
                </div>
                {!parent.is_primary && (
                  <button
                    onClick={() => handleDeleteParent(parent.uuid, parent.username)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'black',
                      cursor: 'pointer',
                      padding: 0,
                      font: 'inherit',
                      textDecoration: 'underline'
                    }}
                    title="Remove parent"
                  >
                    Delete
                  </button>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Class Rep Section */}
      <ClassRepSection
        myChildren={children}
        collapsed={collapsedSections.classRep ?? true}
        onToggle={() => toggleSection('classRep')}
      />

      {/* Integrations Section */}
      <div className="profile-card">
        <div className="collapsible-header" onClick={() => toggleSection('integrations')}>
          <h3><span className="collapse-chevron">{collapsedSections.integrations ? '▶' : '▼'}</span> Integrations</h3>
        </div>

        {!collapsedSections.integrations && (loadingIntegrations ? (
          <div className="empty-state">
            <p>Loading integrations...</p>
          </div>
        ) : (
          <>
            {/* Always show Pitchero connection option */}
            {integrations.length === 0 ? (
              // No integrations connected - show setup cards
              <div className="integrations-list">
                <div className="integration-summary-card">
                  <div className="integration-summary-icon">
                    <span className="integration-letter">S</span>
                  </div>
                  <div className="integration-summary-details">
                    <h4>SOCS School Sports</h4>
                    <p style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                      Automatically sync school sports fixtures and matches from your school's SOCS system
                    </p>
                    <div className="integration-actions" style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                      <button
                        onClick={() => {
                          if (children.length === 0) {
                            alert('Please add children to your account first before setting up SOCS integration');
                            return;
                          }
                          setShowSOCSModal(true);
                        }}
                        className="view-btn"
                        style={{ flex: '1 1 auto' }}
                      >
                        🏫 Setup SOCS Sync
                      </button>
                    </div>
                  </div>
                </div>
                <div className="integration-summary-card">
                  <div className="integration-summary-icon">
                    <span className="integration-letter">P</span>
                  </div>
                  <div className="integration-summary-details">
                    <h4>Pitchero</h4>
                    <p style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                      Connect your Pitchero account to automatically sync club activities, training sessions, and matches
                    </p>
                    <div className="integration-actions" style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                      <button
                        onClick={() => {
                          setSelectedIntegration({ id: 'pitchero', name: 'Pitchero' });
                          setPitcheroUsername('');
                          setPitcheroPassword('');
                          setShowCredentialsModal(true);
                        }}
                        className="view-btn"
                        style={{ flex: '1 1 auto' }}
                      >
                        🔗 Connect Pitchero
                      </button>
                    </div>
                  </div>
                </div>
                {INTEGRATION_BUILDER_ENABLED && (<>
                <div className="integration-summary-card">
                  <div className="integration-summary-icon">
                    <span className="integration-letter">+</span>
                  </div>
                  <div className="integration-summary-details">
                    <h4>Build a new integration</h4>
                    <p style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                      Don't see your child's club app? Teach Cluubz to read it — log in inside a scan window and we'll figure out the rest.
                    </p>
                    <div className="integration-actions" style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                      <button
                        onClick={() => setShowIntegrationBuilder(true)}
                        className="view-btn"
                        style={{ flex: '1 1 auto' }}
                      >
                        ✨ Build a new integration
                      </button>
                    </div>
                  </div>
                </div>
                <div className="integration-summary-card" style={{ background: '#f7f7f8' }}>
                  <div className="integration-summary-icon">
                    <span className="integration-letter">🌐</span>
                  </div>
                  <div className="integration-summary-details">
                    <h4>Crowd Connectors</h4>
                    <p style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                      Search integrations other parents have built — for Spond, Pitchero, TeamSnap and hundreds more club apps. Enable and enter your login to sync fixtures automatically.
                    </p>
                    <input
                      value={crowdQuery}
                      onChange={(e) => setCrowdQuery(e.target.value)}
                      placeholder="Search connectors (e.g. spond, pitchero, teamsnap)…"
                      style={{ width: '100%', marginTop: '10px', padding: '8px 10px', border: '1px solid #ddd', borderRadius: '6px', boxSizing: 'border-box', fontSize: '13px' }}
                    />
                    {crowdError && <div style={{ color: '#c33', fontSize: '12px', marginTop: '6px' }}>{crowdError}</div>}
                    <div style={{ marginTop: '8px', maxHeight: '260px', overflowY: 'auto' }}>
                      {crowdLoading && <div style={{ fontSize: '12px', color: '#666' }}>Searching…</div>}
                      {!crowdLoading && crowdResults.length === 0 && (
                        <div style={{ fontSize: '12px', color: '#666' }}>{crowdQuery ? 'No connectors match.' : 'No connectors yet. Build one!'}</div>
                      )}
                      {crowdResults.map((c: any) => (
                        <div key={c.uuid} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {c.app_name || c.slug}
                              <span style={{ fontSize: '10px', color: '#888', marginLeft: '6px' }}>v{c.version}</span>
                              {c.enabled && <span style={{ background: '#0a7', color: '#fff', fontSize: '10px', padding: '1px 6px', borderRadius: '3px', marginLeft: '6px' }}>Enabled</span>}
                              {!c.enabled && c.success_count > 0 && <span style={{ background: '#eee', color: '#333', fontSize: '10px', padding: '1px 6px', borderRadius: '3px', marginLeft: '6px' }}>{c.success_count} syncs</span>}
                              {c.sync_mode === 'browser' && <span title="This site blocks our servers. You'll need to hit Sync Now yourself." style={{ background: '#fed7aa', color: '#9a3412', fontSize: '10px', padding: '1px 6px', borderRadius: '3px', marginLeft: '6px' }}>Manual sync</span>}
                            </div>
                            {c.enabled_last_sync_error && (
                              <div style={{ fontSize: '11px', color: '#c33' }}>⚠️ {c.enabled_last_sync_error}</div>
                            )}
                            {browserSyncStatus[c.uuid] && (
                              <div style={{ fontSize: '11px', color: browserSyncStatus[c.uuid].kind === 'ok' ? '#0a7' : browserSyncStatus[c.uuid].kind === 'err' ? '#c33' : '#666', marginTop: '2px' }}>{browserSyncStatus[c.uuid].msg}</div>
                            )}
                          </div>
                          {c.enabled && c.sync_mode === 'browser' && (
                            <button
                              onClick={() => runBrowserSync(c.uuid)}
                              className="view-btn"
                              style={{ fontSize: '12px', padding: '4px 10px', background: '#f97316', color: '#fff' }}
                              disabled={!!browserSyncStatus[c.uuid]?.running}
                            >
                              {browserSyncStatus[c.uuid]?.running ? '⏳ Syncing…' : '🔄 Sync now'}
                            </button>
                          )}
                          <button
                            onClick={() => openCrowdEnable(c)}
                            className="view-btn"
                            style={{ fontSize: '12px', padding: '4px 10px' }}
                          >
                            {c.enabled ? 'Reconfigure' : 'Enable'}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                </>)}
                <div className="integration-summary-card">
                  <div className="integration-summary-icon">
                    <span className="integration-letter">M</span>
                  </div>
                  <div className="integration-summary-details">
                    <h4>My School Portal</h4>
                    <p style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                      Sync calendar events and fixtures from Berkhamsted, Westbrook Hay, and other My School Portal schools
                    </p>
                    <div className="integration-actions" style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                      <button
                        onClick={() => {
                          if (children.length === 0) {
                            alert('Please add children to your account first before setting up School Portal integration');
                            return;
                          }
                          setShowSchoolPortalModal(true);
                        }}
                        className="view-btn"
                        style={{ flex: '1 1 auto' }}
                      >
                        🔗 Connect School Portal
                      </button>
                    </div>
                  </div>
                </div>
                <div className="integration-summary-card">
                  <div className="integration-summary-icon">
                    <span className="integration-letter">S</span>
                  </div>
                  <div className="integration-summary-details">
                    <h4>Spond</h4>
                    <p style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                      Sync fixtures and availability from your Spond clubs and teams
                    </p>
                    <div className="integration-actions" style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                      <button
                        onClick={() => {
                          if (children.length === 0) {
                            alert('Please add children to your account first before setting up Spond');
                            return;
                          }
                          setShowSpondModal(true);
                        }}
                        className="view-btn"
                        style={{ flex: '1 1 auto' }}
                      >
                        🔗 Connect Spond
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // Show connected integrations
              <div className="integrations-list">
                {/* Show SOCS Setup option if NOT connected */}
                {(!integrations.find(i => i.id === 'socs') || integrations.find(i => i.id === 'socs' && !i.connected)) && (
                  <div className="integration-summary-card">
                    <div className="integration-summary-icon">
                      <span className="integration-letter">S</span>
                    </div>
                    <div className="integration-summary-details">
                      <h4>SOCS School Sports</h4>
                      <p style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                        Automatically sync school sports fixtures and matches from your school's SOCS system
                      </p>
                      <div className="integration-actions" style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                        <button
                          onClick={() => {
                            if (children.length === 0) {
                              alert('Please add children to your account first before setting up SOCS integration');
                              return;
                            }
                            setShowSOCSModal(true);
                          }}
                          className="view-btn"
                          style={{ flex: '1 1 auto' }}
                        >
                          Set Up SOCS
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Show Pitchero Setup if NOT connected */}
                {integrations.find(i => i.id === 'pitchero' && !i.connected) && (
                  <div className="integration-summary-card">
                    <div className="integration-summary-icon">
                      <span className="integration-letter">P</span>
                    </div>
                    <div className="integration-summary-details">
                      <h4>Pitchero</h4>
                      <p style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                        Sync availability and fixtures from your Pitchero clubs
                      </p>
                      <div className="integration-actions" style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                        <button
                          onClick={() => {
                            const pitcheroIntegration = integrations.find(i => i.id === 'pitchero');
                            if (pitcheroIntegration) {
                              handleManageIntegration(pitcheroIntegration);
                            }
                          }}
                          className="view-btn"
                          style={{ flex: '1 1 auto' }}
                        >
                          Set Up Pitchero
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Show SchoolsBuddy Setup if NOT connected */}
                {integrations.find(i => i.id === 'schoolsbuddy' && !i.connected) && (
                  <div className="integration-summary-card">
                    <div className="integration-summary-icon">
                      <span className="integration-letter">S</span>
                    </div>
                    <div className="integration-summary-details">
                      <h4>SchoolsBuddy</h4>
                      <p style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                        Sync clubs and activities from your SchoolsBuddy account
                      </p>
                      <div className="integration-actions" style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                        <button
                          onClick={() => {
                            const schoolsBuddyIntegration = integrations.find(i => i.id === 'schoolsbuddy');
                            if (schoolsBuddyIntegration) {
                              handleManageIntegration(schoolsBuddyIntegration);
                            }
                          }}
                          className="view-btn"
                          style={{ flex: '1 1 auto' }}
                        >
                          Set Up SchoolsBuddy
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Show School Portal Setup if NOT connected */}
                {integrations.find(i => i.id === 'schoolportal' && !i.connected) && (
                  <div className="integration-summary-card">
                    <div className="integration-summary-icon">
                      <span className="integration-letter">M</span>
                    </div>
                    <div className="integration-summary-details">
                      <h4>My School Portal</h4>
                      <p style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                        Sync calendar events and fixtures from Berkhamsted, Westbrook Hay, and other My School Portal schools
                      </p>
                      <div className="integration-actions" style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                        <button
                          onClick={() => {
                            if (children.length === 0) {
                              alert('Please add children to your account first before setting up School Portal integration');
                              return;
                            }
                            setShowSchoolPortalModal(true);
                          }}
                          className="view-btn"
                          style={{ flex: '1 1 auto' }}
                        >
                          Set Up School Portal
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Show Spond Setup if NOT connected */}
                {integrations.find(i => i.id === 'spond' && !i.connected) && (
                  <div className="integration-summary-card">
                    <div className="integration-summary-icon">
                      <span className="integration-letter">S</span>
                    </div>
                    <div className="integration-summary-details">
                      <h4>Spond</h4>
                      <p style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                        Sync fixtures and availability from your Spond clubs and teams
                      </p>
                      <div className="integration-actions" style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                        <button
                          onClick={() => {
                            if (children.length === 0) {
                              alert('Please add children to your account first before setting up Spond');
                              return;
                            }
                            setShowSpondModal(true);
                          }}
                          className="view-btn"
                          style={{ flex: '1 1 auto' }}
                        >
                          Connect Spond
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Show connected integrations */}
                {integrations.filter(i => i.connected).map((integration) => (
                  <div key={integration.id} className="integration-summary-card">
                    <div className="integration-summary-icon">
                      <span className="integration-letter">{integration.name.charAt(0)}</span>
                    </div>
                    <div className="integration-summary-details">
                      <h4>{integration.name}</h4>
                      <div className="integration-summary-stats">
                        <span className="stat-item">
                          <span className="stat-label">Account:</span> {integration.username}
                        </span>
                        {integration.lastSync && (
                          <span className="stat-item">
                            <span className="stat-label">Last Sync:</span>{' '}
                            {formatDateUK(integration.lastSync)}
                          </span>
                        )}
                        {integration.matchedChildren !== undefined && (
                          <span className="stat-item">
                            <span className="stat-label">Children:</span> {Array.isArray(integration.matchedChildren) ? integration.matchedChildren.length : integration.matchedChildren} matched
                          </span>
                        )}
                      </div>
                      <div className="integration-summary-status">
                        <span className={`status-badge ${integration.syncEnabled ? 'active' : 'inactive'}`}>
                          {integration.syncEnabled ? '✓ Auto-sync enabled' : '⏸ Auto-sync disabled'}
                        </span>
                      </div>
                      <div className="integration-actions" style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => integration.id === 'socs' ? handleSOCSSync() : handleSync(integration)}
                          disabled={integration.id === 'socs' ? syncingSOCS : syncing}
                          className="view-btn"
                          style={{ flex: '1 1 auto', minWidth: '100px' }}
                        >
                          {(integration.id === 'socs' ? syncingSOCS : syncing) ? 'Syncing...' : 'Sync Now'}
                        </button>
                        {integration.id === 'socs' ? (
                          <button
                            onClick={() => setShowSOCSModal(true)}
                            className="view-btn"
                            style={{ flex: '1 1 auto', minWidth: '130px' }}
                          >
                            Update Setup
                          </button>
                        ) : integration.id === 'spond' ? (
                          <button
                            onClick={() => setShowSpondModal(true)}
                            className="view-btn"
                            style={{ flex: '1 1 auto', minWidth: '130px' }}
                          >
                            Update Setup
                          </button>
                        ) : (
                          <button
                            onClick={() => handleManageIntegration(integration)}
                            className="view-btn"
                            style={{ flex: '1 1 auto', minWidth: '130px' }}
                          >
                            Update Setup
                          </button>
                        )}
                        <button
                          onClick={() => handleDisconnectIntegration(integration)}
                          className="view-btn disconnect-btn"
                          style={{
                            flex: '1 1 auto',
                            minWidth: '130px'
                          }}
                        >
                          Disconnect
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {INTEGRATION_BUILDER_ENABLED && (<>
                {/* Build a new integration — also rendered in the "no integrations
                    yet" branch above; mirrored here so parents who already have at
                    least one integration can still discover the builder. */}
                <div className="integration-summary-card">
                  <div className="integration-summary-icon">
                    <span className="integration-letter">+</span>
                  </div>
                  <div className="integration-summary-details">
                    <h4>Build a new integration</h4>
                    <p style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                      Don't see your child's club app? Teach Cluubz to read it — log in inside a scan window and we'll figure out the rest.
                    </p>
                    <div className="integration-actions" style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                      <button
                        onClick={() => setShowIntegrationBuilder(true)}
                        className="view-btn"
                        style={{ flex: '1 1 auto' }}
                      >
                        ✨ Build a new integration
                      </button>
                    </div>
                  </div>
                </div>
                {/* Crowd Connectors search — mirrored from the "no integrations
                    yet" branch above so parents who already have at least one
                    integration can discover community-built ones. */}
                <div className="integration-summary-card" style={{ background: '#f7f7f8' }}>
                  <div className="integration-summary-icon">
                    <span className="integration-letter">🌐</span>
                  </div>
                  <div className="integration-summary-details">
                    <h4>Crowd Connectors</h4>
                    <p style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                      Search integrations other parents have built — Spond, Pitchero, TeamSnap and more. Enable and enter your login to sync fixtures automatically.
                    </p>
                    <input
                      value={crowdQuery}
                      onChange={(e) => setCrowdQuery(e.target.value)}
                      placeholder="Search connectors (e.g. spond, pitchero, teamsnap)…"
                      style={{ width: '100%', marginTop: '10px', padding: '8px 10px', border: '1px solid #ddd', borderRadius: '6px', boxSizing: 'border-box', fontSize: '13px' }}
                    />
                    {crowdError && <div style={{ color: '#c33', fontSize: '12px', marginTop: '6px' }}>{crowdError}</div>}
                    <div style={{ marginTop: '8px', maxHeight: '260px', overflowY: 'auto' }}>
                      {crowdLoading && <div style={{ fontSize: '12px', color: '#666' }}>Searching…</div>}
                      {!crowdLoading && crowdResults.length === 0 && (
                        <div style={{ fontSize: '12px', color: '#666' }}>{crowdQuery ? 'No connectors match.' : 'No connectors yet. Build one!'}</div>
                      )}
                      {crowdResults.map((c: any) => (
                        <div key={c.uuid} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {c.app_name || c.slug}
                              <span style={{ fontSize: '10px', color: '#888', marginLeft: '6px' }}>v{c.version}</span>
                              {c.enabled && <span style={{ background: '#0a7', color: '#fff', fontSize: '10px', padding: '1px 6px', borderRadius: '3px', marginLeft: '6px' }}>Enabled</span>}
                              {!c.enabled && c.success_count > 0 && <span style={{ background: '#eee', color: '#333', fontSize: '10px', padding: '1px 6px', borderRadius: '3px', marginLeft: '6px' }}>{c.success_count} syncs</span>}
                              {c.sync_mode === 'browser' && <span title="This site blocks our servers. You'll need to hit Sync Now yourself." style={{ background: '#fed7aa', color: '#9a3412', fontSize: '10px', padding: '1px 6px', borderRadius: '3px', marginLeft: '6px' }}>Manual sync</span>}
                            </div>
                            {c.enabled_last_sync_error && (
                              <div style={{ fontSize: '11px', color: '#c33' }}>⚠️ {c.enabled_last_sync_error}</div>
                            )}
                            {browserSyncStatus[c.uuid] && (
                              <div style={{ fontSize: '11px', color: browserSyncStatus[c.uuid].kind === 'ok' ? '#0a7' : browserSyncStatus[c.uuid].kind === 'err' ? '#c33' : '#666', marginTop: '2px' }}>{browserSyncStatus[c.uuid].msg}</div>
                            )}
                          </div>
                          {c.enabled && c.sync_mode === 'browser' && (
                            <button
                              onClick={() => runBrowserSync(c.uuid)}
                              className="view-btn"
                              style={{ fontSize: '12px', padding: '4px 10px', background: '#f97316', color: '#fff' }}
                              disabled={!!browserSyncStatus[c.uuid]?.running}
                            >
                              {browserSyncStatus[c.uuid]?.running ? '⏳ Syncing…' : '🔄 Sync now'}
                            </button>
                          )}
                          <button
                            onClick={() => openCrowdEnable(c)}
                            className="view-btn"
                            style={{ fontSize: '12px', padding: '4px 10px' }}
                          >
                            {c.enabled ? 'Reconfigure' : 'Enable'}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                </>)}
              </div>
            )}
          </>
        ))}
      </div>

      {/* Term Planner Section */}
      <div className="profile-card">
        <div className="collapsible-header" onClick={() => toggleSection('termPlanners')}>
          <h3><span className="collapse-chevron">{collapsedSections.termPlanners ? '▶' : '▼'}</span> Term Planners</h3>
        </div>

        {!collapsedSections.termPlanners && (loadingAllTermPlanners ? (
          <div className="empty-state">
            <p>Loading term planners...</p>
          </div>
        ) : children.filter(c => !c.is_parent).length === 0 ? (
          <div className="empty-state">
            <p>No children added yet</p>
          </div>
        ) : (
          <>
            {children.filter(c => !c.is_parent).map(child => {
              const planners = allTermPlanners[child.uuid] || [];
              return (
                <div key={child.uuid} style={{ marginBottom: '24px' }}>
                  <h4 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>
                    👤 {child.display_name || child.name}
                  </h4>
                  {planners.length === 0 ? (
                    <div style={{ padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px', marginBottom: '12px' }}>
                      <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>No term planners yet</p>
                    </div>
                  ) : (
                    <div style={{ marginBottom: '12px' }}>
                      {planners.map(planner => (
                        <div key={planner.uuid} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '12px',
                          backgroundColor: '#f8f9fa',
                          borderRadius: '8px',
                          marginBottom: '8px'
                        }}>
                          <div>
                            <div style={{ fontWeight: '600', marginBottom: '4px' }}>{planner.term_name}</div>
                            {/* Only the planner in force is marked. Current / Upcoming / Replaced /
                                Finished was a four-word vocabulary the parent never asked for, and the
                                name already carries the dates — so the one thing it cannot show is
                                which planner is running now. */}
                            {isPlannerLive(planner) && (
                              <div style={{
                                display: 'inline-block',
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#1b5e20',
                                backgroundColor: '#e8f5e9',
                                border: '1px solid #a5d6a7',
                                borderRadius: '10px',
                                padding: '1px 8px',
                                marginTop: '2px'
                              }}>
                                Active
                              </div>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <button
                              onClick={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setTermPlannerChild(child);
                                setShowTermPlannerModal(true);
                                setTermPlannerStep('list');
                                setLoadingTermPlanners(true);

                                // Load term planners for this child (for modal list view)
                                const primaryParent = parents.find((p: any) => p.is_primary) || parents[0];
                                if (primaryParent) {
                                  const response = await apiService.getTermPlanners(primaryParent.uuid, child.uuid);
                                  if (response.success && response.data) {
                                    setExistingTermPlanners(response.data);
                                  }
                                }

                                // Load the specific term planner for editing before hiding spinner
                                await handleEditTermPlanner(planner.uuid);
                                setLoadingTermPlanners(false);
                              }}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#007bff',
                                cursor: 'pointer',
                                padding: '8px 12px',
                                font: 'inherit',
                                textDecoration: 'underline',
                                minHeight: '44px',
                                minWidth: '44px',
                                touchAction: 'manipulation'
                              }}
                            >
                              Edit
                            </button>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDeleteTermPlanner(planner.uuid, planner.activity_count);
                              }}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#dc3545',
                                cursor: 'pointer',
                                padding: '8px 12px',
                                font: 'inherit',
                                textDecoration: 'underline',
                                minHeight: '44px',
                                minWidth: '44px',
                                touchAction: 'manipulation'
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    onClick={() => {
                      setTermPlannerChild(child);
                      setTermPlannerStep('intro');
                      setShowTermPlannerModal(true);
                      // Load THIS child's planners rather than relying on the list data
                      // happening to be in memory already. Without it the conflict check
                      // depends on a background load having finished, which is a race.
                      (async () => {
                        try {
                          const primaryParent = parents.find((p: any) => p.is_primary) || parents[0];
                          if (primaryParent) {
                            const response = await apiService.getTermPlanners(primaryParent.uuid, child.uuid);
                            if (response.success && response.data) setExistingTermPlanners(response.data);
                          }
                        } catch (error) {
                          console.error('Failed to load planners for conflict check:', error);
                        }
                      })();
                    }}
                    className="add-btn"
                    style={{ fontSize: '14px', padding: '8px 16px' }}
                  >
                    + Create New Term Planner for {child.name}
                  </button>
                </div>
              );
            })}
          </>
        ))}
      </div>

      {/* Notifications Section */}
      <div className="profile-card">
        <div className="collapsible-header" onClick={() => toggleSection('notifications')}>
          <h3><span className="collapse-chevron">{collapsedSections.notifications ? '▶' : '▼'}</span> Notifications</h3>
        </div>

        {!collapsedSections.notifications && (
          <div className="notification-prefs">
            {loadingNotifPrefs ? (
              <div className="empty-state"><p>Loading preferences...</p></div>
            ) : (
              <>
                <div className="notif-pref-row">
                  <div className="notif-pref-info">
                    <div className="notif-pref-title">Coverage Gap Alerts</div>
                    <div className="notif-pref-desc">Get notified the evening before when a child has a usual club day but nothing is booked</div>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={notifPrefs.coverage_gap}
                      onChange={() => handleToggleNotifPref('coverage_gap')}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                <div className="notif-pref-row">
                  <div className="notif-pref-info">
                    <div className="notif-pref-title">Daily Schedule Summary</div>
                    <div className="notif-pref-desc">Morning notification listing the day's activities for each child</div>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={notifPrefs.daily_summary}
                      onChange={() => handleToggleNotifPref('daily_summary')}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div className="actions-section">
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleChangePassword();
          }}
          className="action-item"
          style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
        >
          <span>Change Password</span>
          <span className="arrow">→</span>
        </button>

        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleLogout();
          }}
          className="action-item logout-action"
          style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
        >
          <span>Logout</span>
          <span className="arrow">→</span>
        </button>

        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowDeleteAccountModal(true);
            setDeleteAccountPassword('');
          }}
          className="action-item"
          style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent', color: '#dc3545', borderTop: '1px solid #eee', marginTop: '8px' }}
        >
          <span>Delete Account</span>
          <span className="arrow">→</span>
        </button>
      </div>

      <div className="legal-links">
        <a href="/privacy" className="legal-link">
          <span>Privacy Policy</span>
          <span className="arrow">→</span>
        </a>
        <a href="/support" className="legal-link">
          <span>Support</span>
          <span className="arrow">→</span>
        </a>
      </div>

      <div className="app-info">
        <p onClick={async () => {
          try {
            const localNotifService = LocalNotificationService.getInstance();
            const platform = Capacitor.getPlatform();
            const isNative = Capacitor.isNativePlatform();
            let info = `Platform: ${platform}\nNative: ${isNative}\n`;

            if (isNative && platform === 'ios') {
              try {
                const avail = await AlarmKit.isAvailable();
                info += `iOS: ${(avail as any).iosVersion || '?'}\nAlarmKit Available: ${avail.available}\n`;
                if (avail.available) {
                  const perm = await AlarmKit.checkPermission();
                  info += `AlarmKit Permission: ${perm.granted} (${perm.status})\n`;
                }
              } catch (e: any) {
                info += `AlarmKit Error: ${e?.message || e}\n`;
              }
            }

            const reminders = await localNotifService.getAllReminders();
            info += `\nStored Reminders: ${reminders.length}\n`;
            for (const r of reminders) {
              info += `\n${r.activityName}\n  Type: ${r.useAlarmKit ? 'ALARMKIT' : 'LOCAL NOTIF'}\n  Date: ${r.startDate} ${r.startTime}\n  Before: ${r.minutesBefore}m\n`;
            }

            if (isNative) {
              const pending = await localNotifService.getPendingNotifications();
              info += `\nPending Local Notifs: ${pending.length}`;
            }

            alert(info);
          } catch (e: any) {
            alert('Debug error: ' + (e?.message || e));
          }
        }} style={{ cursor: 'pointer' }}>Cluubz v1.0.0</p>
      </div>

      {/* Alarm Debug Modal */}
      {showAlarmDebug && (
        <div
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => setShowAlarmDebug(false)}
        >
          <div
            style={{
              background: '#1a1a2e',
              borderRadius: '12px',
              padding: '16px',
              width: '100%',
              maxWidth: '400px',
              maxHeight: '80vh',
              overflow: 'auto',
              fontSize: '12px',
              fontFamily: 'monospace',
              color: '#0f0'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <div style={{ color: '#fff', fontWeight: 'bold', fontSize: '14px' }}>Alarm Debug</div>
              <button onClick={() => setShowAlarmDebug(false)} style={{ background: 'none', border: 'none', color: '#888', fontSize: '20px', cursor: 'pointer' }}>x</button>
            </div>
            {alarmDebugLoading ? (
              <div>Loading...</div>
            ) : alarmDebugInfo ? (
              <>
                <div style={{ marginBottom: '8px', color: '#ff0', fontWeight: 'bold' }}>SYSTEM</div>
                <div>Platform: {alarmDebugInfo.platform}</div>
                <div>Native: {alarmDebugInfo.isNative ? 'YES' : 'NO'}</div>
                <div>iOS Version: {alarmDebugInfo.iosVersion}</div>
                <div style={{ marginTop: '8px', marginBottom: '8px', color: '#ff0', fontWeight: 'bold' }}>ALARMKIT</div>
                <div>Available: <span style={{ color: alarmDebugInfo.alarmKitAvailable ? '#0f0' : '#f00' }}>
                  {alarmDebugInfo.alarmKitAvailable ? 'YES' : 'NO'}
                </span></div>
                <div>Permission: <span style={{ color: alarmDebugInfo.alarmKitGranted ? '#0f0' : '#f00' }}>
                  {alarmDebugInfo.alarmKitGranted ? 'GRANTED' : 'NOT GRANTED'}
                </span></div>
                <div>Status: {alarmDebugInfo.alarmKitStatus}</div>
                <div style={{ marginTop: '8px', marginBottom: '8px', color: '#ff0', fontWeight: 'bold' }}>
                  PENDING LOCAL NOTIFS: {alarmDebugInfo.pendingLocalNotifs}
                </div>
                <div style={{ marginBottom: '8px', color: '#ff0', fontWeight: 'bold' }}>
                  STORED REMINDERS ({alarmDebugInfo.storedReminders.length})
                </div>
                {alarmDebugInfo.storedReminders.length === 0 ? (
                  <div style={{ color: '#888' }}>No stored reminders</div>
                ) : (
                  alarmDebugInfo.storedReminders.map((r, i) => (
                    <div key={i} style={{
                      marginBottom: '8px',
                      padding: '6px',
                      background: '#0d0d1a',
                      borderRadius: '4px',
                      borderLeft: `3px solid ${r.useAlarmKit ? '#0f0' : '#f00'}`
                    }}>
                      <div style={{ color: '#fff', fontWeight: 'bold' }}>{r.activityName}</div>
                      <div>Type: <span style={{ color: r.useAlarmKit ? '#0f0' : '#f00', fontWeight: 'bold' }}>
                        {r.useAlarmKit ? 'ALARMKIT' : 'LOCAL NOTIF'}
                      </span></div>
                      <div>Date: {r.startDate} {r.startTime}</div>
                      <div>Before: {r.minutesBefore}m | Target: {r.reminderTarget || 'start'}</div>
                      {r.childName && <div>Child: {r.childName}</div>}
                      {r.alarmId && <div style={{ fontSize: '10px', color: '#888' }}>AlarmID: {r.alarmId}</div>}
                      {r.notificationId > 0 && <div style={{ fontSize: '10px', color: '#888' }}>NotifID: {r.notificationId}</div>}
                    </div>
                  ))
                )}
                <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                  <button
                    onClick={() => loadAlarmDebugInfo()}
                    style={{
                      padding: '8px 16px',
                      background: '#333',
                      border: '1px solid #555',
                      borderRadius: '6px',
                      color: '#0f0',
                      fontSize: '13px',
                      cursor: 'pointer',
                      flex: 1
                    }}
                  >
                    Refresh
                  </button>
                  <button
                    onClick={async () => {
                      const localNotifService = LocalNotificationService.getInstance();
                      await localNotifService.purgeAllAlarms();
                      alert('All alarms purged. Re-open app to re-sync.');
                      loadAlarmDebugInfo();
                    }}
                    style={{
                      padding: '8px 16px',
                      background: '#440000',
                      border: '1px solid #880000',
                      borderRadius: '6px',
                      color: '#f00',
                      fontSize: '13px',
                      cursor: 'pointer',
                      flex: 1
                    }}
                  >
                    Purge All
                  </button>
                </div>
              </>
            ) : (
              <div style={{ color: '#888' }}>No data loaded</div>
            )}
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="modal-overlay" onMouseDown={handlePasswordCancel}>
          <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
            <h3>Change Password</h3>
            
            <div className="form-group">
              <label>Current Password</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="form-input"
                placeholder="Enter your current password"
              />
            </div>

            <div className="form-group">
              <label>New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="form-input"
                placeholder="Enter new password (min 6 characters)"
              />
            </div>

            <div className="form-group">
              <label>Confirm New Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="form-input"
                placeholder="Confirm new password"
              />
            </div>

            <div className="modal-actions">
              <button
                onClick={handlePasswordCancel}
                className="cancel-btn"
                disabled={passwordLoading}
              >
                Cancel
              </button>
              <button
                onClick={handlePasswordSubmit}
                disabled={passwordLoading}
                className={`confirm-btn ${passwordLoading ? 'disabled' : ''}`}
              >
                {passwordLoading ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Modal */}
      {showDeleteAccountModal && (
        <div className="modal-overlay" onMouseDown={() => { setShowDeleteAccountModal(false); setDeleteAccountPassword(''); }}>
          <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
            <h3 style={{ color: '#dc3545' }}>Delete Account</h3>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
              This action is <strong>permanent and cannot be undone</strong>. All your data including children, activities, connections, and integrations will be permanently deleted.
            </p>

            <div className="form-group">
              <label>Enter your password to confirm</label>
              <input
                type="password"
                value={deleteAccountPassword}
                onChange={(e) => setDeleteAccountPassword(e.target.value)}
                className="form-input"
                placeholder="Your current password"
              />
            </div>

            <div className="modal-actions">
              <button
                onClick={() => { setShowDeleteAccountModal(false); setDeleteAccountPassword(''); }}
                className="cancel-btn"
                disabled={deleteAccountLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteAccountLoading || !deleteAccountPassword}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: (deleteAccountLoading || !deleteAccountPassword) ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  backgroundColor: deleteAccountPassword ? '#dc3545' : '#ccc',
                  color: 'white',
                  opacity: deleteAccountLoading ? 0.7 : 1
                }}
              >
                {deleteAccountLoading ? 'Deleting...' : 'Delete My Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Parent Modal */}
      {showAddParentModal && (
        <div className="modal-overlay">
          <div
            className="modal"
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setShowAddParentModal(false);
              }
              e.stopPropagation();
            }}
          >
            <div className="modal-header">
              <h3>Add Parent to Account</h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setShowAddParentModal(false)}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            
            <div className="name-fields">
              <div className="form-group">
                <label>First Name</label>
                <input
                  type="text"
                  value={newParentFirstName}
                  onChange={(e) => setNewParentFirstName(e.target.value)}
                  className="form-input"
                  placeholder="First name"
                />
              </div>
              
              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  value={newParentLastName}
                  onChange={(e) => setNewParentLastName(e.target.value)}
                  className="form-input"
                  placeholder="Last name"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={newParentEmail}
                onChange={(e) => setNewParentEmail(e.target.value)}
                className="form-input"
                placeholder="Enter parent's email"
              />
            </div>

            <div className="form-group">
              <label>Phone</label>
              <PhoneInput
                value={newParentPhone}
                onChange={setNewParentPhone}
                className="form-input"
                placeholder="Enter parent's phone number"
              />
            </div>

            <div className="form-group">
              <label>Password</label>
              <div className="password-field">
                <input
                  type={showParentPassword ? "text" : "password"}
                  value={newParentPassword}
                  onChange={(e) => setNewParentPassword(e.target.value)}
                  className="form-input password-input"
                  placeholder="Enter password (min 6 characters)"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowParentPassword(!showParentPassword)}
                >
                  {showParentPassword ? '👁️' : '🙈'}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>Confirm Password</label>
              <div className="password-field">
                <input
                  type={showParentConfirmPassword ? "text" : "password"}
                  value={newParentConfirmPassword}
                  onChange={(e) => setNewParentConfirmPassword(e.target.value)}
                  className="form-input password-input"
                  placeholder="Confirm password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowParentConfirmPassword(!showParentConfirmPassword)}
                >
                  {showParentConfirmPassword ? '👁️' : '🙈'}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>Role</label>
              <select
                value={newParentRole}
                onChange={(e) => setNewParentRole(e.target.value)}
                className="form-input"
              >
                <option value="parent">Parent</option>
                <option value="guardian">Guardian</option>
                <option value="caregiver">Caregiver</option>
              </select>
            </div>

            <div className="modal-actions">
              <button
                onClick={() => setShowAddParentModal(false)}
                className="cancel-btn"
                disabled={addingParent}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateParent}
                disabled={addingParent}
                className={`confirm-btn ${addingParent ? 'disabled' : ''}`}
              >
                {addingParent ? 'Adding...' : 'Add Parent'}
              </button>
            </div>
          </div>
        </div>
      )}

      <AddressModal
        isOpen={showNewParentAddressModal}
        onClose={handleNewParentAddressModalClose}
        onSaved={handleNewParentAddressModalSaved}
        title={`${newParentDetails?.name || 'New Parent'} Added Successfully!`}
        description="Would you like to add their address information now? This helps with local activity suggestions. They can also add this themselves when they first log in."
        canSkip={true}
        parentEmail={newParentDetails?.email}
      />

      {/* Add Child Modal */}
      {showAddChildModal && (
        <div className="modal-overlay" onMouseDown={() => setShowAddChildModal(false)}>
          <div className="modal-content" onMouseDown={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Child</h2>
              <button
                className="close-btn"
                onClick={() => setShowAddChildModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  value={newChildFirstName}
                  onChange={(e) => setNewChildFirstName(e.target.value)}
                  className="form-input"
                  placeholder="Enter first name"
                />
              </div>

              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  value={newChildLastName}
                  onChange={(e) => setNewChildLastName(e.target.value)}
                  className="form-input"
                  placeholder="Enter last name (optional)"
                />
              </div>

              <div className="form-group">
                <label>Birth Date</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select
                    value={newChildBirthDay}
                    onChange={(e) => setNewChildBirthDay(e.target.value)}
                    className="form-input"
                    style={{ flex: 1 }}
                  >
                    <option value="">Day</option>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                      <option key={day} value={day}>{day}</option>
                    ))}
                  </select>
                  <select
                    value={newChildBirthMonth}
                    onChange={(e) => setNewChildBirthMonth(e.target.value)}
                    className="form-input"
                    style={{ flex: 1.5 }}
                  >
                    <option value="">Month</option>
                    <option value="1">January</option>
                    <option value="2">February</option>
                    <option value="3">March</option>
                    <option value="4">April</option>
                    <option value="5">May</option>
                    <option value="6">June</option>
                    <option value="7">July</option>
                    <option value="8">August</option>
                    <option value="9">September</option>
                    <option value="10">October</option>
                    <option value="11">November</option>
                    <option value="12">December</option>
                  </select>
                  <select
                    value={newChildBirthYear}
                    onChange={(e) => setNewChildBirthYear(e.target.value)}
                    className="form-input"
                    style={{ flex: 1 }}
                  >
                    <option value="">Year</option>
                    {Array.from({ length: 19 }, (_, i) => new Date().getFullYear() - i).map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Interests (Select all that apply)</label>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '10px',
                  marginTop: '8px'
                }}>
                  {INTEREST_TYPES.map(type => (
                    <label key={type} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        checked={newChildInterests.includes(type)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewChildInterests([...newChildInterests, type]);
                          } else {
                            setNewChildInterests(newChildInterests.filter(i => i !== type));
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                      />
                      <span>{type}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>School Name <span style={{ color: '#dc3545' }}>*</span></label>
                <input
                  type="text"
                  value={newChildSchoolVenue}
                  onChange={(e) => setNewChildSchoolVenue(e.target.value)}
                  className="form-input"
                  placeholder="e.g., St Mary's School"
                  required
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => {
                  setShowAddChildModal(false);
                  // Emit event so Dashboard can restore onboarding if needed
                  window.dispatchEvent(new CustomEvent('addChildCanceled'));
                }}
                className="cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleAddChild}
                disabled={savingChild}
                className="save-btn"
              >
                {savingChild ? 'Adding...' : 'Add Child'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Child Modal */}
      {showEditChildModal && (
        <div className="modal-overlay edit-child-profile-modal-overlay" onMouseDown={() => setShowEditChildModal(false)}>
          <div className="modal-content" onMouseDown={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Child</h2>
              <button
                className="close-btn"
                onClick={() => setShowEditChildModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  value={editChildFirstName}
                  onChange={(e) => setEditChildFirstName(e.target.value)}
                  className="form-input"
                  placeholder="Enter first name"
                />
              </div>

              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  value={editChildLastName}
                  onChange={(e) => setEditChildLastName(e.target.value)}
                  className="form-input"
                  placeholder="Enter last name (optional)"
                />
              </div>

              <div className="form-group">
                <label>Birth Date</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select
                    value={editChildBirthDay}
                    onChange={(e) => setEditChildBirthDay(e.target.value)}
                    className="form-input"
                    style={{ flex: 1 }}
                  >
                    <option value="">Day</option>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                      <option key={day} value={day}>{day}</option>
                    ))}
                  </select>
                  <select
                    value={editChildBirthMonth}
                    onChange={(e) => setEditChildBirthMonth(e.target.value)}
                    className="form-input"
                    style={{ flex: 1.5 }}
                  >
                    <option value="">Month</option>
                    <option value="1">January</option>
                    <option value="2">February</option>
                    <option value="3">March</option>
                    <option value="4">April</option>
                    <option value="5">May</option>
                    <option value="6">June</option>
                    <option value="7">July</option>
                    <option value="8">August</option>
                    <option value="9">September</option>
                    <option value="10">October</option>
                    <option value="11">November</option>
                    <option value="12">December</option>
                  </select>
                  <select
                    value={editChildBirthYear}
                    onChange={(e) => setEditChildBirthYear(e.target.value)}
                    className="form-input"
                    style={{ flex: 1 }}
                  >
                    <option value="">Year</option>
                    {Array.from({ length: 19 }, (_, i) => new Date().getFullYear() - i).map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Interests (Select all that apply)</label>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '10px',
                  marginTop: '8px'
                }}>
                  {INTEREST_TYPES.map(type => (
                    <label key={type} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        checked={editChildInterests.includes(type)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setEditChildInterests([...editChildInterests, type]);
                          } else {
                            setEditChildInterests(editChildInterests.filter(i => i !== type));
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                      />
                      <span>{type}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>School Name <span style={{ color: '#dc3545' }}>*</span></label>
                <input
                  type="text"
                  value={editChildSchoolVenue}
                  onChange={(e) => setEditChildSchoolVenue(e.target.value)}
                  className="form-input"
                  placeholder="e.g., St Mary's School"
                  required
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowEditChildModal(false)}
                className="cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateChild}
                disabled={updatingChild}
                className="save-btn"
              >
                {updatingChild ? 'Updating...' : 'Update Child'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Integration Credentials Modal */}
      {showCredentialsModal && selectedIntegration && (
        <div className="modal-overlay" onMouseDown={() => setShowCredentialsModal(false)}>
          <div
            className="modal"
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
          >
            <h3>{integrations.length === 0 ? `Connect ${selectedIntegration.name}` : `Update ${selectedIntegration.name} Credentials`}</h3>
            <p>{integrations.length === 0 ? `Enter your ${selectedIntegration.name} login credentials to connect` : `Update your ${selectedIntegration.name} login credentials`}</p>

            {selectedIntegration.id === 'pitchero' && (
              <>
                <div className="form-group">
                  <label>Username / Email</label>
                  <input
                    type="text"
                    value={pitcheroUsername}
                    onChange={(e) => setPitcheroUsername(e.target.value)}
                    className="form-input"
                    placeholder="Your Pitchero email"
                    autoComplete="username"
                  />
                </div>

                <div className="form-group">
                  <label>Password</label>
                  <div className="password-input-group">
                    <input
                      type={showPitcheroPassword ? 'text' : 'password'}
                      value={pitcheroPassword}
                      onChange={(e) => setPitcheroPassword(e.target.value)}
                      className="form-input"
                      placeholder="Your Pitchero password"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="toggle-password-btn"
                      onClick={() => setShowPitcheroPassword(!showPitcheroPassword)}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '18px'
                      }}
                    >
                      {showPitcheroPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                </div>

                <div style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
                  🔒 Your credentials are encrypted and stored securely.
                </div>

                <div className="modal-actions">
                  <button
                    onClick={() => {
                      setShowCredentialsModal(false);
                      setPitcheroUsername('');
                      setPitcheroPassword('');
                      setSelectedIntegration(null);
                    }}
                    className="cancel-btn"
                    disabled={savingCredentials}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSavePitcheroCredentials}
                    disabled={savingCredentials || !pitcheroUsername || !pitcheroPassword}
                    className="confirm-btn"
                  >
                    {savingCredentials
                      ? (integrations.length === 0 ? '⏳ Connecting...' : '⏳ Updating...')
                      : (integrations.length === 0 ? 'Connect Pitchero' : 'Update Credentials')}
                  </button>
                </div>
              </>
            )}

          </div>
        </div>
      )}

      {/* Manual Child Matching Modal */}
      {showManualMatching && (
        <div className="modal-overlay" onMouseDown={() => setShowManualMatching(false)}>
          <div className="modal modal-large" onMouseDown={(e) => e.stopPropagation()}>
            <h3>{isReviewMode ? '🎯 Review Child Mappings' : 'Match Children to Pitchero Profiles'}</h3>
            <p style={{ marginBottom: '20px', color: '#666' }}>
              {isReviewMode
                ? "We've automatically matched your Pitchero children with your Cluubz children. Please verify these are correct before importing activities."
                : "We found some children in Pitchero that we couldn't automatically match. Please select which child in your account corresponds to each Pitchero profile, or select \"Skip\" if they don't belong to you."}
            </p>

            <div style={{ marginBottom: '20px' }}>
              {(isReviewMode ? pitcheroMappings : unmatchedChildren).map((item: any, index: number) => {
                const pitcheroChild = isReviewMode ? {
                  pitcheroName: item.pitcheroChildName,
                  member_id: item.pitcheroMemberId,
                  mappingId: item.id,
                  confidenceScore: item.confidenceScore,
                  clubs: item.clubs || []
                } : item;

                const mappingKey = isReviewMode ? pitcheroChild.mappingId : pitcheroChild.member_id;

                return (
                <div
                  key={mappingKey}
                  style={{
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '12px',
                    backgroundColor: '#f9f9f9'
                  }}
                >
                  <div style={{ marginBottom: '8px' }}>
                    <strong style={{ fontSize: '16px' }}>👶 {pitcheroChild.pitcheroName}</strong>
                    {isReviewMode && pitcheroChild.confidenceScore && (
                      <span
                        style={{
                          marginLeft: '8px',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          backgroundColor:
                            pitcheroChild.confidenceScore >= 0.9
                              ? '#4CAF50'
                              : pitcheroChild.confidenceScore >= 0.7
                              ? '#FF9800'
                              : '#F44336',
                          color: 'white'
                        }}
                      >
                        {Math.round(pitcheroChild.confidenceScore * 100)}% match
                      </span>
                    )}
                  </div>

                  {pitcheroChild.clubs && pitcheroChild.clubs.length > 0 && isReviewMode && (
                    <div style={{
                      marginBottom: '12px',
                      padding: '8px 12px',
                      backgroundColor: '#f0f8ff',
                      borderRadius: '4px',
                      fontSize: '13px',
                      color: '#555'
                    }}>
                      <strong style={{ display: 'block', marginBottom: '6px' }}>🏟️ Import from these clubs:</strong>
                      {pitcheroChild.clubs.map((club: string) => {
                        const key = `${mappingKey}-${club}`;
                        const isExcluded = reviewExcludedChildren[mappingKey as number];
                        return (
                          <label key={key} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '4px 0',
                            cursor: isExcluded ? 'not-allowed' : 'pointer',
                            opacity: isExcluded ? 0.4 : 1
                          }}>
                            <input
                              type="checkbox"
                              checked={!isExcluded && (reviewSelectedClubs[key] !== false)}
                              disabled={isExcluded}
                              onChange={(e) => {
                                setReviewSelectedClubs(prev => ({
                                  ...prev,
                                  [key]: e.target.checked
                                }));
                              }}
                              style={{ width: '16px', height: '16px' }}
                            />
                            <span>{club}</span>
                          </label>
                        );
                      })}
                      {pitcheroChild.clubs.length > 1 && (
                        <label style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '6px 0 2px',
                          cursor: 'pointer',
                          borderTop: '1px solid #ddd',
                          marginTop: '4px',
                          fontSize: '12px',
                          color: '#999'
                        }}>
                          <input
                            type="checkbox"
                            checked={reviewExcludedChildren[mappingKey as number] || false}
                            onChange={(e) => {
                              setReviewExcludedChildren(prev => ({
                                ...prev,
                                [mappingKey as number]: e.target.checked
                              }));
                            }}
                            style={{ width: '14px', height: '14px' }}
                          />
                          <span>Skip this child entirely</span>
                        </label>
                      )}
                    </div>
                  )}
                  {pitcheroChild.clubs && pitcheroChild.clubs.length > 0 && !isReviewMode && (
                    <div style={{
                      marginBottom: '12px',
                      padding: '8px 12px',
                      backgroundColor: '#f0f8ff',
                      borderRadius: '4px',
                      fontSize: '13px',
                      color: '#555'
                    }}>
                      <strong>🏟️ Clubs:</strong> {pitcheroChild.clubs.join(', ')}
                    </div>
                  )}

                  <div style={{ marginTop: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500 }}>
                      Match to your child:
                    </label>
                    <select
                      value={childMatches[mappingKey] || ''}
                      onChange={async (e) => {
                        const newValue = e.target.value;
                        setChildMatches({
                          ...childMatches,
                          [mappingKey]: newValue
                        });

                        // If in review mode, update mapping immediately
                        if (isReviewMode && primaryParentUuid) {
                          try {
                            await apiService.updatePitcheroMapping(primaryParentUuid, pitcheroChild.mappingId, newValue);
                          } catch (error) {
                            console.error('Failed to update mapping:', error);
                          }
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '6px',
                        border: '1px solid #ccc',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    >
                      <option value="">-- Select a child --</option>
                      {cluubzChildren.map((child: any) => (
                        <option key={child.uuid} value={child.uuid}>
                          {child.name || `${child.first_name} ${child.last_name}`}
                        </option>
                      ))}
                      {!isReviewMode && <option value="skip">⏭️ Skip (not my child)</option>}
                    </select>
                  </div>
                </div>
              );
              })}
            </div>

            <div className="modal-actions">
              <button
                onClick={() => {
                  setShowManualMatching(false);
                  setChildMatches({});
                  setPitcheroMappings([]);
                  setIsReviewMode(false);
                  setReviewSelectedClubs({});
                  setReviewExcludedChildren({});
                }}
                className="cancel-btn"
                disabled={savingCredentials || syncing}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (isReviewMode) {
                    // Review mode: Import activities
                    if (!primaryParentUuid) {
                      alert('Error: Parent UUID not found');
                      return;
                    }

                    try {
                      setSyncing(true);
                      alert('📥 Importing activities...\n\nThis may take a moment as we import your fixtures and training sessions.');

                      // Build excluded clubs and child mapping IDs from selection state
                      const excludedClubs: { [mappingId: number]: string[] } = {};
                      const excludedChildMappingIds: number[] = [];

                      pitcheroMappings.forEach((mapping: any) => {
                        if (reviewExcludedChildren[mapping.id]) {
                          excludedChildMappingIds.push(mapping.id);
                          if (mapping.clubs) {
                            excludedClubs[mapping.id] = [...mapping.clubs];
                          }
                        } else if (mapping.clubs) {
                          const excluded = mapping.clubs.filter((club: string) =>
                            reviewSelectedClubs[`${mapping.id}-${club}`] === false
                          );
                          if (excluded.length > 0) {
                            excludedClubs[mapping.id] = excluded;
                          }
                        }
                      });

                      const importResponse = await apiService.importPitcheroActivities(
                        primaryParentUuid,
                        Object.keys(excludedClubs).length > 0 ? excludedClubs : undefined,
                        excludedChildMappingIds.length > 0 ? excludedChildMappingIds : undefined
                      );

                      if (importResponse.success) {
                        const activitiesCount = (importResponse.data?.activitiesCreated || 0) + (importResponse.data?.activitiesUpdated || 0);

                        alert(`✅ Import Complete!\n\n${activitiesCount} activities have been added to your calendar.\n\nYou'll receive notifications for upcoming fixtures and training sessions.`);

                        setShowManualMatching(false);
                        setChildMatches({});
                        setPitcheroMappings([]);
                        setIsReviewMode(false);
                        setReviewSelectedClubs({});
                        setReviewExcludedChildren({});
                        loadIntegrations(); // Refresh integrations list
                      } else {
                        alert(`Import failed: ${importResponse.error || 'Unknown error'}`);
                      }
                    } catch (error) {
                      console.error('Failed to import activities:', error);
                      alert('Failed to import activities. Please try again later.');
                    } finally {
                      setSyncing(false);
                    }
                  } else {
                    // Manual match mode: Save matches
                    handleSaveManualMatches();
                  }
                }}
                disabled={savingCredentials || syncing || (!isReviewMode && Object.keys(childMatches).length === 0)}
                className="confirm-btn"
              >
                {syncing || savingCredentials
                  ? '⏳ Processing...'
                  : isReviewMode
                  ? '✅ Approve & Import Activities'
                  : `Save ${Object.keys(childMatches).length} Match${Object.keys(childMatches).length !== 1 ? 'es' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SOCS Setup Modal */}
      <SOCSSetupModal
        isOpen={showSOCSModal}
        onClose={() => {
          setShowSOCSModal(false);
          loadIntegrations(); // Reload to refresh status
        }}
        children={children}
      />

      {/* School Portal Setup Modal */}
      <SchoolPortalSetupModal
        isOpen={showSchoolPortalModal}
        onClose={(completed) => {
          setShowSchoolPortalModal(false);
          // Always reload integrations after modal closes to ensure UI is in sync
          // This handles both completed setups and partial setups where credentials may have been saved
          loadIntegrations();
        }}
        children={children}
        suggestedEmails={integrationEmails}
        defaultEmail={user?.email || ''}
      />

      {/* Spond Setup Modal */}
      <SpondSetupModal
        isOpen={showSpondModal}
        onClose={(completed) => {
          setShowSpondModal(false);
          loadIntegrations();
        }}
        children={children}
        suggestedEmails={integrationEmails}
        defaultEmail={user?.email || ''}
      />

      {/* Build a New Integration — v2 guided flow */}
      {INTEGRATION_BUILDER_ENABLED && showIntegrationBuilder && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100, overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
             onClick={() => setShowIntegrationBuilder(false)}>
          <div style={{ background: '#fff', maxWidth: 560, margin: '20px', borderRadius: 12, boxShadow: '0 14px 50px rgba(0,0,0,0.3)', padding: 24 }}
               onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>✨ Build a new integration</h3>
              <button onClick={() => setShowIntegrationBuilder(false)} style={{ background: 'transparent', border: 0, fontSize: 22, cursor: 'pointer', color: '#6b7280' }}>×</button>
            </div>
            <p style={{ fontSize: 14, color: '#374151', marginBottom: 16 }}>
              You'll teach Cluubz how to read a new site in about 2 minutes. Once done, every parent whose child uses that site can enable it in one click.
            </p>
            <div style={{ background: '#f3f4f6', borderRadius: 8, padding: 14, marginBottom: 16 }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Step-by-step:</div>
              <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#374151', lineHeight: 1.7 }}>
                <li>Install the <b>Cluubz Capture</b> Chrome extension.</li>
                <li>Open a new tab and navigate to your club's website (Google it, or type the URL).</li>
                <li>Click the <b>Cluubz Capture</b> icon in your Chrome toolbar.</li>
                <li>Follow the on-screen wizard — it walks you through login, team selection, and testing.</li>
              </ol>
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>
              💡 You don't need to know the site's URL up front — browse to it however you like. The wizard picks up automatically once you land on the page.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <a href="https://chromewebstore.google.com/" target="_blank" rel="noopener noreferrer"
                 style={{ flex: 1, textAlign: 'center', textDecoration: 'none', background: '#1d4ed8', color: '#fff', padding: '10px 12px', borderRadius: 8, fontWeight: 500, fontSize: 13 }}>
                Get Chrome extension
              </a>
              <button onClick={() => setShowIntegrationBuilder(false)}
                      style={{ padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 13 }}>
                Got it
              </button>
            </div>
            <details style={{ marginTop: 16, fontSize: 12, color: '#6b7280' }}>
              <summary style={{ cursor: 'pointer' }}>Advanced (HAR-file upload)</summary>
              <div style={{ marginTop: 8 }}>
                <IntegrationBuilder onClose={() => { setShowIntegrationBuilder(false); loadIntegrations(); }} />
              </div>
            </details>
          </div>
        </div>
      )}

      {/* Crowd Connector Enable Modal */}
      {INTEGRATION_BUILDER_ENABLED && crowdEnableFor && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1200, overflow: 'auto' }}
             onClick={() => setCrowdEnableFor(null)}>
          <div style={{ background: '#fff', maxWidth: 560, margin: '40px auto', borderRadius: 10, padding: '20px', boxShadow: '0 14px 50px rgba(0,0,0,0.3)' }}
               onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ margin: 0 }}>Enable {crowdEnableFor.app_name || crowdEnableFor.slug}</h3>
              <button onClick={() => setCrowdEnableFor(null)} style={{ background: 'transparent', border: 0, fontSize: '20px', cursor: 'pointer' }}>×</button>
            </div>
            {/* STEP 1: Credentials + Probe. Runs a live scrape so the picker
                is built from THIS parent's actual response, not the publisher's
                stored sample. Credentials aren't saved until step 2. */}
            {!crowdPicker && (
              <>
                <div style={{ fontSize: '13px', color: '#333', marginBottom: '10px' }}>
                  {crowdEnableFor.requires_login === false
                    ? <>Step 1 — Fetching public fixtures from <b>{crowdEnableFor.app_name}</b>…</>
                    : <>Step 1 — Enter your <b>{crowdEnableFor.app_name}</b> login. We'll do one test fetch to see your team(s) then ask you to map them to your children.</>
                  }
                </div>
                {crowdEnableFor.requires_login !== false && <input
                  value={crowdCreds.username}
                  onChange={(e) => setCrowdCreds(v => ({ ...v, username: e.target.value }))}
                  placeholder="Email / username"
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: '6px', marginBottom: '6px', boxSizing: 'border-box' }}
                />}
                {crowdEnableFor.requires_login !== false && <input
                  value={crowdCreds.password}
                  onChange={(e) => setCrowdCreds(v => ({ ...v, password: e.target.value }))}
                  type="password"
                  placeholder="Password"
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: '6px', marginBottom: '10px', boxSizing: 'border-box' }}
                />}
                {crowdEnableResult && (
                  <div style={{ marginBottom: '10px', padding: '8px', background: '#fdecec', color: '#7a1f1f', borderRadius: '4px', fontSize: '13px' }}>
                    {crowdEnableResult}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '8px' }}>
                  {crowdEnableFor.requires_login !== false && <button
                    onClick={runCrowdProbe}
                    disabled={crowdBusy || !crowdCreds.username || !crowdCreds.password}
                    className="view-btn"
                    style={{ flex: 1 }}
                  >
                    {crowdBusy ? '⏳ Fetching your teams…' : 'Test login & fetch teams →'}
                  </button>}
                  <button onClick={() => setCrowdEnableFor(null)} style={{ padding: '6px 12px', border: '1px solid #ccc', borderRadius: '6px', background: '#fff', cursor: 'pointer' }}>Cancel</button>
                </div>
              </>
            )}
            {/* STEP 2: Mapping. Picker comes from the live probe response so
                every option shown is one this parent actually has access to. */}
            {crowdPicker && (
              <>
                <div style={{ fontSize: '13px', color: '#333', marginBottom: '10px' }}>
                  Step 2 — We found <b>{crowdPicker.rowsFetched} fixtures</b> for {crowdCreds.username}. Map each team to your Cluubz child then save.
                </div>
                {crowdPicker.identities?.length > 0 && (
                  <div style={{ maxHeight: '360px', overflowY: 'auto', marginBottom: '10px' }}>
                    <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '.5px', padding: '0 4px 6px' }}>
                      <div style={{ flex: 1 }}>From {crowdEnableFor.app_name || crowdEnableFor.slug}</div>
                      <div style={{ width: '20px' }}></div>
                      <div style={{ flex: 1 }}>Your Cluubz child</div>
                    </div>
                    {crowdPicker.identities.map((id: string) => {
                      const meta = crowdPicker.identityMeta?.[id] || {};
                      const hints = meta.memberHints || [];
                      // Left column: what the site returned. Show detected child
                      // name as primary (from behalfOfIds/self-hints), team name
                      // as secondary. Team-only rows show team as primary.
                      const primary = hints.length > 0 ? hints.map((h: any) => h.name).join(', ') : (meta.label || id);
                      const secondary = hints.length > 0 ? (meta.label || null) : null;
                      const suggested = crowdPicker.suggestions?.[id];
                      // % badge = confidence that the auto-picked Cluubz child
                      // is the right one for this scraped identity. If no
                      // suggestion, no badge — never fake a match.
                      const matchConfidence: number = suggested ? (crowdPicker.suggestionConfidence?.[id] || 0) : 0;
                      const badgeBg = matchConfidence >= 0.9 ? '#4CAF50' : matchConfidence >= 0.7 ? '#FF9800' : '#F44336';
                      const isMapped = !!crowdMapping[id];
                      return (
                        <div
                          key={id}
                          style={{
                            border: isMapped ? '1px solid #e0e0e0' : '1px solid #ccc',
                            borderRadius: '8px',
                            padding: '12px',
                            marginBottom: '10px',
                            backgroundColor: isMapped ? 'white' : '#f5f5f5',
                            opacity: isMapped ? 1 : 0.7,
                          }}
                        >
                          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                            {/* LEFT: scraped name from the API */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 600, color: isMapped ? '#111' : '#666', overflow: 'hidden', textOverflow: 'ellipsis' }}>{primary}</div>
                              {secondary && <div style={{ fontSize: '12px', color: '#666', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{secondary}</div>}
                              {matchConfidence > 0 && (
                                <span style={{ display: 'inline-block', marginTop: '6px', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', backgroundColor: badgeBg, color: 'white' }}>
                                  {Math.round(matchConfidence * 100)}% name match
                                </span>
                              )}
                              {meta.detected === 'confident' && (
                                <span style={{ marginLeft: '6px', fontSize: '10px', color: '#080' }} title="Detected from your login response — this is confirmed as your child by the site, but Cluubz still needs you to pick which Cluubz child they map to.">✅ your child on {crowdEnableFor.app_name}</span>
                              )}
                              {!suggested && meta.detected === 'confident' && (
                                <div style={{ marginTop: '6px', fontSize: '11px', color: '#c33' }}>
                                  No Cluubz child fuzzy-matched — pick manually.
                                </div>
                              )}
                            </div>
                            {/* ARROW */}
                            <div style={{ color: '#999', fontSize: '18px' }}>→</div>
                            {/* RIGHT: Cluubz child selector */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <select
                                value={crowdMapping[id] || ''}
                                onChange={(e) => setCrowdMapping(m => ({ ...m, [id]: e.target.value }))}
                                style={{ width: '100%', padding: '6px 10px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '13px' }}
                              >
                                <option value="">— Don't import —</option>
                                {(crowdPicker.children || []).map((k: any) => (
                                  <option key={k.uuid} value={k.uuid}>{k.name || `${k.first_name || ''} ${k.last_name || ''}`.trim()}</option>
                                ))}
                              </select>
                              {!isMapped && <div style={{ fontSize: '11px', color: '#c33', marginTop: '4px' }}>Fixtures will not be imported</div>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {(!crowdPicker.identities || crowdPicker.identities.length === 0) && (
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '6px' }}>
                    We couldn't detect distinct teams in the response — fixtures will apply to every child on your account. To add another specific team, use the Chrome extension's "+ Add my team" button.
                  </div>
                )}
                {(() => {
                  // Prefer live browser-sync progress if a sync is in flight
                  // for this connector; fall back to the one-shot enable result.
                  const bs = crowdEnableFor && browserSyncStatus[crowdEnableFor.uuid];
                  const msg = bs?.msg || crowdEnableResult;
                  if (!msg) return null;
                  const isErr = bs ? bs.kind === 'err' : !msg.startsWith('✅');
                  return (
                    <div style={{ marginTop: '10px', padding: '8px', background: isErr ? '#fdecec' : '#e8f7ef', borderRadius: '4px', fontSize: '13px' }}>
                      {msg}
                    </div>
                  );
                })()}
                <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => { setCrowdPicker(null); setCrowdEnableResult(null); }}
                    disabled={crowdBusy}
                    style={{ padding: '6px 12px', border: '1px solid #ccc', borderRadius: '6px', background: '#fff', cursor: 'pointer' }}
                  >
                    ← Back
                  </button>
                  <button
                    onClick={submitCrowdEnable}
                    disabled={crowdBusy}
                    className="view-btn"
                    style={{ flex: 1 }}
                  >
                    {crowdBusy ? '⏳ Working…' : 'Save & sync now'}
                  </button>
                  <button onClick={() => setCrowdEnableFor(null)} style={{ padding: '6px 12px', border: '1px solid #ccc', borderRadius: '6px', background: '#fff', cursor: 'pointer' }}>Close</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* SchoolsBuddy Setup Modal */}
      <SchoolsBuddySetupModal
        isOpen={showSchoolsBuddyModal}
        suggestedEmails={integrationEmails}
        defaultEmail={user?.email || ''}
        onClose={async (completed, termPlannerData, needsTermPlannerCreation) => {
          setShowSchoolsBuddyModal(false);
          if (completed) {
            // Show loading overlay immediately
            setLoadingTermPlannerTransition(true);
            console.log('🔵 SchoolsBuddy completed, term planner data:', termPlannerData, 'needsTermPlannerCreation:', needsTermPlannerCreation);

            try {
              await loadIntegrations(); // Reload to refresh status
              await loadAllTermPlanners(); // Reload term planners

              // Children that don't yet have an active term planner — auto-
              // create a term plan (default school-year dates + standard
              // school hours) and let the TP-CREATE-BG path refresh the
              // SchoolsBuddy cache + regenerate activities. We then poll
              // bg_sync_completed_at and open the editor directly so the
              // user sees their SchoolsBuddy clubs on first view, without
              // having to do a save round-trip themselves.
              if (needsTermPlannerCreation && needsTermPlannerCreation.length > 0) {
                const first = needsTermPlannerCreation[0];
                const child = children.find((c: any) => c.uuid === first.childUuid);
                const primaryParent = parents.find((p: any) => p.is_primary) || parents[0];
                if (child && primaryParent) {
                  const defaults = getDefaultPlannerDates();
                  const defaultStart = '08:45';
                  const defaultEnd = '15:30';
                  const weekdayShape = { drop_off: { time: defaultStart }, club_reminders: [], pick_up: { time: defaultEnd } };
                  const weekendShape = { drop_off: {}, club_reminders: [], pick_up: {} };
                  try {
                    const create = await apiService.createTermPlanner(primaryParent.uuid, {
                      child_uuid: child.uuid,
                      term_name: defaults.termName,
                      start_date: defaults.startDate,
                      end_date: defaults.endDate,
                      school_start_time: defaultStart,
                      school_end_time: defaultEnd,
                      daily_schedules: {
                        0: weekendShape,
                        1: weekdayShape, 2: weekdayShape, 3: weekdayShape, 4: weekdayShape, 5: weekdayShape,
                        6: weekendShape,
                      }
                    } as any);
                    const newUuid: string | undefined = create.success ? create.data?.uuid : undefined;
                    if (newUuid) {
                      // Wait for TP-CREATE-BG to land SB clubs before opening
                      // the editor — otherwise the editor opens against an
                      // unpopulated planner and the user sees nothing.
                      const startedAt = Date.now();
                      while (Date.now() - startedAt < 45000) {
                        try {
                          const s = await apiService.getTermPlannerBgStatus(newUuid);
                          if (s.success && s.data?.completed) break;
                        } catch { /* keep polling */ }
                        await new Promise(r => setTimeout(r, 1500));
                      }
                      await loadAllTermPlanners().catch(() => {});
                      setTermPlannerChild(child);
                      await handleEditTermPlanner(newUuid);
                      setLoadingTermPlannerTransition(false);
                      setShowTermPlannerModal(true);
                      return;
                    }
                  } catch (createErr) {
                    console.warn('Auto-create term planner after SB setup failed:', createErr);
                  }
                }
              }

              // If we have specific term planner data from the import, use it directly
              if (termPlannerData?.termPlannerUuid && termPlannerData?.childUuid) {
                console.log('🔵 Opening specific term planner:', termPlannerData.termPlannerUuid);
                const importedChild = children.find((c: any) => c.uuid === termPlannerData.childUuid);
                if (importedChild) {
                  setTermPlannerChild(importedChild);
                  await handleEditTermPlanner(termPlannerData.termPlannerUuid);
                  setTimeout(() => {
                    console.log('🔵 Opening term planner modal for imported term planner');
                    setLoadingTermPlannerTransition(false);
                    setShowTermPlannerModal(true);
                  }, 100);
                  return;
                }
              }

              // Fallback: Show the term planner for the first child that has one
              if (children.length > 0) {
                const primaryParent = parents.find((p: any) => p.is_primary) || parents[0];
                console.log('🔵 Primary parent:', primaryParent?.uuid, 'Children count:', children.length);
                if (primaryParent) {
                  // Find the first child with a term planner
                  for (const child of children) {
                    console.log('🔵 Checking child:', child.name, child.uuid);
                    const response = await apiService.getTermPlanners(primaryParent.uuid, child.uuid);
                    console.log('🔵 Term planners response:', response.success, response.data?.length);
                    if (response.success && response.data && response.data.length > 0) {
                      // Found a child with term planner - show it
                      console.log('🔵 Found term planner for child:', child.name);
                      setTermPlannerChild(child);
                      setExistingTermPlanners(response.data);
                      setTermPlannerStep('create'); // Set step before loading
                      // Load the most recent term planner for editing/viewing
                      const latestPlanner = response.data[0];
                      console.log('🔵 Loading term planner:', latestPlanner.uuid);
                      await handleEditTermPlanner(latestPlanner.uuid);
                      // Use setTimeout to ensure state updates have applied
                      setTimeout(() => {
                        console.log('🔵 Opening term planner modal');
                        setLoadingTermPlannerTransition(false);
                        setShowTermPlannerModal(true);
                      }, 100);
                      return; // Exit after finding term planner
                    }
                  }
                }
              }
              // No term planner found, hide loading
              setLoadingTermPlannerTransition(false);
            } catch (error) {
              console.error('Error loading term planner:', error);
              setLoadingTermPlannerTransition(false);
            }
          }
        }}
        children={children}
      />

      <PitcheroSetupModal
        isOpen={showPitcheroModal}
        onClose={(completed) => {
          setShowPitcheroModal(false);
          if (completed) {
            loadIntegrations(); // Reload to refresh status
          }
        }}
        parentUuid={user?.uuid || ''}
        suggestedEmails={integrationEmails}
        defaultEmail={user?.email || ''}
      />

      {/* Term Planner Full Page View */}
      {showTermPlannerModal && termPlannerChild && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: '#f5f7fa',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* Generating activities overlay */}
          {creatingTermPlanner && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255,255,255,0.92)',
              zIndex: 1010,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '16px'
            }}>
              <div className="loading-spinner" style={{ width: '40px', height: '40px' }}></div>
              <div style={{ fontSize: '16px', fontWeight: '600', color: '#333' }}>
                Generating activities...
              </div>
              <div style={{ fontSize: '13px', color: '#888' }}>
                Pulling in any connected clubs &amp; activities — this can take up to 30 seconds.
              </div>
            </div>
          )}

          {/* Full Page Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingTop: 'max(16px, env(safe-area-inset-top, 16px))',
            paddingBottom: '12px',
            paddingLeft: '16px',
            paddingRight: '16px',
            backgroundColor: 'white',
            borderBottom: '1px solid #e0e0e0',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            flexShrink: 0
          }}>
            <h2 style={{
              fontSize: 'clamp(16px, 4vw, 20px)',
              margin: 0,
              wordBreak: 'break-word',
              flex: 1
            }}>
              📅 Term Planner - {termPlannerChild.name}
            </h2>
            <button
              onClick={() => {
                if (hasUnsavedTermPlannerChanges) {
                  const confirmed = window.confirm('You have unsaved changes. Are you sure you want to exit? Your changes will be lost.');
                  if (!confirmed) return;
                }
                setShowTermPlannerModal(false);
                setHasUnsavedTermPlannerChanges(false);
              }}
              style={{
                background: '#f0f0f0',
                border: 'none',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '12px 16px',
                color: '#333',
                marginLeft: '12px',
                borderRadius: '8px',
                minWidth: '44px',
                minHeight: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ✕
            </button>
          </div>
          {/* Full Page Body */}
          <div ref={termPlannerBodyRef} style={{
            flex: 1,
            overflow: 'auto',
            padding: '16px',
            boxSizing: 'border-box'
          }}>
            <style>{`
              /* Hide body scroll when term planner modal is open */
              body {
                overflow: hidden !important;
              }
              .term-planner-page input,
              .term-planner-page select,
              .term-planner-page textarea {
                box-sizing: border-box !important;
                max-width: 100% !important;
              }
              .term-planner-page .form-group {
                max-width: 100%;
                box-sizing: border-box;
              }
            `}</style>
            <div className="term-planner-page" style={{ maxWidth: '800px', margin: '0 auto' }}>
              {termPlannerStep === 'intro' && (
                <>
                  <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                    <p style={{ fontSize: '16px', marginBottom: '20px' }}>
                      Would you like to set up a term planner for {termPlannerChild.name}?
                    </p>
                    <div style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: '12px',
                      padding: '20px',
                      color: 'white',
                      marginBottom: '20px'
                    }}>
                      <p style={{ fontWeight: 'bold', marginBottom: '15px' }}>This helps you:</p>
                      <ul style={{
                        textAlign: 'left',
                        listStyleType: 'none',
                        padding: 0,
                        margin: 0
                      }}>
                        <li style={{ marginBottom: '10px' }}>✓ Remember drop-off and pick-up times</li>
                        <li style={{ marginBottom: '10px' }}>✓ Know which uniform is needed each day</li>
                        <li style={{ marginBottom: '10px' }}>✓ Get reminders for clubs and kit</li>
                        <li>✓ See your full schedule at a glance</li>
                      </ul>
                    </div>
                  </div>
                  <button
                    onClick={handleSetupTermPlanner}
                    className="primary-button"
                    style={{
                      width: '100%',
                      marginBottom: '10px',
                      padding: '12px',
                      fontSize: '16px'
                    }}
                  >
                    Yes, Set Up Term Planner
                  </button>
                  <button
                    onClick={handleSkipTermPlanner}
                    className="secondary-button"
                    style={{
                      width: '100%',
                      padding: '12px',
                      fontSize: '16px',
                      background: '#f5f5f5',
                      border: '1px solid #ddd',
                      color: '#666'
                    }}
                  >
                    Skip for Now
                  </button>
                </>
              )}
              {termPlannerStep === 'setup' && (
                <>
                  {/* Green child name banner */}
                  <div style={{
                    backgroundColor: '#28a745',
                    color: 'white',
                    padding: '16px 20px',
                    borderRadius: '12px',
                    marginBottom: '24px',
                    textAlign: 'center',
                    boxShadow: '0 2px 8px rgba(40, 167, 69, 0.3)'
                  }}>
                    <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '4px' }}>
                      Setting up term planner for
                    </div>
                    <div style={{ fontSize: '22px', fontWeight: '700' }}>
                      {termPlannerChild.name || `${termPlannerChild.first_name} ${termPlannerChild.last_name}`}
                    </div>
                  </div>

                  <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                    {/* Planners are open-ended. The first one starts today, so there
                        is nothing to ask. A subsequent one replaces the live planner,
                        so we ask only for the single date the parent actually knows:
                        when the new term starts. Past dates are blocked — superseding
                        clears activities from the start date forward. */}
                    {/* The start date is ALWAYS asked for, even for a first planner and even
                        when nothing is running. A parent may well be setting next term up in
                        advance, and defaulting silently to today took that away from them.
                        Today remains the default, so the common case is still one tap. */}
                    <h4 style={{ marginTop: 0, marginBottom: '12px', fontSize: '16px' }}>📅 When does this planner start?</h4>
                    <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                      {outgoingPlanner
                        ? `It takes over from this date, and "${outgoingPlanner.term_name}" ends the day before.`
                        : 'Defaults to today. Pick a later date if you are setting up a term in advance.'}
                    </p>
                    {upcomingPlanner && (
                      <p style={{ fontSize: '13px', color: '#1565c0', marginBottom: '12px' }}>
                        Note: "{upcomingPlanner.term_name}" is already set to start {formatDateUK(upcomingPlanner.start_date)}.
                        This planner will run up to the day before that.
                      </p>
                    )}
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                        Planner starts:
                      </label>
                      <input
                        type="date"
                        value={termStartDate}
                        min={todayForInput()}
                        onChange={(e) => {
                          setTermStartDate(e.target.value);
                          if (e.target.value) setTermName(plannerNameFromDates(e.target.value, ''));
                        }}
                        style={{
                          width: '100%',
                          padding: '8px',
                          borderRadius: '4px',
                          border: '1px solid #ccc',
                          fontSize: '14px',
                          boxSizing: 'border-box',
                          maxWidth: '100%',
                          WebkitAppearance: 'none'
                        }}
                      />
                      {termStartDate && (
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                          {formatDateUK(termStartDate)}
                        </div>
                      )}
                    </div>

                    <h4 style={{ marginTop: '16px', marginBottom: '12px', fontSize: '16px' }}>🏫 Default School Hours</h4>
                    <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                      These times will be used as default drop-off and pick-up times. You can override them with clubs on specific days.
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                          School Start (Drop-off):
                        </label>
                        <TimePickerDropdown
                          value={schoolStartTime}
                          onChange={setSchoolStartTime}
                          defaultScrollPosition="start"
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                          School End (Pick-up):
                        </label>
                        <TimePickerDropdown
                          value={schoolEndTime}
                          onChange={setSchoolEndTime}
                          defaultScrollPosition="end"
                        />
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={handleProceedToSchedule}
                    className="primary-button"
                    disabled={!termStartDate}
                    style={{
                      width: '100%',
                      padding: '12px',
                      fontSize: '16px',
                      opacity: !termStartDate ? 0.5 : 1
                    }}
                  >
                    Continue to Schedule →
                  </button>
                </>
              )}
              {termPlannerStep === 'list' && (
                <>
                  {loadingTermPlanners ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px' }}>
                      <div className="loading-spinner" style={{ width: '30px', height: '30px' }}></div>
                    </div>
                  ) : existingTermPlanners.length > 0 ? (
                    <>
                      <p style={{ marginBottom: '20px' }}>
                        Manage term planners for {termPlannerChild.name}. Each planner generates the recurring activities for its dates.
                      </p>

                      <div style={{ marginBottom: '20px' }}>
                        {existingTermPlanners.map((term: any) => (
                          <div key={term.uuid} className="parent-card">
                            <div className="parent-info">
                              <div className="parent-details">
                                <h4>{term.term_name}</h4>
                                <p>
                                  {formatDateUK(term.start_date)} - {formatDateUK(term.end_date)}
                                </p>
                              </div>
                            </div>
                            <div className="child-actions">
                              <button
                                onClick={() => handleEditTermPlanner(term.uuid)}
                                className="edit-btn"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteTermPlanner(term.uuid)}
                                className="delete-btn"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>

                      <button
                        onClick={handleCreateNewTerm}
                        className="primary-button"
                        style={{ width: '100%' }}
                      >
                        + Create New Term Planner
                      </button>
                    </>
                  ) : (
                    <>
                      <p style={{ marginBottom: '20px', textAlign: 'center' }}>
                        No term planners created yet for {termPlannerChild.name}.
                      </p>
                      <p style={{ marginBottom: '20px', textAlign: 'center', color: '#666' }}>
                        Set up your child's school routine with drop-off/pick-up times, uniform requirements,
                        and club reminders. Activities are generated automatically for the planner's dates.
                      </p>
                      <button
                        onClick={handleCreateNewTerm}
                        className="primary-button"
                        style={{ width: '100%' }}
                      >
                        Create Term Planner
                      </button>
                    </>
                  )}
                </>
              )}

              {termPlannerStep === 'create' && (
                <>
                  <div style={{ marginBottom: '20px' }}>
                    <button
                      onClick={() => setTermPlannerStep(editingTermPlannerUuid ? 'list' : 'setup')}
                      style={{ background: 'none', border: 'none', color: '#007bff', cursor: 'pointer', padding: '0', fontSize: '14px' }}
                    >
                      ← Back
                    </button>
                  </div>

                  {/* Term Summary - Only show when not editing */}
                  {!editingTermPlannerUuid && (
                    <div style={{
                      padding: '16px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: '8px',
                      marginBottom: '20px',
                      color: 'white'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <h4 style={{ margin: 0, fontSize: '16px' }}>📅 {termName}</h4>
                        <button
                          onClick={() => setTermPlannerStep('setup')}
                          style={{
                            background: 'rgba(255,255,255,0.2)',
                            border: 'none',
                            color: 'white',
                            padding: '4px 10px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Edit
                        </button>
                      </div>
                      <div style={{ fontSize: '13px', opacity: 0.9 }}>
                        {termStartDate && termEndDate && (
                          <span>{formatDateUK(termStartDate)} - {formatDateUK(termEndDate)}</span>
                        )}
                      </div>
                      <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '8px' }}>
                        🏫 Default times: Drop-off {schoolStartTime} | Pick-up {schoolEndTime}
                      </div>
                      <div style={{ fontSize: '11px', opacity: 0.7, marginTop: '4px' }}>
                        Add clubs below to override times on specific days
                      </div>
                    </div>
                  )}

                  {/* Term Details - Only show when editing existing */}
                  {editingTermPlannerUuid && (
                    <>
                      <div style={{
                        padding: '18px',
                        background: '#f8f9fa',
                        borderRadius: '8px',
                        marginBottom: '25px',
                        border: '1px solid #dee2e6'
                      }}>
                        <div style={{ fontSize: '15px', fontWeight: 'bold', marginBottom: '12px', color: '#495057' }}>
                          📅 Term Dates
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={{ fontWeight: '500' }}>Term Starts</label>
                            <input
                              type="date"
                              value={termStartDate}
                              onChange={(e) => {
                                setTermStartDate(e.target.value);
                                if (e.target.value) setTermName(plannerNameFromDates(e.target.value, termEndDate));
                              }}
                              style={{
                                width: '100%',
                                padding: '10px',
                                marginTop: '5px',
                                fontSize: '14px',
                                borderRadius: '6px',
                                border: '1px solid #ced4da',
                                boxSizing: 'border-box',
                                maxWidth: '100%',
                                WebkitAppearance: 'none'
                              }}
                            />
                            {/* The native picker draws its own text in the BROWSER's locale, which
                                shows 18 August as 08/18/2026 on a US-configured device and cannot be
                                overridden. Echoing the date unambiguously removes the doubt. */}
                            {termStartDate && (
                              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                {formatDateUK(termStartDate)}
                              </div>
                            )}
                          </div>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={{ fontWeight: '500' }}>Term Ends</label>
                            <input
                              type="date"
                              value={termEndDate}
                              onChange={(e) => {
                                setTermEndDate(e.target.value);
                                if (termStartDate) setTermName(plannerNameFromDates(termStartDate, e.target.value));
                              }}
                              style={{
                                width: '100%',
                                padding: '10px',
                                marginTop: '5px',
                                fontSize: '14px',
                                borderRadius: '6px',
                                border: '1px solid #ced4da',
                                boxSizing: 'border-box',
                                maxWidth: '100%',
                                WebkitAppearance: 'none'
                              }}
                            />
                            {/* The native picker draws its own text in the BROWSER's locale, which
                                shows 18 August as 08/18/2026 on a US-configured device and cannot be
                                overridden. Echoing the date unambiguously removes the doubt. */}
                            {termEndDate && (
                              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                {formatDateUK(termEndDate)}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Day Tabs */}
                  <div ref={dailyScheduleRef} style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '20px', justifyContent: 'center' }}>
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => {
                      const dayData = dailySchedules[index] || {};
                      const hasData = (dayData.drop_off?.manualOneOffs?.length > 0) ||
                        (dayData.drop_off?.clubs?.length > 0) ||
                        (dayData.pick_up?.manualOneOffs?.length > 0) ||
                        (dayData.pick_up?.clubs?.length > 0) ||
                        (dayData.club_reminders?.length > 0);
                      return (
                      <button
                        key={index}
                        onClick={() => setCurrentDayTab(index)}
                        style={{
                          padding: '6px 10px',
                          border: currentDayTab === index ? '2px solid #007bff' : hasData ? '2px solid #28a745' : '1px solid #ccc',
                          background: currentDayTab === index ? '#007bff' : 'white',
                          color: currentDayTab === index ? 'white' : 'black',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          fontSize: '13px',
                          whiteSpace: 'nowrap',
                          minWidth: '40px',
                          position: 'relative'
                        }}
                      >
                        {day}
                        {hasData && currentDayTab !== index && <span style={{ position: 'absolute', top: '-3px', right: '-3px', width: '8px', height: '8px', borderRadius: '50%', background: '#28a745' }} />}
                      </button>
                      );
                    })}
                  </div>

                  {/* Daily Schedule Form */}
                  {(() => {
                    const currentDay = dailySchedules[currentDayTab] || { drop_off: {}, club_reminders: [], pick_up: {} };

                    return (
                      <div style={{ textAlign: 'left' }}>
                        <h3 style={{ marginBottom: '15px' }}>{dayNames[currentDayTab]}</h3>

                        {/* Drop-off */}
                        <div style={{ marginBottom: '15px', padding: '15px', backgroundColor: '#e8f5e9', border: '1px solid #c8e6c9', borderRadius: '8px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <h4 style={{ margin: 0, color: '#2e7d32' }}>Drop-off</h4>
                            {((currentDay.drop_off?.clubs && currentDay.drop_off.clubs.length > 0) ||
                              (currentDay.drop_off?.manualOneOffs && currentDay.drop_off.manualOneOffs.length > 0) ||
                              currentDay.drop_off?.club_name) && (
                              <button
                                onClick={() => {
                                  setDeleteScheduleCategory('drop_off');
                                  setShowDeleteScheduleModal(true);
                                }}
                                style={{
                                  padding: '4px 10px',
                                  backgroundColor: '#dc3545',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                              >
                                Clear Schedule
                              </button>
                            )}
                          </div>

                          {/* Add Club Button - shows "Replace" when nothing configured, "Add Another" when clubs/one-offs exist */}
                          <button
                            onClick={() => {
                              setReplaceClubType('drop_off');
                              setReplaceClubSelected(null);
                              setReplaceClubStartTime('');
                              setReplaceClubEndTime('');
                              setReplaceClubKitItems([]);
                              setReplaceClubUniform(currentDay.drop_off?.uniform_items || []);
                              setReplaceClubDesignatedParent(currentDay.drop_off?.designated_parent_uuid || '');
                              setReplaceClubReminderMinutes(currentDay.drop_off?.reminder_minutes ?? null);
                              setShowReplaceClubModal(true);
                            }}
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              marginBottom: '15px',
                              backgroundColor: '#17a2b8',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '8px'
                            }}
                          >
                            {(currentDay.drop_off?.clubs?.length > 0 || currentDay.drop_off?.manualOneOffs?.length > 0 || currentDay.drop_off?.notes) ? '➕ Add Another' : 'Replace'}
                          </button>
                          {/* Only show full form fields when no clubs/manualOneOffs exist (they have their own UI) */}
                          {(!currentDay.drop_off?.clubs || currentDay.drop_off.clubs.length === 0) && (!currentDay.drop_off?.manualOneOffs || currentDay.drop_off.manualOneOffs.length === 0) && (
                          <>
                          <div className="form-group">
                            <label>Time</label>
                            <TimePickerDropdown
                              value={currentDay.drop_off?.time || ''}
                              onChange={(time) => updateDaySchedule(currentDayTab, 'drop_off', { ...currentDay.drop_off, time })}
                              placeholder="Select drop-off time"
                              defaultScrollPosition="start"
                            />
                          </div>
                          <div className="form-group">
                            <label>🚗 Designated Parent (Optional)</label>
                            <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px', marginTop: '4px' }}>
                              Select the parent responsible for drop-off on {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][currentDayTab]}s
                            </p>
                            <select
                              value={currentDay.drop_off?.designated_parent_uuid || ''}
                              onChange={(e) => updateDaySchedule(currentDayTab, 'drop_off', { ...currentDay.drop_off, designated_parent_uuid: e.target.value || undefined })}
                              style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '4px',
                                border: '1px solid #ddd',
                                fontSize: '14px',
                                backgroundColor: 'white',
                                cursor: 'pointer',
                                marginTop: '5px'
                              }}
                            >
                              <option value="">Not assigned</option>
                              {parents.map((parent) => (
                                <option key={parent.uuid} value={parent.uuid}>
                                  {parent.username} ({parent.email}){parent.is_primary ? ' - Primary' : ''}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="form-group">
                            <label>🔔 Reminder (Optional)</label>
                            <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px', marginTop: '4px' }}>
                              Get reminded before drop-off
                            </p>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px', color: '#4a5568' }}>
                              Remind me for:
                            </label>
                            <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                              {([
                                { label: 'Start Time', value: 'start' as const },
                                { label: 'End Time', value: 'end' as const },
                                { label: 'Both', value: 'both' as const }
                              ]).map((opt) => (
                                <button
                                  key={opt.value}
                                  type="button"
                                  onClick={() => updateDaySchedule(currentDayTab, 'drop_off', { ...currentDay.drop_off, reminder_target: opt.value })}
                                  style={{
                                    flex: 1, padding: '10px 8px', fontSize: '13px', fontWeight: '600',
                                    color: currentDay.drop_off?.reminder_target === opt.value ? 'white' : '#4a5568',
                                    background: currentDay.drop_off?.reminder_target === opt.value ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'white',
                                    border: currentDay.drop_off?.reminder_target === opt.value ? '2px solid #667eea' : '2px solid #e2e8f0',
                                    borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s ease'
                                  }}
                                >
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                            <select
                              value={currentDay.drop_off?.reminder_minutes ?? ''}
                              onChange={(e) => updateDaySchedule(currentDayTab, 'drop_off', { ...currentDay.drop_off, reminder_minutes: e.target.value ? parseInt(e.target.value) : null })}
                              style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '4px',
                                border: '1px solid #ddd',
                                fontSize: '14px',
                                backgroundColor: 'white',
                                cursor: 'pointer',
                                marginTop: '5px'
                              }}
                            >
                              <option value="">No reminder</option>
                              {REMINDER_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="form-group">
                            <label>School Uniform</label>
                            <div style={{ marginTop: '10px' }}>
                              <div style={{ marginBottom: '10px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: '8px' }}>
                                  <input
                                    type="checkbox"
                                    checked={(currentDay.drop_off?.uniform_items || []).includes('Smart Uniform')}
                                    onChange={(e) => {
                                      const items = currentDay.drop_off?.uniform_items || [];
                                      const newItems = e.target.checked
                                        ? [...items, 'Smart Uniform']
                                        : items.filter((item: string) => item !== 'Smart Uniform');
                                      updateDaySchedule(currentDayTab, 'drop_off', { ...currentDay.drop_off, uniform_items: newItems });
                                    }}
                                    style={{ marginRight: '8px' }}
                                  />
                                  Smart Uniform
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: '8px' }}>
                                  <input
                                    type="checkbox"
                                    checked={(currentDay.drop_off?.uniform_items || []).includes('PE Kit')}
                                    onChange={(e) => {
                                      const items = currentDay.drop_off?.uniform_items || [];
                                      const newItems = e.target.checked
                                        ? [...items, 'PE Kit']
                                        : items.filter((item: string) => item !== 'PE Kit');
                                      updateDaySchedule(currentDayTab, 'drop_off', { ...currentDay.drop_off, uniform_items: newItems });
                                    }}
                                    style={{ marginRight: '8px' }}
                                  />
                                  PE Kit
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: '8px' }}>
                                  <input
                                    type="checkbox"
                                    checked={(currentDay.drop_off?.uniform_items || []).includes('Home Clothes')}
                                    onChange={(e) => {
                                      const items = currentDay.drop_off?.uniform_items || [];
                                      const newItems = e.target.checked
                                        ? [...items, 'Home Clothes']
                                        : items.filter((item: string) => item !== 'Home Clothes');
                                      updateDaySchedule(currentDayTab, 'drop_off', { ...currentDay.drop_off, uniform_items: newItems });
                                    }}
                                    style={{ marginRight: '8px' }}
                                  />
                                  Home Clothes
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: '8px' }}>
                                  <input
                                    type="checkbox"
                                    checked={(currentDay.drop_off?.uniform_items || []).includes('Costume')}
                                    onChange={(e) => {
                                      const items = currentDay.drop_off?.uniform_items || [];
                                      const newItems = e.target.checked
                                        ? [...items, 'Costume']
                                        : items.filter((item: string) => item !== 'Costume');
                                      updateDaySchedule(currentDayTab, 'drop_off', { ...currentDay.drop_off, uniform_items: newItems });
                                    }}
                                    style={{ marginRight: '8px' }}
                                  />
                                  Costume
                                </label>
                              </div>
                              <div style={{ display: 'flex', gap: '5px', marginTop: '10px' }}>
                                <input
                                  type="text"
                                  placeholder="Custom item (e.g., Swimming Kit)"
                                  id={`profile-custom-uniform-${currentDayTab}`}
                                  style={{ flex: 1, padding: '8px' }}
                                />
                                <button
                                  onClick={() => {
                                    const input = document.getElementById(`profile-custom-uniform-${currentDayTab}`) as HTMLInputElement;
                                    if (input && input.value.trim()) {
                                      const items = currentDay.drop_off?.uniform_items || [];
                                      updateDaySchedule(currentDayTab, 'drop_off', {
                                        ...currentDay.drop_off,
                                        uniform_items: [...items, input.value.trim()]
                                      });
                                      input.value = '';
                                    }
                                  }}
                                  style={{ padding: '8px 15px', background: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                >
                                  Add
                                </button>
                              </div>
                              {currentDay.drop_off?.uniform_items && currentDay.drop_off.uniform_items.length > 0 && (
                                <div style={{ marginTop: '10px' }}>
                                  {currentDay.drop_off.uniform_items.map((item: string, idx: number) => (
                                    <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 10px', background: '#e9ecef', borderRadius: '4px', marginBottom: '5px' }}>
                                      <span>{item}</span>
                                      <button
                                        onClick={() => {
                                          const items = currentDay.drop_off?.uniform_items || [];
                                          updateDaySchedule(currentDayTab, 'drop_off', {
                                            ...currentDay.drop_off,
                                            uniform_items: items.filter((_: string, i: number) => i !== idx)
                                          });
                                        }}
                                        style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', padding: '0 5px', fontSize: '18px' }}
                                      >
                                        ×
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          </>
                          )}
                          {/* Drop-off Schedule - shows clubs and/or one-offs */}
                          {(currentDay.drop_off?.notes || (currentDay.drop_off?.manualOneOffs && currentDay.drop_off.manualOneOffs.length > 0) || (currentDay.drop_off?.clubs && currentDay.drop_off.clubs.length > 0)) && (
                            <IrregularActivitiesDisplay
                              notes={currentDay.drop_off?.notes || ''}
                              termStartDate={termStartDate}
                              termEndDate={termEndDate}
                              dayOfWeek={currentDayTab}
                              label="📅 Drop-off Schedule"
                              backgroundColor="#fff3cd"
                              borderColor="#ffc107"
                              textColor="#856404"
                              activeIntegration={integrations.find(i => i.id === 'schoolsbuddy') || null}
                              manualOneOffs={currentDay.drop_off?.manualOneOffs || []}
                              defaultClubs={(currentDay.drop_off?.clubs || []).map((c: any) => ({
                                name: c.name,
                                startTime: c.startTime,
                                endTime: c.endTime,
                                kit_items: c.kit_items,
                                designated_parent_uuid: c.designated_parent_uuid,
                                reminder_minutes: c.reminder_minutes,
                                excludedDates: c.excludedDates || []
                              }))}
                              onAddManually={(date, dateStr) => {
                                setOneOffDate(date);
                                setOneOffDateStr(dateStr);
                                setOneOffType('drop_off');
                                setOneOffTime('');
                                setOneOffSelectedClub(null);
                                setShowOneOffClubModal(true);
                              }}
                              onRemoveManualOneOff={(dateStr) => {
                                setDailySchedules((prev: any) => {
                                  const dayData = prev[currentDayTab] || {};
                                  const dropOffData = dayData.drop_off || {};
                                  const existingOneOffs = dropOffData.manualOneOffs || [];
                                  return {
                                    ...prev,
                                    [currentDayTab]: {
                                      ...dayData,
                                      drop_off: {
                                        ...dropOffData,
                                        manualOneOffs: existingOneOffs.filter((o: ManualOneOff) => o.dateStr !== dateStr)
                                      }
                                    }
                                  };
                                });
                              }}
                              onRemoveDefaultClub={(clubName) => {
                                setDailySchedules((prev: any) => {
                                  const dayData = prev[currentDayTab] || {};
                                  const dropOffData = dayData.drop_off || {};
                                  const existingClubs = dropOffData.clubs || [];
                                  return {
                                    ...prev,
                                    [currentDayTab]: {
                                      ...dayData,
                                      drop_off: {
                                        ...dropOffData,
                                        clubs: existingClubs.filter((c: any) => c.name !== clubName)
                                      }
                                    }
                                  };
                                });
                              }}
                              onExcludeDateFromClub={(clubName, dateStr) => {
                                setDailySchedules((prev: any) => {
                                  const dayData = prev[currentDayTab] || {};
                                  const dropOffData = dayData.drop_off || {};
                                  const existingClubs = dropOffData.clubs || [];
                                  return {
                                    ...prev,
                                    [currentDayTab]: {
                                      ...dayData,
                                      drop_off: {
                                        ...dropOffData,
                                        clubs: existingClubs.map((c: any) =>
                                          c.name === clubName
                                            ? { ...c, excludedDates: [...(c.excludedDates || []), dateStr] }
                                            : c
                                        )
                                      }
                                    }
                                  };
                                });
                              }}
                              onEditOneOff={(dateStr, clubs) => {
                                const existingKitItems = clubs.length > 0 ? (clubs[0].kit_items || []) : [];
                                const existingParent = clubs.length > 0 ? (clubs[0].designated_parent_uuid || '') : '';
                                setEditOneOffDateStr(dateStr);
                                setEditOneOffClubs(clubs);
                                setEditOneOffKitItems(existingKitItems);
                                setEditOneOffDesignatedParent(existingParent);
                                setEditOneOffType('drop_off');
                                setShowEditOneOffModal(true);
                              }}
                              onRefreshIntegration={handleRefreshSchoolsBuddyFromTermPlanner}
                              isRefreshingIntegration={syncingSchoolsBuddy}
                              bookedDates={integrationBookedDates}
                              onBookedOnIntegration={handleBookedOnIntegration}
                              activityCategory="drop_off"
                              onOpenConfigModal={(activityName, kitItems, parentUuid, reminderMinutes) => {
                                setConfigActivityName(activityName);
                                setConfigActivityCategory('drop_off');
                                setConfigKitItems(kitItems);
                                setConfigParentUuid(parentUuid);
                                setConfigReminderMinutes(reminderMinutes);
                                setShowActivityConfigModal(true);
                              }}
                            />
                          )}
                        </div>

                        {/* Special Lessons Needing Kit */}
                        <div style={{ marginBottom: '15px', padding: '15px', backgroundColor: '#e3f2fd', border: '1px solid #90caf9', borderRadius: '8px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <h4 style={{ margin: 0, color: '#1565c0' }}>Special Lessons Needing Kit</h4>
                            {currentDay.club_reminders && currentDay.club_reminders.filter((r: any) => r.club_name).length > 0 && (
                              <button
                                onClick={() => {
                                  setDeleteScheduleCategory('special_lessons');
                                  setShowDeleteScheduleModal(true);
                                }}
                                style={{
                                  padding: '4px 10px',
                                  backgroundColor: '#dc3545',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                              >
                                Clear Schedule
                              </button>
                            )}
                          </div>
                          {currentDay.club_reminders && currentDay.club_reminders.filter((r: any) => r.club_name).map((reminder: any, idx: number) => (
                            <div key={idx} style={{ marginTop: '10px', padding: '10px', background: '#f9f9f9', borderRadius: '4px', position: 'relative' }}>
                              <button
                                onClick={() => removeClubReminder(currentDayTab, idx)}
                                style={{
                                  position: 'absolute',
                                  top: '10px',
                                  right: '10px',
                                  background: 'none',
                                  border: 'none',
                                  color: '#dc3545',
                                  cursor: 'pointer',
                                  fontSize: '18px',
                                  padding: '0 5px'
                                }}
                              >
                                ×
                              </button>
                              {/* Club details and kit form hidden - schedule shows all info with Update buttons */}
                              {/* Lesson Schedule - shows club for all dates, one-offs override specific dates */}
                              {(reminder.club_name || (reminder.manual_one_offs && reminder.manual_one_offs.length > 0)) && (
                                <IrregularActivitiesDisplay
                                  notes=""
                                  termStartDate={termStartDate}
                                  termEndDate={termEndDate}
                                  dayOfWeek={currentDayTab}
                                  label="📅 Lesson Schedule"
                                  backgroundColor="#cce5ff"
                                  borderColor="#007bff"
                                  textColor="#004085"
                                  activeIntegration={integrations.find(i => i.id === 'schoolsbuddy') || null}
                                  manualOneOffs={reminder.manual_one_offs || []}
                                  defaultClubs={reminder.club_name ? [{
                                    name: reminder.club_name,
                                    startTime: reminder.start_time || undefined,
                                    endTime: reminder.end_time || undefined,
                                    kit_items: reminder.kit_items || [],
                                    designated_parent_uuid: undefined,
                                    excludedDates: reminder.excludedDates || []
                                  }] : []}
                                  onAddManually={(date, dateStr) => {
                                    setOneOffDate(date);
                                    setOneOffDateStr(dateStr);
                                    setOneOffType('special_lesson');
                                    setOneOffTime('');
                                    setOneOffSelectedClub(null);
                                    setShowOneOffClubModal(true);
                                  }}
                                  onExcludeDateFromClub={(clubName, dateStr) => {
                                    setDailySchedules((prev: any) => {
                                      const dayData = prev[currentDayTab] || {};
                                      const reminders = dayData.club_reminders || [];
                                      return {
                                        ...prev,
                                        [currentDayTab]: {
                                          ...dayData,
                                          club_reminders: reminders.map((r: any, rIdx: number) =>
                                            rIdx === idx
                                              ? { ...r, excludedDates: [...(r.excludedDates || []), dateStr] }
                                              : r
                                          )
                                        }
                                      };
                                    });
                                  }}
                                  onEditOneOff={(dateStr, clubs) => {
                                    const existingKitItems = clubs.length > 0 ? (clubs[0].kit_items || []) : [];
                                    const existingParent = clubs.length > 0 ? (clubs[0].designated_parent_uuid || '') : '';
                                    setEditOneOffDateStr(dateStr);
                                    setEditOneOffClubs(clubs);
                                    setEditOneOffKitItems(existingKitItems);
                                    setEditOneOffDesignatedParent(existingParent);
                                    setEditOneOffType('drop_off'); // Special lessons use same storage pattern
                                    setShowEditOneOffModal(true);
                                  }}
                                  onRefreshIntegration={handleRefreshSchoolsBuddyFromTermPlanner}
                                  isRefreshingIntegration={syncingSchoolsBuddy}
                                  bookedDates={integrationBookedDates}
                                  onBookedOnIntegration={handleBookedOnIntegration}
                                  activityCategory="special_lesson"
                                  onOpenConfigModal={(activityName, kitItems, parentUuid, reminderMinutes) => {
                                    setConfigActivityName(activityName);
                                    setConfigActivityCategory('special_lesson');
                                    setConfigKitItems(kitItems);
                                    setConfigParentUuid(parentUuid);
                                    setConfigReminderMinutes(reminderMinutes);
                                    setShowActivityConfigModal(true);
                                  }}
                                />
                              )}
                            </div>
                          ))}
                          {/* Only show Add button if no special lessons exist yet */}
                          {(!currentDay.club_reminders || currentDay.club_reminders.filter((r: any) => r.club_name).length === 0) && (
                            <button
                              onClick={() => {
                                setClubModalType('club_reminder');
                                setShowClubModal(true);
                                setCurrentClubReminderIndex(-1);
                              }}
                              style={{ marginTop: '10px', padding: '8px 15px', background: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                            >
                              + Add Special Lesson
                            </button>
                          )}
                        </div>

                        {/* Pick-up */}
                        <div style={{ padding: '15px', backgroundColor: '#fff3e0', border: '1px solid #ffcc80', borderRadius: '8px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <h4 style={{ margin: 0, color: '#e65100' }}>Pick-up</h4>
                            {((currentDay.pick_up?.clubs && currentDay.pick_up.clubs.length > 0) ||
                              (currentDay.pick_up?.manualOneOffs && currentDay.pick_up.manualOneOffs.length > 0) ||
                              currentDay.pick_up?.club_name) && (
                              <button
                                onClick={() => {
                                  setDeleteScheduleCategory('pick_up');
                                  setShowDeleteScheduleModal(true);
                                }}
                                style={{
                                  padding: '4px 10px',
                                  backgroundColor: '#dc3545',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                              >
                                Clear Schedule
                              </button>
                            )}
                          </div>

                          {/* Add Club Button - shows "Replace" when nothing configured, "Add Another" when clubs/one-offs exist */}
                          <button
                            onClick={() => {
                              setReplaceClubType('pick_up');
                              setReplaceClubSelected(null);
                              setReplaceClubStartTime('');
                              setReplaceClubEndTime('');
                              setReplaceClubKitItems([]);
                              setReplaceClubUniform([]);
                              setReplaceClubDesignatedParent(currentDay.pick_up?.designated_parent_uuid || '');
                              setReplaceClubReminderMinutes(currentDay.pick_up?.reminder_minutes ?? null);
                              setShowReplaceClubModal(true);
                            }}
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              marginBottom: '15px',
                              backgroundColor: '#17a2b8',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '8px'
                            }}
                          >
                            {(currentDay.pick_up?.clubs?.length > 0 || currentDay.pick_up?.manualOneOffs?.length > 0 || currentDay.pick_up?.notes) ? '➕ Add Another' : 'Replace'}
                          </button>
                          {/* Only show full form fields when no clubs/manualOneOffs/notes exist (they have their own UI) */}
                          {(!currentDay.pick_up?.clubs || currentDay.pick_up.clubs.length === 0) && !(currentDay.pick_up?.manualOneOffs && currentDay.pick_up.manualOneOffs.length > 0) && !currentDay.pick_up?.notes && (
                          <>
                              <div className="form-group">
                                <label>Time</label>
                                <TimePickerDropdown
                                  value={currentDay.pick_up?.time || ''}
                                  onChange={(time) => updateDaySchedule(currentDayTab, 'pick_up', { ...currentDay.pick_up, time })}
                                  placeholder="Select pick-up time"
                                  defaultScrollPosition="end"
                                />
                              </div>
                              <div className="form-group">
                                <label>🚗 Designated Parent (Optional)</label>
                                <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px', marginTop: '4px' }}>
                                  Select the parent responsible for pick-up on {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][currentDayTab]}s
                                </p>
                                <select
                                  value={currentDay.pick_up?.designated_parent_uuid || ''}
                                  onChange={(e) => updateDaySchedule(currentDayTab, 'pick_up', { ...currentDay.pick_up, designated_parent_uuid: e.target.value || undefined })}
                                  style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '4px',
                                    border: '1px solid #ddd',
                                    fontSize: '14px',
                                    backgroundColor: 'white',
                                    cursor: 'pointer',
                                    marginTop: '5px'
                                  }}
                                >
                                  <option value="">Not assigned</option>
                                  {parents.map((parent) => (
                                    <option key={parent.uuid} value={parent.uuid}>
                                      {parent.username} ({parent.email}){parent.is_primary ? ' - Primary' : ''}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="form-group">
                                <label>🔔 Reminder (Optional)</label>
                                <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px', marginTop: '4px' }}>
                                  Get reminded before pick-up
                                </p>
                                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px', color: '#4a5568' }}>
                                  Remind me for:
                                </label>
                                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                                  {([
                                    { label: 'Start Time', value: 'start' as const },
                                    { label: 'End Time', value: 'end' as const },
                                    { label: 'Both', value: 'both' as const }
                                  ]).map((opt) => (
                                    <button
                                      key={opt.value}
                                      type="button"
                                      onClick={() => updateDaySchedule(currentDayTab, 'pick_up', { ...currentDay.pick_up, reminder_target: opt.value })}
                                      style={{
                                        flex: 1, padding: '10px 8px', fontSize: '13px', fontWeight: '600',
                                        color: currentDay.pick_up?.reminder_target === opt.value ? 'white' : '#4a5568',
                                        background: currentDay.pick_up?.reminder_target === opt.value ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'white',
                                        border: currentDay.pick_up?.reminder_target === opt.value ? '2px solid #667eea' : '2px solid #e2e8f0',
                                        borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s ease'
                                      }}
                                    >
                                      {opt.label}
                                    </button>
                                  ))}
                                </div>
                                <select
                                  value={currentDay.pick_up?.reminder_minutes ?? ''}
                                  onChange={(e) => updateDaySchedule(currentDayTab, 'pick_up', { ...currentDay.pick_up, reminder_minutes: e.target.value ? parseInt(e.target.value) : null })}
                                  style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '4px',
                                    border: '1px solid #ddd',
                                    fontSize: '14px',
                                    backgroundColor: 'white',
                                    cursor: 'pointer',
                                    marginTop: '5px'
                                  }}
                                >
                                  <option value="">No reminder</option>
                                  {REMINDER_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                          </>
                          )}
                          {/* Pick-up Schedule - shows clubs and/or one-offs */}
                          {(currentDay.pick_up?.notes || (currentDay.pick_up?.manualOneOffs && currentDay.pick_up.manualOneOffs.length > 0) || (currentDay.pick_up?.clubs && currentDay.pick_up.clubs.length > 0)) && (
                            <IrregularActivitiesDisplay
                              notes={currentDay.pick_up?.notes || ''}
                              termStartDate={termStartDate}
                              termEndDate={termEndDate}
                              dayOfWeek={currentDayTab}
                              label="📅 After School Clubs"
                              backgroundColor="#d4edda"
                              borderColor="#28a745"
                              textColor="#155724"
                              activeIntegration={integrations.find(i => i.id === 'schoolsbuddy') || null}
                              manualOneOffs={currentDay.pick_up?.manualOneOffs || []}
                              defaultClubs={(currentDay.pick_up?.clubs || []).map((c: any) => ({
                                name: c.name,
                                startTime: c.startTime,
                                endTime: c.endTime,
                                kit_items: c.kit_items,
                                designated_parent_uuid: c.designated_parent_uuid,
                                reminder_minutes: c.reminder_minutes,
                                excludedDates: c.excludedDates || []
                              }))}
                              onAddManually={(date, dateStr) => {
                                setOneOffDate(date);
                                setOneOffDateStr(dateStr);
                                setOneOffType('pick_up');
                                setOneOffTime('');
                                setOneOffSelectedClub(null);
                                setShowOneOffClubModal(true);
                              }}
                              onRemoveManualOneOff={(dateStr) => {
                                setDailySchedules((prev: any) => {
                                  const dayData = prev[currentDayTab] || {};
                                  const pickUpData = dayData.pick_up || {};
                                  const existingOneOffs = pickUpData.manualOneOffs || [];
                                  return {
                                    ...prev,
                                    [currentDayTab]: {
                                      ...dayData,
                                      pick_up: {
                                        ...pickUpData,
                                        manualOneOffs: existingOneOffs.filter((o: ManualOneOff) => o.dateStr !== dateStr)
                                      }
                                    }
                                  };
                                });
                              }}
                              onRemoveDefaultClub={(clubName) => {
                                setDailySchedules((prev: any) => {
                                  const dayData = prev[currentDayTab] || {};
                                  const pickUpData = dayData.pick_up || {};
                                  const existingClubs = pickUpData.clubs || [];
                                  return {
                                    ...prev,
                                    [currentDayTab]: {
                                      ...dayData,
                                      pick_up: {
                                        ...pickUpData,
                                        clubs: existingClubs.filter((c: any) => c.name !== clubName)
                                      }
                                    }
                                  };
                                });
                              }}
                              onExcludeDateFromClub={(clubName, dateStr) => {
                                setDailySchedules((prev: any) => {
                                  const dayData = prev[currentDayTab] || {};
                                  const pickUpData = dayData.pick_up || {};
                                  const existingClubs = pickUpData.clubs || [];
                                  return {
                                    ...prev,
                                    [currentDayTab]: {
                                      ...dayData,
                                      pick_up: {
                                        ...pickUpData,
                                        clubs: existingClubs.map((c: any) =>
                                          c.name === clubName
                                            ? { ...c, excludedDates: [...(c.excludedDates || []), dateStr] }
                                            : c
                                        )
                                      }
                                    }
                                  };
                                });
                              }}
                              onEditOneOff={(dateStr, clubs) => {
                                // Get existing kit items and designated parent from the first club
                                const existingKitItems = clubs.length > 0 ? (clubs[0].kit_items || []) : [];
                                const existingParent = clubs.length > 0 ? (clubs[0].designated_parent_uuid || '') : '';
                                setEditOneOffDateStr(dateStr);
                                setEditOneOffClubs(clubs);
                                setEditOneOffKitItems(existingKitItems);
                                setEditOneOffDesignatedParent(existingParent);
                                setEditOneOffType('pick_up');
                                setShowEditOneOffModal(true);
                              }}
                              onRefreshIntegration={handleRefreshSchoolsBuddyFromTermPlanner}
                              isRefreshingIntegration={syncingSchoolsBuddy}
                              bookedDates={integrationBookedDates}
                              onBookedOnIntegration={handleBookedOnIntegration}
                              activityCategory="pick_up"
                              onOpenConfigModal={(activityName, kitItems, parentUuid, reminderMinutes) => {
                                setConfigActivityName(activityName);
                                setConfigActivityCategory('pick_up');
                                setConfigKitItems(kitItems);
                                setConfigParentUuid(parentUuid);
                                setConfigReminderMinutes(reminderMinutes);
                                setShowActivityConfigModal(true);
                              }}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <button
                      className="primary-button"
                      onClick={() => {
                        if (hasUnsavedTermPlannerChanges) {
                          const confirmed = window.confirm('You have unsaved changes. Are you sure you want to exit? Your changes will be lost.');
                          if (!confirmed) return;
                        }
                        setEditingTermPlannerUuid(null);
                        setTermPlannerStep('list');
                        setHasUnsavedTermPlannerChanges(false);
                      }}
                      style={{ background: '#6c757d' }}
                    >
                      Cancel
                    </button>
                    {currentDayTab < 6 && (
                      <button
                        className="primary-button"
                        onClick={() => {
                          setCurrentDayTab(currentDayTab + 1);
                          setTimeout(() => {
                            dailyScheduleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }, 100);
                        }}
                      >
                        Next Day →
                      </button>
                    )}
                    <button
                      className="primary-button"
                      onClick={handleSaveTermPlanner}
                      disabled={creatingTermPlanner || !termName || !termStartDate}
                    >
                      {creatingTermPlanner ? 'Creating...' : 'Save & Generate Activities'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Term Planner Club Modal */}
      {showClubModal && (
        <TermPlannerClubModal
          onClubSelect={handleClubSelect}
          onCancel={() => setShowClubModal(false)}
          child={termPlannerChild || undefined}
        />
      )}

      {/* One-Off Club Modal */}
      {showOneOffClubModal && (
        <TermPlannerClubModal
          onClubSelect={(club) => {
            setOneOffSelectedClub(club);
            setOneOffKitItems(club.kit_items || []);  // Initialize with club's kit items
            setShowOneOffClubModal(false);
            setShowOneOffTimeModal(true);
          }}
          onCancel={() => {
            setShowOneOffClubModal(false);
            setOneOffDate(null);
            setOneOffDateStr('');
            setOneOffKitItems([]);
          }}
          child={termPlannerChild || undefined}
        />
      )}

      {/* Replace With Club - Club Selection Modal */}
      {showReplaceClubModal && (
        <TermPlannerClubModal
          onClubSelect={(club) => {
            setReplaceClubSelected(club);
            setReplaceClubKitItems(club.kit_items || []);
            setShowReplaceClubModal(false);
            setShowReplaceClubDetailsModal(true);
          }}
          onCancel={() => {
            setShowReplaceClubModal(false);
            setReplaceClubSelected(null);
          }}
          child={termPlannerChild || undefined}
        />
      )}

      {/* Replace With Club - Details Modal */}
      {showReplaceClubDetailsModal && replaceClubSelected && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10001
          }}
          onMouseDown={() => {
            setShowReplaceClubDetailsModal(false);
            setReplaceClubSelected(null);
            setReplaceClubDesignatedParent('');
            setReplaceClubReminderMinutes(null);
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px' }}>
              {replaceClubType === 'drop_off' ? '🌅 Add Drop-off Club' : '🌆 Add Pick-up Club'}
            </h3>

            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
              <div style={{ fontWeight: '600', marginBottom: '4px' }}>{replaceClubSelected.name}</div>
              <div style={{ fontSize: '14px', color: '#666' }}>
                {replaceClubSelected.venue && <span>{replaceClubSelected.venue}</span>}
                {replaceClubSelected.venue && replaceClubSelected.location && ', '}
                {replaceClubSelected.location && <span>{replaceClubSelected.location}</span>}
              </div>
            </div>

            {/* Start and End Times */}
            <div className="replace-club-times-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Start Time
                </label>
                <TimePickerDropdown
                  value={replaceClubStartTime}
                  onChange={(time) => setReplaceClubStartTime(time)}
                  placeholder="Select start time"
                  defaultScrollPosition="start"
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  End Time
                </label>
                <TimePickerDropdown
                  value={replaceClubEndTime}
                  onChange={(time) => setReplaceClubEndTime(time)}
                  placeholder="Select end time"
                  defaultScrollPosition="end"
                />
              </div>
            </div>

            {/* Designated Parent */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                🚗 Designated Parent (Optional)
              </label>
              <select
                value={replaceClubDesignatedParent}
                onChange={(e) => setReplaceClubDesignatedParent(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '6px',
                  border: '1px solid #ddd',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="">Not assigned</option>
                {parents.map((parent) => (
                  <option key={parent.uuid} value={parent.uuid}>
                    {parent.username} ({parent.email}){parent.is_primary ? ' - Primary' : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Kit Items */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                🎒 Kit Items (Optional)
              </label>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                <input
                  type="text"
                  placeholder="e.g., Swimming Kit"
                  id="replace-club-kit-input"
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      const input = e.target as HTMLInputElement;
                      if (input.value.trim()) {
                        setReplaceClubKitItems([...replaceClubKitItems, input.value.trim()]);
                        input.value = '';
                      }
                    }
                  }}
                />
                <button
                  onClick={() => {
                    const input = document.getElementById('replace-club-kit-input') as HTMLInputElement;
                    if (input && input.value.trim()) {
                      setReplaceClubKitItems([...replaceClubKitItems, input.value.trim()]);
                      input.value = '';
                    }
                  }}
                  style={{
                    padding: '10px 16px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Add
                </button>
              </div>
              {replaceClubKitItems.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {replaceClubKitItems.map((item, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 10px',
                        backgroundColor: '#e9ecef',
                        borderRadius: '4px',
                        fontSize: '13px'
                      }}
                    >
                      <span>{item}</span>
                      <button
                        onClick={() => setReplaceClubKitItems(replaceClubKitItems.filter((_, i) => i !== idx))}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#dc3545',
                          cursor: 'pointer',
                          padding: '0',
                          fontSize: '16px',
                          lineHeight: 1
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* School Uniform - Only for Drop-off */}
            {replaceClubType === 'drop_off' && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  👔 School Uniform
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {['Smart Uniform', 'PE Kit', 'Home Clothes', 'Costume'].map((uniform) => (
                    <label key={uniform} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={replaceClubUniform.includes(uniform)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setReplaceClubUniform([...replaceClubUniform, uniform]);
                          } else {
                            setReplaceClubUniform(replaceClubUniform.filter(u => u !== uniform));
                          }
                        }}
                        style={{ marginRight: '6px' }}
                      />
                      {uniform}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Reminder */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                🔔 Reminder (Optional)
              </label>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px', color: '#4a5568' }}>
                Remind me for:
              </label>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                {([
                  { label: 'Start Time', value: 'start' as const },
                  { label: 'End Time', value: 'end' as const },
                  { label: 'Both', value: 'both' as const }
                ]).map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setReplaceClubReminderTarget(opt.value)}
                    style={{
                      flex: 1, padding: '10px 8px', fontSize: '13px', fontWeight: '600',
                      color: replaceClubReminderTarget === opt.value ? 'white' : '#4a5568',
                      background: replaceClubReminderTarget === opt.value ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'white',
                      border: replaceClubReminderTarget === opt.value ? '2px solid #667eea' : '2px solid #e2e8f0',
                      borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s ease'
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <select
                value={replaceClubReminderMinutes ?? ''}
                onChange={(e) => setReplaceClubReminderMinutes(e.target.value ? parseInt(e.target.value) : null)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '6px',
                  border: '1px solid #ddd',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="">No reminder</option>
                {REMINDER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {replaceClubReminderMinutes !== null && replaceClubReminderTarget === null && (
                <p style={{ fontSize: '12px', color: '#e53e3e', marginTop: '8px' }}>
                  Please select whether this reminder is for the start time, end time, or both.
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => {
                  setShowReplaceClubDetailsModal(false);
                  setReplaceClubSelected(null);
                  setReplaceClubDesignatedParent('');
                  setReplaceClubReminderMinutes(null);
                  setReplaceClubReminderTarget(null);
                }}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  backgroundColor: '#f8f9fa',
                  color: '#333',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  // Generate individual entries for each week in the term
                  const targetField = replaceClubType;

                  // Save kit items to the club in the database if any were added
                  if (replaceClubKitItems.length > 0 && replaceClubSelected.uuid) {
                    try {
                      await apiService.updateClubKitItems(replaceClubSelected.uuid, replaceClubKitItems);
                    } catch (error) {
                      console.error('Failed to update club kit items:', error);
                    }
                  }

                  // Merge uniform items into kit_items so they show in config modal and on activities
                  const mergedKitItems = Array.from(new Set([...replaceClubKitItems, ...replaceClubUniform]));

                  // Add club to the clubs array (supports multiple clubs per day)
                  const newClub = {
                    name: replaceClubSelected.name,
                    startTime: replaceClubStartTime || '',
                    endTime: replaceClubEndTime || '',
                    venue: replaceClubSelected.venue || '',
                    city: replaceClubSelected.location || '',
                    kit_items: mergedKitItems,
                    uniform_items: replaceClubUniform,
                    designated_parent_uuid: replaceClubDesignatedParent || '',
                    reminder_minutes: replaceClubReminderMinutes,
                    reminder_target: replaceClubReminderTarget
                  };

                  setDailySchedules((prev: any) => {
                    const dayData = prev[currentDayTab] || {};
                    const fieldData = dayData[targetField] || {};
                    const existingClubs = fieldData.clubs || [];

                    // Calculate time from clubs for the schedule
                    const allClubs = [...existingClubs, newClub];
                    let newTime = fieldData.time;
                    if (targetField === 'drop_off') {
                      // Drop-off time = earliest start time
                      const startTimes = allClubs.map((c: any) => c.startTime).filter((t: string) => t).sort();
                      if (startTimes.length > 0) newTime = startTimes[0];
                    } else {
                      // Pick-up time = latest end time
                      const endTimes = allClubs.map((c: any) => c.endTime).filter((t: string) => t).sort();
                      if (endTimes.length > 0) newTime = endTimes[endTimes.length - 1];
                    }

                    return {
                      ...prev,
                      [currentDayTab]: {
                        ...dayData,
                        [targetField]: {
                          ...fieldData,
                          clubs: allClubs,
                          club_name: allClubs[0].name,
                          time: newTime
                        }
                      }
                    };
                  });

                  // Close modal and reset
                  setShowReplaceClubDetailsModal(false);
                  setReplaceClubSelected(null);
                  setReplaceClubStartTime('');
                  setReplaceClubEndTime('');
                  setReplaceClubKitItems([]);
                  setReplaceClubUniform([]);
                  setReplaceClubDesignatedParent('');
                  setReplaceClubReminderMinutes(null);
                  setReplaceClubReminderTarget(null);
                }}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Add Club
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit One-Off Kit/Parent Modal */}
      {showEditOneOffModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10001
          }}
          onMouseDown={() => {
            setShowEditOneOffModal(false);
            setEditOneOffDateStr('');
            setEditOneOffClubs([]);
            setEditOneOffKitItems([]);
            setEditOneOffDesignatedParent('');
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px' }}>
              ✏️ Update Kit & Parent - {editOneOffDateStr}
            </h3>

            {/* Club list for this date */}
            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
              <div style={{ fontWeight: '600', marginBottom: '8px', fontSize: '14px' }}>Clubs on this date:</div>
              {editOneOffClubs.map((club, idx) => (
                <div key={idx} style={{ fontSize: '14px', color: '#333', marginBottom: '4px' }}>
                  • {club.clubName}
                  {(club.startTime || club.endTime) && (
                    <span style={{ color: '#666', marginLeft: '8px' }}>
                      ({club.startTime || ''}{club.startTime && club.endTime ? '-' : ''}{club.endTime || ''})
                    </span>
                  )}
                </div>
              ))}
            </div>

            {/* Designated Parent */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                🚗 Designated Parent (Optional)
              </label>
              <select
                value={editOneOffDesignatedParent}
                onChange={(e) => setEditOneOffDesignatedParent(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '6px',
                  border: '1px solid #ddd',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="">Not assigned</option>
                {parents.map((parent) => (
                  <option key={parent.uuid} value={parent.uuid}>
                    {parent.username} ({parent.email}){parent.is_primary ? ' - Primary' : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Kit Items */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                🎒 Kit Items (Optional)
              </label>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                <input
                  type="text"
                  placeholder="e.g., Swimming Kit"
                  id="edit-oneoff-kit-input"
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      const input = e.target as HTMLInputElement;
                      if (input.value.trim()) {
                        setEditOneOffKitItems([...editOneOffKitItems, input.value.trim()]);
                        input.value = '';
                      }
                    }
                  }}
                />
                <button
                  onClick={() => {
                    const input = document.getElementById('edit-oneoff-kit-input') as HTMLInputElement;
                    if (input && input.value.trim()) {
                      setEditOneOffKitItems([...editOneOffKitItems, input.value.trim()]);
                      input.value = '';
                    }
                  }}
                  style={{
                    padding: '10px 16px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Add
                </button>
              </div>
              {editOneOffKitItems.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {editOneOffKitItems.map((item, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 10px',
                        backgroundColor: '#e9ecef',
                        borderRadius: '4px',
                        fontSize: '13px'
                      }}
                    >
                      <span>{item}</span>
                      <button
                        onClick={() => setEditOneOffKitItems(editOneOffKitItems.filter((_, i) => i !== idx))}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#dc3545',
                          cursor: 'pointer',
                          padding: '0',
                          fontSize: '16px',
                          lineHeight: 1
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => {
                  setShowEditOneOffModal(false);
                  setEditOneOffDateStr('');
                  setEditOneOffClubs([]);
                  setEditOneOffKitItems([]);
                  setEditOneOffDesignatedParent('');
                }}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  backgroundColor: '#f8f9fa',
                  color: '#333',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Calculate the new state first
                  const dayData = dailySchedules[currentDayTab] || {};
                  const targetField = editOneOffType;
                  const fieldData = dayData[targetField] || {};
                  const existingOneOffs: ManualOneOff[] = fieldData.manualOneOffs || [];

                  // Check if any clubs for this date exist in manualOneOffs
                  const hasExistingForDate = existingOneOffs.some((oneOff: ManualOneOff) => oneOff.dateStr === editOneOffDateStr);

                  let updatedOneOffs: ManualOneOff[];

                  if (hasExistingForDate) {
                    // Update existing one-offs for this date
                    updatedOneOffs = existingOneOffs.map((oneOff: ManualOneOff) => {
                      if (oneOff.dateStr === editOneOffDateStr) {
                        return {
                          ...oneOff,
                          kit_items: editOneOffKitItems,
                          designated_parent_uuid: editOneOffDesignatedParent || undefined
                        };
                      }
                      return oneOff;
                    });
                  } else {
                    // No existing entries - add clubs from editOneOffClubs with the new kit/parent data
                    const newOneOffs: ManualOneOff[] = editOneOffClubs.map(club => ({
                      dateStr: editOneOffDateStr,
                      clubName: club.clubName,
                      startTime: club.startTime,
                      endTime: club.endTime,
                      kit_items: editOneOffKitItems,
                      designated_parent_uuid: editOneOffDesignatedParent || undefined
                    }));
                    updatedOneOffs = [...existingOneOffs, ...newOneOffs];
                  }

                  // Update local state only - changes will be saved when user clicks "Save & Generate Activities"
                  setDailySchedules({
                    ...dailySchedules,
                    [currentDayTab]: {
                      ...dayData,
                      [targetField]: {
                        ...fieldData,
                        manualOneOffs: updatedOneOffs
                      }
                    }
                  });

                  // Mark that we have unsaved changes
                  setHasUnsavedTermPlannerChanges(true);

                  // Close modal
                  setShowEditOneOffModal(false);
                  setEditOneOffDateStr('');
                  setEditOneOffClubs([]);
                  setEditOneOffKitItems([]);
                  setEditOneOffDesignatedParent('');
                }}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Activity Configuration Modal (Kit + Parent for all instances) */}
      {showActivityConfigModal && configActivityName && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10001
          }}
          onMouseDown={() => {
            setShowActivityConfigModal(false);
            setConfigActivityName('');
            setConfigKitItems([]);
            setConfigNewKitItem('');
            setConfigParentUuid('');
            setConfigReminderMinutes(null);
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '450px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 8px 0', fontSize: '18px' }}>⚙️ Configure Activity</h3>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
              {configActivityName}
            </p>
            <p style={{ fontSize: '12px', color: '#888', marginBottom: '16px' }}>
              Changes will apply to all instances of this activity.
            </p>

            {/* Uniform Quick-Add Section (for drop-off activities only) */}
            {configActivityCategory === 'drop_off' && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '14px' }}>
                  👔 Uniform & Essentials
                </label>
                <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                  Quick add common items for drop-off:
                </p>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {['Smart Uniform', 'PE Kit', 'Home Clothes', 'Costume'].map((item) => (
                    <button
                      key={item}
                      onClick={() => {
                        if (configKitItems.includes(item)) {
                          setConfigKitItems(configKitItems.filter(k => k !== item));
                        } else {
                          setConfigKitItems([...configKitItems, item]);
                        }
                      }}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: configKitItems.includes(item) ? '#d4edda' : '#f8f9fa',
                        color: configKitItems.includes(item) ? '#155724' : '#333',
                        border: `1px solid ${configKitItems.includes(item) ? '#28a745' : '#ddd'}`,
                        borderRadius: '16px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      {configKitItems.includes(item) ? '✓ ' : '+ '}{item}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Kit Items Section */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '14px' }}>
                🎒 {configActivityCategory === 'drop_off' ? 'Additional Items' : 'Kit Items'}
              </label>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
                {configKitItems.map((item, index) => (
                  <span
                    key={index}
                    style={{
                      backgroundColor: '#e3f2fd',
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '13px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    {item}
                    <button
                      onClick={() => setConfigKitItems(configKitItems.filter((_, i) => i !== index))}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        fontSize: '14px',
                        color: '#666',
                        lineHeight: 1
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <input
                  type="text"
                  value={configNewKitItem}
                  onChange={(e) => setConfigNewKitItem(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && configNewKitItem.trim()) {
                      setConfigKitItems([...configKitItems, configNewKitItem.trim()]);
                      setConfigNewKitItem('');
                    }
                  }}
                  placeholder="Add kit item..."
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                <button
                  onClick={() => {
                    if (configNewKitItem.trim()) {
                      setConfigKitItems([...configKitItems, configNewKitItem.trim()]);
                      setConfigNewKitItem('');
                    }
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  Add
                </button>
              </div>
            </div>

            {/* Designated Parent Section - only for drop_off and pick_up */}
            {configActivityCategory !== 'special_lesson' && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '14px' }}>
                  🚗 Designated Parent
                </label>
                <select
                  value={configParentUuid}
                  onChange={(e) => setConfigParentUuid(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Not assigned</option>
                  {parents.map((parent) => (
                    <option key={parent.uuid} value={parent.uuid}>
                      {parent.username} ({parent.email}){parent.is_primary ? ' - Primary' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Reminder Section */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '14px' }}>
                🔔 Reminder
              </label>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px', color: '#4a5568' }}>
                Remind me for:
              </label>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                {([
                  { label: 'Start Time', value: 'start' as const },
                  { label: 'End Time', value: 'end' as const },
                  { label: 'Both', value: 'both' as const }
                ]).map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setConfigReminderTarget(opt.value)}
                    style={{
                      flex: 1, padding: '10px 8px', fontSize: '13px', fontWeight: '600',
                      color: configReminderTarget === opt.value ? 'white' : '#4a5568',
                      background: configReminderTarget === opt.value ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'white',
                      border: configReminderTarget === opt.value ? '2px solid #667eea' : '2px solid #e2e8f0',
                      borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s ease'
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <select
                value={configReminderMinutes ?? ''}
                onChange={(e) => setConfigReminderMinutes(e.target.value ? parseInt(e.target.value) : null)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value="">No reminder</option>
                {REMINDER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {configReminderMinutes !== null && configReminderTarget === null && (
                <p style={{ fontSize: '12px', color: '#e53e3e', marginTop: '8px' }}>
                  Please select whether this reminder is for the start time, end time, or both.
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => {
                  setShowActivityConfigModal(false);
                  setConfigActivityName('');
                  setConfigKitItems([]);
                  setConfigNewKitItem('');
                  setConfigParentUuid('');
                  setConfigReminderMinutes(null);
                  setConfigReminderTarget(null);
                }}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  backgroundColor: '#f8f9fa',
                  color: '#333',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  // If user is setting a reminder on native platform, request alarm permission now
                  // (must happen in response to user tap for iOS to show the prompt)
                  if (configReminderMinutes !== null && Capacitor.isNativePlatform()) {
                    try {
                      const localNotificationService = LocalNotificationService.getInstance();
                      await localNotificationService.requestAlarmPermission();
                    } catch (e) {
                      console.warn('Failed to request alarm permission:', e);
                    }
                  }

                  // Update all instances of this activity based on category
                  setDailySchedules((prev: any) => {
                    const updated = { ...prev };
                    Object.keys(updated).forEach(dayKey => {
                      const day = parseInt(dayKey);

                      if (configActivityCategory === 'drop_off' && updated[day]?.drop_off) {
                        updated[day] = {
                          ...updated[day],
                          drop_off: {
                            ...updated[day].drop_off,
                            manualOneOffs: (updated[day].drop_off.manualOneOffs || []).map((o: ManualOneOff) =>
                              o.clubName === configActivityName
                                ? { ...o, kit_items: configKitItems, uniform_items: [], designated_parent_uuid: configParentUuid || undefined, reminder_minutes: configReminderMinutes }
                                : o
                            ),
                            clubs: (updated[day].drop_off.clubs || []).map((c: any) =>
                              c.name === configActivityName
                                ? { ...c, kit_items: configKitItems, uniform_items: [], designated_parent_uuid: configParentUuid || undefined, reminder_minutes: configReminderMinutes }
                                : c
                            )
                          }
                        };
                      }

                      if (configActivityCategory === 'pick_up' && updated[day]?.pick_up) {
                        updated[day] = {
                          ...updated[day],
                          pick_up: {
                            ...updated[day].pick_up,
                            manualOneOffs: (updated[day].pick_up.manualOneOffs || []).map((o: ManualOneOff) =>
                              o.clubName === configActivityName
                                ? { ...o, kit_items: configKitItems, uniform_items: [], designated_parent_uuid: configParentUuid || undefined, reminder_minutes: configReminderMinutes }
                                : o
                            ),
                            clubs: (updated[day].pick_up.clubs || []).map((c: any) =>
                              c.name === configActivityName
                                ? { ...c, kit_items: configKitItems, uniform_items: [], designated_parent_uuid: configParentUuid || undefined, reminder_minutes: configReminderMinutes }
                                : c
                            )
                          }
                        };
                      }

                      if (configActivityCategory === 'special_lesson' && updated[day]?.club_reminders) {
                        updated[day] = {
                          ...updated[day],
                          club_reminders: updated[day].club_reminders.map((r: any) =>
                            (r.activity_name === configActivityName || r.club_name === configActivityName)
                              ? {
                                  ...r,
                                  kit_items: configKitItems,
                                  reminder_minutes: configReminderMinutes,
                                  manual_one_offs: r.manual_one_offs?.map((o: ManualOneOff) =>
                                    o.clubName === configActivityName
                                      ? { ...o, kit_items: configKitItems, reminder_minutes: configReminderMinutes }
                                      : o
                                  )
                                }
                              : r
                          )
                        };
                      }
                    });
                    return updated;
                  });

                  setHasUnsavedTermPlannerChanges(true);
                  setShowActivityConfigModal(false);
                  setConfigActivityName('');
                  setConfigKitItems([]);
                  setConfigNewKitItem('');
                  setConfigParentUuid('');
                  setConfigReminderMinutes(null);
                }}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* One-Off Time Picker Modal */}
      {showOneOffTimeModal && oneOffSelectedClub && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10001
          }}
          onMouseDown={() => {
            setShowOneOffTimeModal(false);
            setOneOffSelectedClub(null);
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '450px',
              width: '90%',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px' }}>
              Add {oneOffType === 'drop_off' ? 'Drop-off' : oneOffType === 'pick_up' ? 'Pick-up' : 'Special Lesson'} Club
            </h3>

            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
              <div style={{ fontWeight: '600', marginBottom: '4px' }}>{oneOffSelectedClub.name}</div>
              <div style={{ fontSize: '14px', color: '#666' }}>
                {oneOffSelectedClub.venue && <span>{oneOffSelectedClub.venue}</span>}
                {oneOffSelectedClub.venue && oneOffSelectedClub.location && ', '}
                {oneOffSelectedClub.location && <span>{oneOffSelectedClub.location}</span>}
              </div>
              <div style={{ fontSize: '13px', color: '#007bff', marginTop: '8px' }}>
                📅 {oneOffDateStr}
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                {oneOffType === 'drop_off' ? 'Drop-off Time' : oneOffType === 'pick_up' ? 'Pick-up Time' : 'Lesson Time'}
              </label>
              <TimePickerDropdown
                value={oneOffTime}
                onChange={(time) => setOneOffTime(time)}
                placeholder={`Select ${oneOffType === 'drop_off' ? 'drop-off' : oneOffType === 'pick_up' ? 'pick-up' : 'lesson'} time`}
                defaultScrollPosition={oneOffType === 'drop_off' ? 'start' : 'end'}
              />
            </div>

            {/* Kit Items Section */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                🎒 Kit Items (Optional)
              </label>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                <input
                  type="text"
                  placeholder="e.g., Swimming Kit"
                  id="one-off-kit-input"
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      const input = e.target as HTMLInputElement;
                      if (input.value.trim()) {
                        setOneOffKitItems([...oneOffKitItems, input.value.trim()]);
                        input.value = '';
                      }
                    }
                  }}
                />
                <button
                  onClick={() => {
                    const input = document.getElementById('one-off-kit-input') as HTMLInputElement;
                    if (input && input.value.trim()) {
                      setOneOffKitItems([...oneOffKitItems, input.value.trim()]);
                      input.value = '';
                    }
                  }}
                  style={{
                    padding: '10px 16px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Add
                </button>
              </div>
              {oneOffKitItems.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {oneOffKitItems.map((item, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 10px',
                        backgroundColor: '#e9ecef',
                        borderRadius: '4px',
                        fontSize: '13px'
                      }}
                    >
                      <span>{item}</span>
                      <button
                        onClick={() => {
                          setOneOffKitItems(oneOffKitItems.filter((_, i) => i !== idx));
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#dc3545',
                          cursor: 'pointer',
                          padding: '0',
                          fontSize: '16px',
                          lineHeight: 1
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Designated Parent */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                🚗 Designated Parent (Optional)
              </label>
              <select
                value={oneOffDesignatedParent}
                onChange={(e) => setOneOffDesignatedParent(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '6px',
                  border: '1px solid #ddd',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="">Not assigned</option>
                {parents.map((parent) => (
                  <option key={parent.uuid} value={parent.uuid}>
                    {parent.username} ({parent.email}){parent.is_primary ? ' - Primary' : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* School Uniform - Only for Drop-off */}
            {oneOffType === 'drop_off' && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  👔 School Uniform
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {['Smart Uniform', 'PE Kit', 'Home Clothes', 'Costume'].map((uniform) => (
                    <label key={uniform} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={oneOffUniform.includes(uniform)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setOneOffUniform([...oneOffUniform, uniform]);
                          } else {
                            setOneOffUniform(oneOffUniform.filter(u => u !== uniform));
                          }
                        }}
                        style={{ marginRight: '6px' }}
                      />
                      {uniform}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Reminder */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                🔔 Reminder (Optional)
              </label>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px', color: '#4a5568' }}>
                Remind me for:
              </label>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                {([
                  { label: 'Start Time', value: 'start' as const },
                  { label: 'End Time', value: 'end' as const },
                  { label: 'Both', value: 'both' as const }
                ]).map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setOneOffReminderTarget(opt.value)}
                    style={{
                      flex: 1, padding: '10px 8px', fontSize: '13px', fontWeight: '600',
                      color: oneOffReminderTarget === opt.value ? 'white' : '#4a5568',
                      background: oneOffReminderTarget === opt.value ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'white',
                      border: oneOffReminderTarget === opt.value ? '2px solid #667eea' : '2px solid #e2e8f0',
                      borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s ease'
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <select
                value={oneOffReminderMinutes ?? ''}
                onChange={(e) => setOneOffReminderMinutes(e.target.value ? parseInt(e.target.value) : null)}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  fontSize: '14px'
                }}
              >
                <option value="">No reminder</option>
                {REMINDER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {oneOffReminderMinutes !== null && oneOffReminderTarget === null && (
                <p style={{ fontSize: '12px', color: '#e53e3e', marginTop: '8px' }}>
                  Please select whether this reminder is for the start time, end time, or both.
                </p>
              )}
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => {
                  setShowOneOffTimeModal(false);
                  setOneOffSelectedClub(null);
                  setOneOffDate(null);
                  setOneOffDateStr('');
                  setOneOffKitItems([]);
                  setOneOffDesignatedParent('');
                  setOneOffReminderMinutes(null);
                  setOneOffReminderTarget(null);
                  setOneOffUniform([]);
                }}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  backgroundColor: '#f8f9fa',
                  color: '#333',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!oneOffDate || !oneOffSelectedClub || !oneOffTime) {
                    alert('Please select a time');
                    return;
                  }

                  // Create the manual one-off entry
                  const manualOneOff: ManualOneOff = {
                    dateStr: oneOffDateStr,
                    clubName: oneOffSelectedClub.name,
                    clubUuid: oneOffSelectedClub.uuid,
                    time: oneOffTime,
                    location: oneOffSelectedClub.location || '',
                    venue: oneOffSelectedClub.venue || '',
                    kit_items: oneOffKitItems.length > 0 ? oneOffKitItems : undefined,
                    designated_parent_uuid: oneOffDesignatedParent || undefined,
                    reminder_minutes: oneOffReminderMinutes
                  };

                  // Add to the appropriate day's manualOneOffs array
                  const targetField = oneOffType === 'drop_off' ? 'drop_off' : 'pick_up';
                  setDailySchedules((prev: any) => {
                    const dayData = prev[currentDayTab] || {};
                    const fieldData = dayData[targetField] || {};
                    const existingOneOffs = fieldData.manualOneOffs || [];

                    return {
                      ...prev,
                      [currentDayTab]: {
                        ...dayData,
                        [targetField]: {
                          ...fieldData,
                          manualOneOffs: [...existingOneOffs, manualOneOff]
                        }
                      }
                    };
                  });

                  // Close the modal and reset state
                  setShowOneOffTimeModal(false);
                  setOneOffSelectedClub(null);
                  setOneOffDate(null);
                  setOneOffDateStr('');
                  setOneOffTime('');
                  setOneOffKitItems([]);
                  setOneOffDesignatedParent('');
                  setOneOffReminderMinutes(null);
                  setOneOffUniform([]);
                }}
                disabled={!oneOffTime}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  backgroundColor: oneOffTime ? '#28a745' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: oneOffTime ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Add to Term Plan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Schedule Confirmation Modal */}
      {showDeleteScheduleModal && deleteScheduleCategory && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10001
          }}
          onMouseDown={() => {
            setShowDeleteScheduleModal(false);
            setDeleteScheduleCategory(null);
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#dc3545' }}>
              Clear {deleteScheduleCategory === 'drop_off' ? 'Drop-off' : deleteScheduleCategory === 'special_lessons' ? 'Special Lessons' : 'Pick-up'} Schedule?
            </h3>

            <div style={{
              padding: '12px',
              backgroundColor: '#fff3cd',
              borderRadius: '6px',
              marginBottom: '16px',
              border: '1px solid #ffc107'
            }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#856404' }}>
                <strong>Warning:</strong> This will remove all {deleteScheduleCategory === 'drop_off' ? 'drop-off' : deleteScheduleCategory === 'special_lessons' ? 'special lesson' : 'pick-up'} entries for {dayNames[currentDayTab]}.
              </p>
              <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#856404' }}>
                This action cannot be undone. You will need to save the term planner to make the changes permanent.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowDeleteScheduleModal(false);
                  setDeleteScheduleCategory(null);
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#e9ecef',
                  color: '#333',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => clearScheduleForCategory(deleteScheduleCategory)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Clear Schedule
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Next child intro slide (onboarding flow) */}
      {nextChildIntroSlide && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'white',
          zIndex: 9998,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          textAlign: 'center'
        }}>
          {nextChildIntroSlide.currentChildName && (
            <>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>✅</div>
              <div style={{ fontSize: '0.95rem', color: '#4CAF50', fontWeight: 600, marginBottom: '24px' }}>
                Term planner saved for {nextChildIntroSlide.currentChildName}!
                {' '}{nextChildIntroSlide.activitiesCreated} activities created
                {nextChildIntroSlide.alarmsScheduled > 0 ? `, ${nextChildIntroSlide.alarmsScheduled} reminders scheduled` : ''}.
              </div>
            </>
          )}
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>🎯</div>
          <h2 style={{ fontSize: '1.4rem', marginBottom: '12px', color: '#333', maxWidth: '340px' }}>
            {nextChildIntroSlide.hasIntegrations
              ? `Let's set up ${nextChildIntroSlide.nextChildName}'s activities for the year`
              : `Let's set up ${nextChildIntroSlide.nextChildName}'s term planner`}
          </h2>
          <p style={{ fontSize: '1rem', color: '#666', maxWidth: '340px', lineHeight: 1.5, marginBottom: '16px' }}>
            {nextChildIntroSlide.hasIntegrations
              ? `We'll import any activities from your connected apps and set up ${nextChildIntroSlide.nextChildName}'s term planner. You can add anything else manually.`
              : `You can add activities manually.`}
          </p>
          {nextChildIntroSlide.remainingCount > 1 && (
            <p style={{ fontSize: '0.9rem', color: '#999', maxWidth: '340px', lineHeight: 1.5, marginBottom: '16px' }}>
              {nextChildIntroSlide.remainingCount} {nextChildIntroSlide.remainingCount === 1 ? 'child' : 'children'} left to set up.
            </p>
          )}
          <button
            onClick={() => {
              const action = pendingNextChildActionRef.current;
              pendingNextChildActionRef.current = null;
              setNextChildIntroSlide(null);
              if (action) action();
            }}
            style={{
              padding: '14px 40px',
              fontSize: '16px',
              fontWeight: 600,
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              marginTop: '16px'
            }}
          >
            Let's go
          </button>
        </div>
      )}

      {/* Loading overlay for term planner transitions */}
      {loadingTermPlannerTransition && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '20px'
        }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid #e0e0e0',
            borderTop: '4px solid #28a745',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <div style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#333'
          }}>
            {loadingTermPlannerMessage?.title || 'Generating Term Planner...'}
          </div>
          <div style={{
            fontSize: '14px',
            color: '#666'
          }}>
            {loadingTermPlannerMessage?.subtitle || 'Setting up your weekly schedule'}
          </div>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}

    </div>
  );
};

export default ProfileScreen;